import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  KeyboardEvent,
  useLayoutEffect,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import { AppDispatch, RootState } from "../../../redux/store";
import { clearTraditionalQuoteData } from "../../../redux/reducer/locationSlice/locationSlice";
import GetQuote from "../../../components/getQuote/GetQuote";
import { MutliDropdown, MultiSelectChips, SingleDropdown } from "../../../components/shared";
import { SingleDropdownItemType } from "../../../components/shared/SingleDropdown";
import AddDocumentURLModal from "../../../components/modals/AddDocumentURLModal";
import {
  cmToInch,
  kgToLbs,
  onInputPhNumFormat,
  expiredData,
  getISODate,
  extractTime,
  pickerTimeToStored,
  formatTimeForDisplay,
} from "../../../utils/utils";

import {
  validateZipCode,
  validateMaskedPhoneNumber,
  validateContactEmail,
  ValidateDate,
  ValidateExpedietedDate,
  ValidateExpedietedCrrentTime,
} from "../../../utils/validation";
import {
  GET_DATA_FROM_ZIPCODE,
  SAVE_QUOTATION,
  SAVE_LTL_QUOTATION,
} from "../../../apis/end-points";
import {
  customerOrderColumns,
  agentOrderColumns,
  CustomerRowDataType,
  AgentRowDataType,
  DataTypes,
  DocumentInfo,
  Fields,
  FormErrorsType,
  GetQuoteApiResponse,
  MappedService,
  orderMandatoryFields,
  orderTypeColumns,
  OrderTypeRowDataType,
  QuoteInfo,
  ServiceLevelType,
  tableConfig,
  MoreReferencesType,
  GetLTLQuoteApiResponse,
  orderLTLMandatoryFields,
  orderExpeditedMandatoryFields,
  GetLTLFinalMileQuoteApiResponse,
  accessorialsColumn,
  hubData,
  orderLTLFinalMileMandatoryFields,
} from "./CustomerOrder-types";

import { getApi, postApi } from "../../../apis/api";
import { Loader } from "../../../components/loaders/loaders";
import { useMessage } from "../../../context/MessageContext";
import EmailChips from "../../../components/shared/EmailChips";
import useFormChangeHandler from "../../../hooks/useFormChangeHandler";
import useMandatoryFieldFocus from "../../../hooks/useMandatoryFieldFocus";
import {
  DROPDOWN_FIELD_KEYS,
  FINAL_MILE_SERVICE_TYPE_OPTIONS,
  HANDLING_UNITS_FIELD_KEY,
  LOGISTICS_TYPE,
} from "../../../utils/constants";
import {
  isMandatoryMessage,
  pruneItemIssues,
  prunePackageIssues,
  validateOrderForm,
} from "../../../utils/orderValidation";
import ConfirmModal from "../../../components/modals/ConfirmModal";
import { CUSTOMER_ID } from "../../../utils/authUtils";

import { AddIcon, DeleteIcon } from "../../../assets";
import {
  GENERAL_ERROR_MESSAGE,
  HANDLING_UNITS_REQUIRED_MESSAGE,
  ORDER_MANAGEMENT_MESSAGES,
} from "../../../utils/message";
import GetLTLQuote from "../../../components/getQuote/GetLTLQuote";
import OrderItemDetailTable from "../../../components/shared/table/OrderItemDetailTable";
import { EXPEDIET_PACKAGE_TYPE, PACKAGE_TYPE } from "../../../utils/constants";
import moment from "moment";
import CustomTimePicker from "../../../components/CustomTimePicker";
import GetTraditionalQuote, {
  filterLtlOnlyQuotes,
} from "../../../components/getQuote/GetTraditionalQuote";

interface ColumnTypes {
  key: string;
  title: string;
}

const agentHubColumns: ColumnTypes[] = [{ key: "name", title: "Name" }];
const serviceColumns: ColumnTypes[] = [{ key: "name", title: "Service Level" }];

interface AgentHubDropdown {
  id?: string;
  name: string | null;
  agentHubNo: string;
}
interface DropwdownValueTypes {
  id: string;
  name: string;
}
const AdditionalServices = [
  "Additional Services",
  "Remove & Disposal",
  "Order Swap & Exchange",
];

/**
 * CustomerOrder Component
 *
 * This component allows users to create a new customer order by filling out a form with various fields,
 * including destination address, service level, item details, and more. The form supports validation,
 * fetching data from APIs, and submitting the order data to the backend.
 */
const QuickQuote: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { state } = location;
  const { setMessage } = useMessage();
  const referenceLimit = 5;
  const { countryCode } = useSelector((state: RootState) => state.staticData);
  const { isUserOfOmniMove, userRole, isCustomer, customerLevel } = useSelector(
    (state: RootState) => state.auth
  );
  const [originCities, setOriginCities] = useState<string[]>([]);
  const [destinationCities, setDestinationCities] = useState<string[]>([]);
  // boolean states
  const [loading, setLoading] = useState<boolean>(false);
  const [groupQuoteId, setGroupQuoteId] = useState<string>(state?.id ?? "");
  const [stateQuoteId, setStateQuoteId] = useState<string>(
    state?.quoteId ?? ""
  );
  const [quoteType, setQuoteType] = useState<"traditional" | "nonTraditional">(
    "nonTraditional"
  );
  const [getQuote, setGetQuote] = useState<boolean>(false);
  const [selectedHubData, setSelectedHubData] = useState<hubData>({
    operationFrom: "",
    operationTo: "",
    hubName: "",
  });
  const [hasMasterHub, setHasMasterHub] = useState(false);
  const [masterHubName, setMasterHubName] = useState("");
  const [masterHubAddress, setMasterHubAddress] = useState<any>({});
  const [masterHubNameToTransfer, setMasterHubNameToTransfer] = useState("");
  const [masterHubAddressToTransfer, setMasterHubAddressToTransfer] =
    useState<any>({});
  const [ltlFMData, setLtlFMData] = useState<any>(null);
  const [ltlAccessorialData, setLtlAccessorialData] = useState<any>(null);

  // modal States
  const [viewAddDocumentModal, setViewAddDocumentModal] =
    useState<boolean>(false);
  // Add item details table data states
  const [data, setData] = useState<DataTypes[]>([
    {
      id: "",
      serial: 1,
      description: "",
      handlingUnit: "",
      quantity: "",
      weight: "",
      itemDimension: {
        length: "",
        width: "",
        height: "",
      },
      unit: "IN",
      itemClass: "",
      comments: "",
      nmfc: "",
      skuNumber: "",
      packageType: "",
      package: [],
      stackable: "No",
      hazmat: false,
      weightUnit: { id: "lbs", name: "LBS" },
    },
  ]);
  // get quotes table data states
  const [quoteData, SetQuoteData] = useState<
    { info: QuoteInfo; services: MappedService[] }[]
  >([]);
  const [serviceSelected, setServiceSelected] = useState<any>({});
  // Final Mile only: the full set of services to quote. serviceSelected is kept in sync
  // with the first entry so every existing single-service consumer keeps working.
  const [serviceSelectedList, setServiceSelectedList] = useState<any[]>([]);
  const [serviceList, setServiceList] = useState<DropwdownValueTypes[]>([]);
  const [showLTLList, setShowLTLList] = useState<boolean>(false);
  // "LTL Only" flag chosen on the quote panel; sent with LTL saved quotes.
  const [isLtlOnly, setIsLtlOnly] = useState<boolean>(false);
  const [TLList, setLTLList] = useState<any[]>([]);
  const [isLtlFinalmile, setIsLTLFinalMile] = useState(false);
  const [saveQuotationPage, setSaveQuotationPage] = useState(false);
  // fields states, errors states
  const [fields, setFields] = useState<Fields>({
    orderReference: "",
    orderReference2: "",
    destinationAddress: "",
    destinationAPTSuite: "",
    destinationZipcode: "",
    destinationCity: "",
    destinationState: "",
    destinationCountry: "",
    destinationBusinessLocation: "",
    destinationConsigneeName: "",
    destinationConsigneePhoneNumber: "",
    destinationConsigneePhoneExt: "+1",
    destinationConsigneeContactEmail: "",
    OriginAddress: "",
    OriginAPTSuite: "",
    OriginZipcode: "",
    OriginCity: "",
    OriginState: "",
    OriginCountry: "",
    OriginBusinessLocation: "",
    OriginConsigneeName: "",
    OriginConsigneePhoneNumber: "",
    OriginConsigneePhoneExt: "+1",
    OriginConsigneeContactEmail: "",
    LTLPickupAccessorials: [],
    PickUpDockName: "",
    PickUpDOckNumber: "",
    PickUpDockDate: moment().format("YYYY-MM-DD"),
    PickUpDockTime: "08:00 AM",
    PickUpDockOpenTime: "",
    PickUpDOckCloseTime: "",
    PickUpDOckNote: "",
    PickUpFCFS: false,
    LTLDropAccessorials: [],
    DropDockName: "",
    DropDOckNumber: "",
    DropDockDate: "",
    DropDockTime: "",
    DropDockOpenTime: "",
    DropDOckCloseTime: "",
    DropDOckNote: "",
    DropFCFS: false,
    markup: 0,
    totalLinearFeet: "0.00",
    totalDensity: "0.00",
    totalCBM: "0.00",
    destinationExtramen: "",
    destinationNoOfFlights: "",
    destinationWaitTimeMinutes: "",
    destinationStorage: "",
    destinationHandingUnits: "",
    destinationWeight: "",
    destinationPieces: "",
    destinationSpecialInstructions: "",
    MidStopAddress: "",
    MidStopAPTSuit: "",
    MidStopZipcode: "",
    MidStopCity: "",
    MidStopState: "",
    MidStopCountry: "",
    MidStopContactName: "",
    MidStopPhoneNumber: "",
    MidStopPhoneNumberExt: "+1",
    courierPro1: "",
    courierPro2: "",
    plProvider: "",
    MidStopContactEmail: [],
    CarrierName: "",
    serviceCost: "",
    costDetails: [],
    scac: "",
    distance: "",
    offerId: "",
    productTransactionId: "",
    agentHubId: "",
    selectedServiceId: "",
    selectedQuoteId: "",
    customerMarkupType: "",
    allQuoteId: [],
    orderSwapAndExchange: false,
    additionalServices: [],
    removeAndDisposal: false,
    PickupTargetDateFrom: "",
    PickupTargetDateTo: "",
    PickupTargetTimeFrom: "",
    PickupTargetTimeTo: "",
    PickupDeliveryDateFrom: "",
    PickupDeliveryDateTo: "",
    PickupDeliveryTimeFrom: "",
    PickupDeliveryTimeTo: "",
    AgentHub: {
      id: "",
      name: "",
    },
    originCity: "",
    finalMileDistance: "",
    finalMileCost: "",
    totalDensityView: "",
    totalLinearFeetView: "",
    totalLinearCBMView: "",
    orderFor: '',
    source: '',
    finalMileSource: '',
  });
  const [errors, setErrors] = useState<FormErrorsType>({});
  const [documentInfo, setDocumentInfo] = useState<DocumentInfo>({
    files: [],
    urls: [],
  });

  // Mandatory-field validation: highlights everything missing when the user asks for a
  // quote, then scrolls to and focuses whichever control sits highest on the page.
  const containerRef = useRef<HTMLDivElement>(null);
  const [invalidCells, setInvalidCells] = useState<Record<number, string[]>>({});
  const [invalidPackageCells, setInvalidPackageCells] = useState<
    Record<number, Record<number, string[]>>
  >({});
  const [itemsEmpty, setItemsEmpty] = useState(false);
  const [focusCell, setFocusCell] = useState<{
    rowIndex: number;
    columnKey: string;
  } | null>(null);
  const { applyValidation, clearMandatory } = useMandatoryFieldFocus({
    containerRef,
    setErrors,
    values: fields,
  });

  // dropdown data and selected row states
  const [parentCustomerId, setParentCustomerId] = useState<string>();
  const { orderTypes } = useSelector((state: RootState) => state.staticData);
  const [customerRowData, setCustomerRowData] = useState<CustomerRowDataType>({
    id: "",
    name: "",
    customerNumber: "",
    customerLevel: "",
  });
  const [apiPackageType, setApiPackageType] = useState<any>([]);
  const handleTimeChange = (time: string, property: string) => {
    setFields((prev) => ({
      ...prev,
      [property]: time,
    }));
    compareExpediteDates({ ...fields, [property]: time });
    if (property === "PickUpDockTime") {
      setShowLTLList(false);
      setFields((prevData) => ({
        ...prevData,
        CarrierName: "",
        serviceCost: "",
        scac: "",
        distance: "",
        offerId: "",
        productTransactionId: "",
      }));
      setGetQuote(false);
    }
    // check error
    let error = "";
    switch (property) {
      case "PickUpDockTime":
        setErrors((prevErrors) => ({
          ...prevErrors,
          PickUpDockDate: "",
          DropDockDate: "",
          DropDockTime: "",
        }));
        if (
          fields.DropDockDate != "" &&
          fields.DropDockTime != ""
        ) {
          error = ValidateDate(
            `${fields.DropDockDate} ${convertTo24HourFormat(
              fields.DropDockTime
            )}:00`,

            "Pickup Date & Time should be earlier than Drop date & Time",
            `${fields.PickUpDockDate} ${convertTo24HourFormat(time)}:00`
          );
        }
        break;
      case "DropDockTime":
        if (fields.DropDockDate != "") {
          setErrors((prevErrors) => ({
            ...prevErrors,
            DropDockDate: "",
            PickUpDockTime: "",
            PickUpDockDate: "",
          }));
          if (fields.PickUpDockDate === "") {
            error = ValidateDate(
              `${fields.DropDockDate} ${convertTo24HourFormat(time)}:00`,
              "Drop Date & Time cannnot be earlier than Current Date &Time"
            );
          } else if (
            fields.PickUpDockDate != "" &&
            fields.PickUpDockTime != ""
          ) {
            error = ValidateDate(
              `${fields.DropDockDate} ${convertTo24HourFormat(time)}:00`,
              "Drop Date & Time should be greater than or equal to the pickup Date & Time",
              `${fields.PickUpDockDate} ${convertTo24HourFormat(
                fields.PickUpDockTime
              )}:00`
            );
          }
        }
        break;
      default:
        break;
    }
    if (
      !(
        property === "PickupTargetTimeFrom" ||
        property === "PickupTargetTimeTo" ||
        property === "PickupDeliveryTimeFrom" ||
        property === "PickupDeliveryTimeTo"
      )
    ) {
      setErrors((prevErrors) => {
        return {
          ...prevErrors,
          [property]: error,
        };
      });
    }
  };
  const [assCustomerRowData, setAssCustomerRowData] =
    useState<CustomerRowDataType>({
      id: "",
      name: "",
      customerNumber: "",
      customerLevel: "",
    });
  const [orderTypeRowData, setOrderTypeRowData] =
    useState<OrderTypeRowDataType>({
      id: "",
      name: "",
      description: "",
    });
  // LTL + Final Mile drop dock hours are sent exactly as entered (unchanged behaviour);
  // every other order type sends them as 24-hour HH:mm like the pickup hours.
  const dropTimeForPayload = (time?: string) =>
    orderTypeRowData?.name === "LTL + Final Mile" ? time || "" : pickerTimeToStored(time);
  // Final Mile is the only order type that quotes multiple services at once.
  const isFinalMileOrderType = orderTypeRowData?.name === "Final Mile";
  // Multi-service quoting is opt-in per request. A single Final Mile selection still
  // uses it, because it also returns every serving agent ordered by distance.
  const isMultiServiceQuote =
    isFinalMileOrderType && serviceSelectedList.length > 0;
  // Final Mile Service Type (Delivery / Pickup). Delivery by default; ignored for other order types.
  const [serviceType, setServiceType] = useState<string>(LOGISTICS_TYPE.DELIVERY);
  // Latest Service Type, so a slow service-list response for a previous type is ignored.
  const serviceTypeRef = useRef<string>(LOGISTICS_TYPE.DELIVERY);
  serviceTypeRef.current = serviceType;
  const isFinalMilePickup =
    isFinalMileOrderType && serviceType === LOGISTICS_TYPE.PICKUP;
  const [customerNameDropdown, setCustomerNameDropdown] = useState<
    CustomerRowDataType[]
  >([]);
  const [assCustomerNameDropdown, setAssCustomerNameDropdown] = useState<
    CustomerRowDataType[]
  >([]);
  const [orderTypeDropdown, setOrderTypeDropdown] = useState<
    OrderTypeRowDataType[]
  >([]);
  const [dropAccessorials, setDropAccessorialDropdownData] = useState<
    OrderTypeRowDataType[]
  >([]);
  const [dropAccessorialsSelected, setDropAccessorialsSelected] = useState<
    { id: string; name: string }[]
  >([]);
  const [pickupAccessorials, setPickupAccessorialDropdownData] = useState<
    OrderTypeRowDataType[]
  >([]);
  const [pickupAccessorialsSelected, setPickupAccessorialsSelected] = useState<
    { id: string; name: string }[]
  >([]);
  const [dropLimitedAccess, setDropLimitedAccessDropdownData] = useState<
    OrderTypeRowDataType[]
  >([]);
  const [dropLimitedAccessSelected, setDropLimitedAccesssSelected] =
    useState<DropwdownValueTypes>();
  const [pickupLimitedAccess, setPickupLimitedAccessDropdownData] = useState<
    OrderTypeRowDataType[]
  >([]);
  const [pickupLimitedAccessSelected, setPickupLimitedAccesssSelected] =
    useState<DropwdownValueTypes>();

  const [notLTL, setNotLTL] = useState<boolean>(true);
  const [lfmQuoteId, setLfmQuoteId] = useState("");
  const [agentNameDropdown, setAgentNameDropdown] = useState<
    AgentRowDataType[]
  >([]);
  const [agentRowData, setAgentRowData] = useState<AgentRowDataType>({
    id: "",
    name: "",
    agentType: "",
  });
  const [dropdownValues, setDropdownValues] = useState<{
    [key: string]: SingleDropdownItemType | null;
  }>({
    hubName: null,
  });
  const [agentHubDropdownData, setAgentHubDropdownData] = useState<
    AgentHubDropdown[]
  >([]);
  const [agentHubRowData, setAgentHubRowData] = useState<AgentHubDropdown>();
  const ltlOrderType: any = orderTypes.find(
    (order: any) => order?.name === "LTL"
  );
  // unsave model
  const {
    checkInputChange,
    handleNavigation,
    handleConfirmNavigation,
    handleCancelNavigation,
    handleCustomerOrderExit,
    handleUpdateNavigation,
    showModal,
    isFormChanged,
  } = useFormChangeHandler();
  const [isQuote, setIsQuote] = useState<boolean>(false);
  const [associatedCusReqired, setAssociatedCusReqired] = useState<boolean>(
    !isUserOfOmniMove &&
      (isCustomer || userRole === "manager" || userRole === "employee" || userRole === "admin")
      ? false
      : true
  );
  const [moreReferences, setMoreReferences] = useState<MoreReferencesType[]>([
    {
      referenceCount: 2,
      referenceValue: "",
    },
  ]);

  //Normalize date by converting to utc
  const normalizeDate = (date: any) => {
    return moment(date).format("YYYY-MM-DD");
  };

  const getUTCDate = (dateStr: string, timeStr: string): string => {
    const combinedDateTime = moment(
      `${dateStr} ${timeStr}`,
      "YYYY-MM-DD hh:mm:ss A"
    );
    return combinedDateTime.isValid()
      ? combinedDateTime.toISOString()
      : "Invalid date or time";
    // const newDate = moment.utc(`${dateStr.split("T")[0]} ${timeStr}`).format('YYYY-MM-DDTHH:mm:ss')+"Z";
    // return newDate;
  };

  const getUTCTime = (dateStr: string, timeStr: string): string => {
    let newDate = dateStr.includes("T") ? dateStr.split("T")[0] : dateStr;
    const newTime = moment.utc(`${newDate} ${timeStr}`).format("hh:mm A");
    return newTime;
  };

  const mapLTLItems = (data: any[]) => {
    return data.map((item) => ({
      billedDimension: {
        length: {
          value:
            item.unit === "in" || item.unit.name === "in"
              ? item.itemDimension?.length || ""
              : cmToInch(item.itemDimension?.length),
          unit: "IN",
        },
        width: {
          value:
            item.unit === "in" || item.unit.name === "in"
              ? item.itemDimension?.width || ""
              : cmToInch(item.itemDimension?.width),
          unit: "IN",
        },
        height: {
          value:
            item?.unit === "in" || item?.unit?.name === "in"
              ? item.itemDimension?.height || ""
              : cmToInch(item.itemDimension?.height),
          unit: "IN",
        },
      },
      isMixedClass: false,
      isStackable:
        item?.stackable == "Yes" ||
          item?.stackable?.name == "Yes" ||
          item?.stackable?.name == "yes"
          ? true
          : false || false,
      marksAndNumbers: null,
      linearFeet: item?.linearFeet?.toString(),

      packagingType:
        typeof item.packageType === "object"
          ? item.packageType?.name
          : item.packageType || "",
      quantity: parseInt(item.quantity) || 0,
      referenceList: mapReferences(false),
      shippedItemList: [
        {
          commodityClass: item.itemClass.toString() || "5",
          commodityDescription: item.description || "",
          commodityType: null,
          dimensions: {
            length: {
              value:
                item?.unit === "in" || item?.unit?.name === "in"
                  ? item.itemDimension?.length || ""
                  : cmToInch(item.itemDimension?.length),
              unit: "IN",
            },
            width: {
              value:
                item?.unit === "in" || item?.unit?.name === "in"
                  ? item.itemDimension?.width || ""
                  : cmToInch(item.itemDimension?.width),
              unit: "IN",
            },
            height: {
              value:
                item?.unit === "in" || item?.unit?.name === "in"
                  ? item.itemDimension?.height || ""
                  : cmToInch(item.itemDimension?.height),
              unit: "IN",
            },
          },
          isHazMat: item.hazmat,
          hazMatItemInfo: item.hazmat
            ? {
              hazIdentificationNbr: item.hazardMaterial?.unno || "",
              hazProperShippingName: item.hazardMaterial?.itemName || "",
              hazClassType: item.hazardMaterial?.hazmatClass || "",
              hazPackingGroupType: item.hazardMaterial?.packagingGroup || "",
              hazEmergencyPhoneNbr:
                item.hazardMaterial?.phoneNumber.replace(/\D/g, "") || "",
              hazEmergencyName: item.hazardMaterial?.contact || "",
              hazPlacardRequiredFlag: false,
              hazPlacardDetails: "Placard Details",
              hazERR: "ERR Number",
              hazFlashpointTemperature: {
                value: 1,
                unit: "F",
              },
              hazAdditionalDetails: "Additional Haz Details",
            }
            : {},
          name: "",
          NMFCDescription: null,
          NMFCNbr: item.nmfc || "",
          packagingType: item.packageType || "",
          quantity: parseInt(item.quantity) || 0,
          weight: {
            value:
              item?.weightUnit === "lbs" || item?.weightUnit?.name === "lbs"
                ? item.weight || ""
                : kgToLbs(item.weight),
            unit: "LB",
          },
        },
      ],
      sortAndSegregateFlag: true,
      weight: {
        value:
          item?.weightUnit === "lbs" || item?.weightUnit?.name === "lbs"
            ? item.weight || ""
            : kgToLbs(item.weight),
        unit: "LB",
      },
    }));
  };

  const mapReferences = (shipment: boolean) => {
    let references = [
      {
        type: `${shipment ? "Shipment " : ""}Reference 1`,
        value: fields.orderReference,
        isPrintAsBarCode: true,
      },
    ];

    if (moreReferences.length > 0) {
      moreReferences.map((ref, index) => {
        if (ref["referenceValue"] != "") {
          references.push({
            type: `${shipment ? "Shipment " : ""}Reference ${index + 2}`,
            value: ref["referenceValue"],
            isPrintAsBarCode: false,
          });
        }
      });
    }
    return references;
  };

  const getLTLFinalMileQuote = async (body: any) => {
    const apiPayload = {
      quoteDetail: {
        customerId: assCustomerRowData?.id || customerRowData?.id,
        serviceIds: [serviceSelected?.id],
        items: mapItemsDataToPayload(data),
        destinationAddress: {
          address: fields.destinationAddress,
          aptSuit: fields.destinationAPTSuite,
          zipCode: fields.destinationZipcode,
          city: fields.destinationCity,
          state: fields.destinationState,
          country: fields.destinationCountry,
          businessLocation: fields.destinationBusinessLocation,
          contactName: fields.destinationConsigneeName,
          phoneNumber: fields.destinationConsigneePhoneNumber.replace(
            /\D/g,
            ""
          ),
          countryCallingCode: fields.destinationConsigneePhoneExt,
          contactEmail: fields.destinationConsigneeContactEmail,
          extraMen: fields.destinationExtramen,
          waitTime: fields.destinationWaitTimeMinutes,
          stairsNumber: fields.destinationNoOfFlights,
          storage: fields.destinationStorage,
          handlingUnit: fields.destinationHandingUnits,
          weight: fields.destinationWeight,
          pieces: fields.destinationPieces,
          specialInstructions: fields.destinationSpecialInstructions,
        },
        additionalServices: fields.additionalServices,
      },
      ltlQuoteDetail: body,
    };
    try {
      setLoading(true);
      const apiResponse = await postApi<GetLTLFinalMileQuoteApiResponse>(
        `/order/order-quote/generate-ltlplusfinalmile-quote`,
        apiPayload
      );
      if (apiResponse.success) {

        const data = apiResponse?.data || {};
        const finalMileData = apiResponse?.data?.selectedFinalMileQuote;
        setShowLTLList(true);
        setLfmQuoteId(finalMileData?.service?.quoteId);
        const ltlDetails = mapLtlFinalMileList(data);
        setIsLTLFinalMile(true);
        setLTLList(ltlDetails);
      } else {
        SetQuoteData([]);
        setMessage(
          apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
      console.error(GENERAL_ERROR_MESSAGE, error);
    } finally {
      setLoading(false);
    }
  };
  const getLTLQuotesData = async () => {
    if (!runQuoteValidation("getLtlQuote")) return;
    setStateQuoteId("");
    setFields((prevData) => ({
      ...prevData,
      CarrierName: "",
      serviceCost: "",
      scac: "",
      distance: "",
      offerId: "",
      productTransactionId: "",
    }));
    const body = {
      request: {
        productType: "LTL",
        quoteDetail: {
          customerId: assCustomerRowData?.id
            ? assCustomerRowData?.id
            : customerRowData?.id,
        },
        shipment: {
          shipmentDate:
            fields.PickUpDockDate !== "" && fields.PickUpDockTime !== ""
              ? getISODate(fields.PickUpDockDate, fields.PickUpDockTime)
              : fields.PickUpDockDate !== ""
                ? getISODate(fields.PickUpDockDate, moment().format("hh:mm A"))
                : "",
          originAddress: {
            address: {
              addressLineList:
                fields.OriginAddress && fields.OriginAPTSuite
                  ? [fields.OriginAddress, fields.OriginAPTSuite]
                  : fields.OriginAddress
                    ? [fields.OriginAddress]
                    : [],
              locality: fields.OriginCity,
              region: fields.OriginState,
              postalCode: fields.OriginZipcode,
              countryCode: fields.OriginCountry,
              companyName: fields.OriginBusinessLocation,
              phone: fields.OriginConsigneePhoneNumber
                ? `${fields.OriginConsigneePhoneNumber.replace(/\D/g, "")}`
                : "",
              contactList: [
                {
                  phone: fields.OriginConsigneePhoneNumber
                    ? `${fields.OriginConsigneePhoneNumber.replace(/\D/g, "")}`
                    : "",
                  firstName: fields.OriginConsigneeName || "",
                },
              ],
              locationType:
                (pickupLimitedAccessSelected &&
                  pickupLimitedAccessSelected?.name) ||
                "",
            },
          },
          destinationAddress: {
            // frontend
            address: {
              addressLineList:
                fields.destinationAddress != "" &&
                  fields.destinationAPTSuite != ""
                  ? [fields.destinationAddress, fields.destinationAPTSuite]
                  : fields.destinationAddress != ""
                    ? [fields.destinationAddress]
                    : [],
              locality: fields.destinationCity,
              region: fields.destinationState,
              postalCode: fields.destinationZipcode,
              countryCode: fields.destinationCountry,
              companyName: fields.destinationBusinessLocation,
              phone: fields.destinationConsigneePhoneNumber
                ? fields.destinationConsigneePhoneNumber.replace(/\D/g, "")
                : "",
              contactList: [
                {
                  phone: fields.destinationConsigneePhoneNumber
                    ? fields.destinationConsigneePhoneNumber.replace(/\D/g, "")
                    : "",
                  firstName: fields.destinationConsigneeName || "",
                },
              ],
              locationType:
                (dropLimitedAccessSelected &&
                  dropLimitedAccessSelected?.name) ||
                "",
            },
          },
          handlingUnitList: mapLTLItems(data),
          totalWeight: {
            value:
              data[0]?.unit === "in"
                ? fields.destinationWeight || ""
                : kgToLbs(fields.destinationWeight),
            unit: "LB",
          },
          insuranceRequestFlag: false,
          totalHandlingUnitCount: fields.destinationHandingUnits,
          shipmentReferenceList: fields.orderReference
            ? mapReferences(true)
            : [],
          appointmentDeliveryFlag: dropAccessorialsSelected.some(
            (x) => x.name === "Appointment"
          ),
          directDeliveryOnlyFlag: null,
          holdAtTerminalFlag: dropAccessorialsSelected.some(
            (x) => x.name === "Hold"
          ),
          insideDeliveryFlag: dropAccessorialsSelected.some(
            (x) => x.name === "Inside Delivery"
          ),
          insidePickupFlag: pickupAccessorialsSelected.some(
            (x) => x.name === "Inside Pickup"
          ),
          carrierTerminalPickupFlag: pickupAccessorialsSelected.some(
            (x) => x.name === "Airport Pickup"
          ),
          liftgateDeliveryFlag: dropAccessorialsSelected.some(
            (x) => x.name === "LiftgateDelivery"
          ),
          liftgatePickupFlag: pickupAccessorialsSelected.some(
            (x) => x.name === "Liftgate"
          ),
          notifyBeforeDeliveryFlag: dropAccessorialsSelected.some(
            (x) => x.name === "Delivery Notification"
          ),
          protectionFromColdFlag: false,
          residentialDeliveryFlag: dropAccessorialsSelected.some(
            (x) => x.name === "Residential"
          ),
          residentialPickupFlag: pickupAccessorialsSelected.some(
            (x) => x.name === "Residential"
          ),
          sortAndSegregateFlag: pickupAccessorialsSelected.some(
            (x) => x.name === "Sort and Segregate"
          ),
          pickupSpecialInstructions: fields.PickUpDOckNote || "",
          deliverySpecialInstructions: fields.DropDOckNote || "",
          tradeshowDeliveryFlag: dropAccessorialsSelected.some(
            (x) => x.name === "Tradeshow Delivery"
          ),
          tradeshowDeliveryName: "",
          tradeshowPickupFlag: pickupAccessorialsSelected.some(
            (x) => x.name === "Tradeshow Pickup"
          ),
          tradeshowPickupName: "",
        },
      },
      correlationId: "WWEX-M2M-shopFlow-ltl",
    };

    if (orderTypeRowData && orderTypeRowData.name != "LTL") {
      getLTLFinalMileQuote(body);
      return;
    }
    try {
      setLoading(true);
      const apiResponse = await postApi<GetLTLQuoteApiResponse>(
        `/order/order-quote/generateltlquote`,
        body
      );
      if (apiResponse.success) {
        const data = apiResponse?.data || { worldWideQuote: [] };
        setShowLTLList(true);
        const ltlDetails = mapLtlList(data.worldWideQuote);
        setIsLTLFinalMile(false);
        setLTLList(ltlDetails);
      } else {
        SetQuoteData([]);
        setMessage(
          apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
      console.error(GENERAL_ERROR_MESSAGE, error);
    } finally {
      setLoading(false);
    }
  };
  const mapLtlFinalMileList = (LTLdata: any): any[] => {
    if (
      LTLdata?.selectedFinalMileQuote &&
      LTLdata?.ltlPlusFinalMileQuote.length
    ) {

      const hubName = LTLdata?.selectedFinalMileQuote?.hub?.agent?.name || "";
      const selectedQuoteData = LTLdata?.selectedFinalMileQuote;
      const quotePrice =
        LTLdata?.selectedFinalMileQuote?.service?.quotePrice || "0";
      const basePrice =
        LTLdata?.selectedFinalMileQuote?.service?.rateDetails?.basePrice || "0";
      const quoteId = LTLdata?.selectedFinalMileQuote?.service?.quoteId || "0";
      const distance =
        LTLdata?.selectedFinalMileQuote?.service?.distanceToDestination || "0";
      const fuelSurcharge = LTLdata?.selectedFinalMileQuote?.service?.rateDetails?.customerFuelSurcharge ||
        LTLdata?.selectedFinalMileQuote?.service?.rateDetails?.fuelSurcharge ||
        "0";
      const finalMileAdditionalCharges =
        LTLdata?.selectedFinalMileQuote?.service?.rateDetails
          ?.additionalOtherCharges;

      // Check if selectedFinalMileQuote has a masterHubData
      const fmQuoteMasterHubData = LTLdata?.selectedFinalMileQuote?.masterHubData;
      const fmQuoteHasMasterHub = !!fmQuoteMasterHubData;
      const fmQuoteMasterHubAddress = fmQuoteMasterHubData?.address;
      const fmQuoteMasterHubName = fmQuoteMasterHubData?.name || "";
      if (fmQuoteHasMasterHub && fmQuoteMasterHubAddress) {
        setHasMasterHub(true);
        setMasterHubName(fmQuoteMasterHubName);
        setMasterHubAddress(fmQuoteMasterHubAddress);
      }

      // Use masterHub address if present, otherwise fall back to hub address
      const effectiveFmAddress = fmQuoteHasMasterHub && fmQuoteMasterHubAddress
        ? fmQuoteMasterHubAddress
        : LTLdata?.selectedFinalMileQuote?.hub?.address;

      setSelectedHubData({
        hubName: fmQuoteHasMasterHub ? fmQuoteMasterHubName : LTLdata?.selectedFinalMileQuote?.hub?.name,
        operationFrom:
          effectiveFmAddress?.operationHrsFrom ||
          "00:00",
        operationTo:
          effectiveFmAddress?.operationHrsTo ||
          "23:59",
      });

      setFields((prevData: any) => ({
        ...prevData,
        AgentHub: {
          id: LTLdata?.selectedFinalMileQuote?.hub?.id || "",
          name: LTLdata?.selectedFinalMileQuote?.hub?.name || "",
        },

        selectedQuoteId: quoteId,
        MidStopAddress: effectiveFmAddress?.address || "",
        MidStopAPTSuit: effectiveFmAddress?.aptSuit || "",
        MidStopZipcode: effectiveFmAddress?.zipCode || "",
        MidStopCity: effectiveFmAddress?.city || "",
        MidStopState: effectiveFmAddress?.state || "",
        MidStopCountry: effectiveFmAddress?.country || "",
        MidStopContactName: effectiveFmAddress?.contactPerson || "",
        MidStopPhoneNumber: effectiveFmAddress?.phoneNumber || "",
        MidStopPhoneNumberExt: effectiveFmAddress?.countryCallingCode || "",
        MidStopContactEmail: effectiveFmAddress?.contactEmail || [],
        finalMileDistance: distance,
        finalMileCost: quotePrice,
      }));

      const row = LTLdata?.ltlPlusFinalMileQuote?.map(
        (item: any, index: any) => {
          // Add banyan array logic here
          let banyanArr: any[] = [];
          if (item?.quoteFrom === "banyan") {
            banyanArr = [
              { name: "Freight Charge", amount: item?.customerPrice?.freightCharge },
              { name: "Fuel Surcharge", amount: item?.customerPrice?.fuelSurcharge },
              ...item?.customerPrice?.charges,
            ];
          }

          // Internal LTL rows carry their own mapped Final Mile quote; other rows
          // carry the shared one.
          const rowFm = item?.finalMileData ?? selectedQuoteData;
          const rowFmRate = rowFm?.service?.rateDetails;

          return {
            count: index + 1,
            selectedFinalMileQuote: rowFm,
            carrierName: item?.carrierNameToDisplay ||
              item?.primaryVendor?.preferredName || `${item?.hub?.agent?.name}`,
            finalmileCarrierName: `${rowFm?.hub?.agent?.name || hubName}`,
            internalLTLPopUpDetails: item?.services?.[0] || {},
            scac: item?.scac || item?.primaryVendor?.scac || item?.hub?.agent?.carrierCode,
            basePrice: rowFmRate?.basePrice || basePrice,
            fuelSurcharge: rowFmRate?.customerFuelSurcharge || rowFmRate?.fuelSurcharge || fuelSurcharge,



            additionalOtherCharges: rowFmRate?.additionalOtherCharges ?? finalMileAdditionalCharges,
            price: item?.totalOfferPrice?.value || "",
            priceWithMarkUp: item?.totalOfferPriceWithMarkUp || item?.totalOfferPriceWithMarkupAndFinalMile
              ? parseFloat(item?.totalOfferPriceWithMarkUp.toFixed(2) || item?.totalOfferPriceWithMarkupAndFinalMile.toFixed(2))
              : "0",
            priceWithFinalMile: item?.totalOfferPriceWithMarkupAndFinalMile
              ? parseFloat(item?.totalOfferPriceWithMarkupAndFinalMile.toFixed(2))
              : "0",
            finalMileData: item?.finalMileData,
            quoteId: item?.internalLTL ? item?.services?.[0]?.quoteId : quoteId,
            priceWithMarkUpString: `$${item?.totalOfferPriceWithMarkupAndFinalMile
              ? parseFloat(
                item?.totalOfferPriceWithMarkupAndFinalMile.toFixed(2)
              )
              : parseFloat(item?.totalOfferPrice?.value.toFixed(2))
              }`,
            quotePrice: parseFloat(rowFm?.service?.quotePrice || quotePrice).toFixed(2),
            customerMarkupType: item?.customerMarkUpType,
            markup: item?.customerMarkUpValue || 0,
            mode: serviceSelected?.name,
            additionalCharges: banyanArr.length ? banyanArr :
              item?.offeredProductList?.[0].chargeItemList ||
              item?.services?.[0]?.rateDetails?.additionalOtherCharges
                ?.calculatedCharges,
            productTransactionId: item?.internalLTL
              ? item?.services?.[0]?.quoteId
              : item?.productTransactionId,
            offerId: item?.internalLTL
              ? item?.services?.[0]?.quoteId
              : item?.offerId,
            miles:
              item?.offeredProductList?.[0]?.shopRQShipment?.timeInTransit
                ?.milesInTransit,
            transitTime:
              item?.offeredProductList?.[0]?.shopRQShipment?.timeInTransit
                ?.transitDays ||
              item?.transitTime ||
              item?.transitDays ||
              "1",
            ltlFMData: rowFm,
            pickupAccessorials: pickupAccessorialsSelected,
            dropAccessorials: dropAccessorialsSelected,
            pickupLimitedAccess: pickupLimitedAccessSelected,
            dropLimitedAccess: dropLimitedAccessSelected,
            internalLTL: item?.internalLTL,
            isBanyan: item?.banyanType,
            isProvidence: item?.quoteFrom?.toLowerCase() === "providence",
            quoteFrom: item?.source ? item?.source : item?.banyanType ? item?.quoteFrom + " - " + item?.banyanType : item?.quoteFrom || "Internal",
            displayQuoteFrom: item?.displayQuoteFrom,
            source: item?.source,
            orderFor: item?.quoteFrom,
            finalMileSource: item?.finalMileData?.source,
            lineItems: item?.lineItems || [],
            carrierQuoteId: item?.carrierQuoteId,
            rateRequestLocator: item?.rateRequestLocator,
            // Add banyan array to the returned object
            ...(item?.internalLTL && {
              services: item?.internalLTL,
              quotePrice: item?.services?.[0]?.quotePrice,
              // fuelSurcharge:
              //   item?.services?.[0]?.rateDetails?.fuelSurcharge || 0.0,
              fuelSurcharge:
                item?.services?.[0]?.rateDetails?.customerFuelSurcharge || 0.0,
              additionalOrderCharges: item?.services?.[0]?.rateDetails
                ?.finalMilePricing
                ? item?.services?.[0]?.rateDetails?.customerOtherChargeDetails
                : item?.services?.[0]?.rateDetails?.additionalOtherCharges,
              basePrice: item?.services?.[0]?.rateDetails?.basePrice || 0.0,
              agentName: item?.hub?.agent?.name,
              serviceId: item?.services?.[0]?.id,
            }),
            ...(item?.quoteFrom?.toLowerCase() === "providence" && {

              ltlBaseRate: item?.baseRate || 0,                  // LTL base rate
              ltlFuel: item?.fuel || 0,                          // LTL fuel surcharge
              ltlAccessorialCharges: item?.accessorialCharges || 0, // LTL accessorials
              ltlTotalRate: item?.totalRate || 0,
            }
            )

          };

        }
      );
      return row;
    } else {
      return [];
    }
  };
  const convertTo24HourFormat = (time: string): string => {
    return moment(time, "h:mm A").format("HH:mm");
  };
  const mapLtlList = (LTLdata: any[]): any[] => {
    if (LTLdata) {
      return LTLdata?.map((item, index) => ({
        count: index + 1,
        carrierName: item?.primaryVendor?.preferredName,
        scac: item?.primaryVendor?.scac,
        price: item?.totalOfferPrice?.value || "",
        priceWithMarkUp: item?.totalOfferPriceWithMarkUp
          ? parseFloat(item?.totalOfferPriceWithMarkUp.toFixed(2))
          : parseFloat(item?.totalOfferPrice?.value.toFixed(2)),
        priceWithMarkUpString: `$${item?.totalOfferPriceWithMarkUp
          ? parseFloat(item?.totalOfferPriceWithMarkUp.toFixed(2))
          : parseFloat(item?.totalOfferPrice?.value.toFixed(2))
          }`,
        customerMarkupType: item?.customerMarkUpType,
        markup: item?.customerMarkUpValue || 0,
        mode: serviceSelected.name,
        additionalCharges: item?.offeredProductList[0].chargeItemList,
        productTransactionId: item?.productTransactionId,
        offerId: item?.offerId,
        miles:
          item?.offeredProductList[0]?.shopRQShipment?.timeInTransit
            ?.milesInTransit,
        //distance, offer id, transactionid,
        transitTime:
          item?.offeredProductList[0]?.shopRQShipment?.timeInTransit
            ?.transitDays,
        pickupAccessorials: pickupAccessorialsSelected,
        dropAccessorials: dropAccessorialsSelected,
        pickupLimitedAccess: pickupLimitedAccessSelected,
        dropLimitedAccess: dropLimitedAccessSelected,
      }));
    } else {
      return [];
    }
  };
  /**
   * Prevents invalid characters from being entered in numeric fields.
   *
   * @param {KeyboardEvent<HTMLInputElement>} e - The keyboard event for the input
   */
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "e" || e.key === "E" || e.key === "-" || e.key === "+") {
      e.preventDefault();
    }
  };

  /**
   * handleAddItemDetails
   *
   * Adds a new row to the item details table.
   */
  const handleAddItemDetails = () => {
    const newRow = {
      id: "",
      serial: data.length + 1,
      description: "",
      handlingUnit: "",
      quantity: "",
      weight: "",
      weightUnit: "lbs",
      // weightUnit: { id: "lbs", name: "LBS" },
      itemDimension: {
        length: "",
        width: "",
        height: "",
      },
      unit: { id: "IN", name: "IN" },
      itemClass: "",
      comments: "",
      nmfc: "",
      skuNumber: "",
      packageType: "",
      linearFeet: "",
      package: [],
      stackable: { id: "No", name: "No" },
      hazmat: false,
    };
    setData([newRow, ...data]);
  };
  /**
   * handleBack
   *
   * Handles the back navigation logic, checking if there are unsaved changes.
   */
  const handleBack = () => {
    if (isFormChanged) {
      handleNavigation();
    } else {
      navigate(-1);
    }
  };

  /**
   * handleFieldChange
   *
   * Handles changes to form input fields, updating the state and checking for errors.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event object from the input field.
   */
  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement> | any) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (name === "destinationConsigneeName" || name === "OriginConsigneeName") {
      // Allow only alphabetic characters and special characters, no numeric values
      updatedValue = value.replace(/[0-9]/g, "");
    }
    if (
      name === "destinationZipcode" ||
      name === "OriginZipcode" ||
      name === "PickUpDockDate" ||
      name === "PickUpDockTime"
    ) {
      setShowLTLList(false);
      setFields((prevData) => ({
        ...prevData,
        CarrierName: "",
        serviceCost: "",
        scac: "",
        distance: "",
        offerId: "",
        productTransactionId: "",
      }));
      setGetQuote(false);
    }

    // Prevent leading whitespace
    updatedValue = updatedValue.trimStart();

    setFields((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    setFields((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    checkInputChange();
    compareExpediteDates({ ...fields, [name]: updatedValue });
    // check error
    let error = "";
    switch (name) {
      case "destinationConsigneePhoneNumber":
        error = validateMaskedPhoneNumber(
          value,
          fields.destinationConsigneePhoneExt
        );
        break;
      case "OriginConsigneePhoneNumber":
        error = validateMaskedPhoneNumber(
          value,
          fields.OriginConsigneePhoneExt
        );
        break;
      case "destinationZipcode":
        error = validateZipCode(value);
        break;
      case "OriginZipcode":
        error = validateZipCode(value);
        break;
      case "PickUpDockDate":
        setErrors((prevErrors) => ({
          ...prevErrors,
          PickUpDockTime: "",
          DropDockDate: "",
          DropDockTime: "",
        }));
        if (
          fields.PickUpDockTime != "" &&
          fields.DropDockDate != "" &&
          fields.DropDockTime != ""
        ) {
          error = ValidateDate(
            `${fields.DropDockDate} ${fields.DropDockTime}:00`,
            "Pickup Date & Time should be earlier than Drop date & Time",
            `${value} ${fields.PickUpDockTime}:00`
          );
        }

        break;
      case "PickUpDockTime":
        setErrors((prevErrors) => ({
          ...prevErrors,
          PickUpDockDate: "",
          DropDockDate: "",
          DropDockTime: "",
        }));
        if (
          fields.DropDockDate != "" &&
          fields.DropDockTime != ""
        ) {
          error = ValidateDate(
            `${fields.DropDockDate} ${fields.DropDockTime}:00`,

            "Pickup Date & Time should be earlier than Drop date & Time",
            `${fields.PickUpDockDate} ${value}:00`
          );
        }
        break;
      case "DropDockDate":
        let time = fields.DropDockTime != "" ? fields.DropDockTime : "00:00";
        setErrors((prevErrors) => ({
          ...prevErrors,
          DropDockTime: "",
          PickUpDockTime: "",
          PickUpDockDate: "",
        }));
        if (fields.PickUpDockDate === "") {
          error = ValidateDate(
            `${value} ${time}:00`,
            "Drop Date & Time cannnot be earlier than Current Date &Time"
          );
        } else if (fields.PickUpDockDate != "" && fields.PickUpDockTime != "") {
          error = ValidateDate(
            `${value} ${time}:00`,
            "Drop Date & Time should be greater than or equal to the pickup Date & Time",
            `${fields.PickUpDockDate} ${fields.PickUpDockTime}:00`
          );
        }
        break;
      case "DropDockTime":
        if (fields.DropDockDate != "") {
          setErrors((prevErrors) => ({
            ...prevErrors,
            DropDockDate: "",
            PickUpDockTime: "",
            PickUpDockDate: "",
          }));
          if (fields.PickUpDockDate === "") {
            error = ValidateDate(
              `${fields.DropDockDate} ${value}:00`,
              "Drop Date & Time cannnot be earlier than Current Date &Time"
            );
          } else if (
            fields.PickUpDockDate != "" &&
            fields.PickUpDockTime != ""
          ) {
            error = ValidateDate(
              `${fields.DropDockDate} ${value}:00`,
              "Drop Date & Time should be greater than or equal to the pickup Date & Time",
              `${fields.PickUpDockDate} ${fields.PickUpDockTime}:00`
            );
          }
        }

        break;
      case "destinationConsigneeContactEmail":
        error = value ? validateContactEmail(value) : "";
        break;
      case "OriginConsigneeContactEmail":
        error = value ? validateContactEmail(value) : "";
        break;
      default:
        break;
    }
    if (
      !(
        name === "PickupTargetDateFrom" ||
        name === "PickupTargetTimeFrom" ||
        name === "PickupTargetDateTo" ||
        name === "PickupTargetTimeTo" ||
        name === "PickupDeliveryDateFrom" ||
        name === "PickupDeliveryTimeFrom" ||
        name === "PickupDeliveryDateTo" ||
        name === "PickupDeliveryTimeTo"
      )
    ) {
      setErrors((prevErrors) => {
        return {
          ...prevErrors,
          [name]: error,
        };
      });
    }
  };

  const compareExpediteDates = (fieldsVal: any) => {
    if (fieldsVal.PickupTargetDateFrom !== "") {
      setErrors((prevData) => ({
        ...prevData,
        PickupTargetDateFrom: "",
      }));
      if (fieldsVal.PickupTargetTimeFrom !== "") {
      }
      if (fieldsVal.PickupTargetDateTo !== "") {
        let error = ValidateExpedietedDate(
          `${fieldsVal.PickupTargetDateTo} ${convertTo24HourFormat(fieldsVal.PickupTargetTimeTo) || "00:00"
          }:00`,
          "Pickup Target Date From & Time must be equal to and less than Pickup Target Date To & Time",
          `${fieldsVal.PickupTargetDateFrom} ${convertTo24HourFormat(fieldsVal.PickupTargetTimeFrom) || "00:00"
          }:00`
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateFrom: error,
          }));
        }
      }
      if (fieldsVal.PickupDeliveryDateFrom !== "") {
        let error = ValidateExpedietedDate(
          `${fieldsVal.PickupDeliveryDateFrom} ${convertTo24HourFormat(fieldsVal.PickupDeliveryTimeFrom) || "00:00"
          }:00`,
          "Pickup Target Date From & Time must be equal to and less than Pickup Delivery Date From & Time",
          `${fieldsVal.PickupTargetDateFrom} ${convertTo24HourFormat(fieldsVal.PickupTargetTimeFrom) || "00:00"
          }:00`
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateFrom: error,
          }));
        }
      }
      if (fieldsVal.PickupDeliveryDateTo !== "") {
        let error = ValidateExpedietedDate(
          `${fieldsVal.PickupDeliveryDateTo} ${convertTo24HourFormat(fieldsVal.PickupDeliveryTimeTo) || "00:00"
          }:00`,
          "Pickup Target Date From & Time must be equal to and less than Pickup Delivery Date To & Time",
          `${fieldsVal.PickupTargetDateFrom} ${convertTo24HourFormat(fieldsVal.PickupTargetTimeFrom) || "00:00"
          }:00`
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateFrom: error,
          }));
        }
      }
      if (fieldsVal.PickupTargetTimeFrom !== "") {
        let error = ValidateExpedietedCrrentTime(
          `${fieldsVal.PickupTargetDateFrom} ${convertTo24HourFormat(
            fieldsVal.PickupTargetTimeFrom
          )}:00`,
          "Pickup target date from and time should be greater than current date and time"
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateFrom: error,
          }));
        }
      }
    }
    if (fieldsVal.PickupTargetDateTo !== "") {
      setErrors((prevData) => ({
        ...prevData,
        PickupTargetDateTo: "",
      }));
      if (fieldsVal.PickupDeliveryDateFrom !== "") {
        let error = ValidateExpedietedDate(
          `${fieldsVal.PickupDeliveryDateFrom} ${convertTo24HourFormat(fieldsVal.PickupDeliveryTimeFrom) || "00:00"
          }:00`,
          "Pickup Target Date To & Time must be equal to and less than Pickup Delivery Date From & Time",
          `${fieldsVal.PickupTargetDateTo} ${convertTo24HourFormat(fieldsVal.PickupTargetTimeTo) || "00:00"
          }:00`
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateTo: error,
          }));
        }
      }
      if (fieldsVal.PickupDeliveryDateTo !== "") {
        let error = ValidateExpedietedDate(
          `${fieldsVal.PickupDeliveryDateTo} ${convertTo24HourFormat(fieldsVal.PickupDeliveryTimeTo) || "00:00"
          }:00`,
          "Pickup Target Date To & Time must be equal to and less than Pickup Delivery Date To & Time",
          `${fieldsVal.PickupTargetDateTo} ${convertTo24HourFormat(fieldsVal.PickupTargetTimeTo) || "00:00"
          }:00`
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateTo: error,
          }));
        }
      }
      if (fieldsVal.PickupTargetTimeTo !== "") {
        let error = ValidateExpedietedCrrentTime(
          `${fieldsVal.PickupTargetDateTo} ${convertTo24HourFormat(
            fieldsVal.PickupTargetTimeTo
          )}:00`,
          "Pickup target date to and time should be greater than current date and time"
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateTo: error,
          }));
        }
      }
    }

    if (fieldsVal.PickupDeliveryDateFrom !== "") {
      if (fieldsVal.PickupDeliveryDateTo !== "") {
        let error = ValidateExpedietedDate(
          `${fieldsVal.PickupDeliveryDateTo} ${convertTo24HourFormat(fieldsVal.PickupDeliveryTimeTo) || "00:00"
          }:00`,
          "Pickup Delivery Date From & Time must be equal to and less than Pickup Delivery Date To & Time",
          `${fieldsVal.PickupDeliveryDateFrom} ${convertTo24HourFormat(fieldsVal.PickupDeliveryTimeFrom) || "00:00"
          }:00`
        );

        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupDeliveryDateFrom: error,
          }));
        } else {
          setErrors((prevData) => ({
            ...prevData,
            PickupDeliveryDateFrom: "",
          }));
        }
      }
      if (fieldsVal.PickupTargetTimeFrom !== "") {
        let error = ValidateExpedietedCrrentTime(
          `${fieldsVal.PickupTargetDateTo} ${convertTo24HourFormat(
            fieldsVal.PickupTargetTimeFrom
          )}:00`,
          "Pickup target date to and time should be greater than current date and time"
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateTo: error,
          }));
        }
      }
      if (fieldsVal.PickupDeliveryTimeFrom !== "") {
        let error = ValidateExpedietedCrrentTime(
          `${fieldsVal.PickupDeliveryDateFrom} ${convertTo24HourFormat(
            fieldsVal.PickupDeliveryTimeFrom
          )}:00`,
          "Pickup delivery date from and time should be greater than current date and time"
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupDeliveryDateFrom: error,
          }));
        }
      }
    }
    if (
      fieldsVal.PickupDeliveryDateTo !== "" &&
      fieldsVal.PickupDeliveryTimeTo !== ""
    ) {
      let error = ValidateExpedietedCrrentTime(
        `${fieldsVal.PickupDeliveryDateTo} ${convertTo24HourFormat(
          fieldsVal.PickupDeliveryTimeTo
        )}:00`,
        "Pickup delivery date to and time should be greater than current date and time"
      );
      if (error != "") {
        setErrors((prevData) => ({
          ...prevData,
          PickupDeliveryDateTo: error,
        }));
      } else {
        setErrors((prevData) => ({
          ...prevData,
          PickupDeliveryDateTo: "",
        }));
      }
    }
  };

  /**
   * handleTextAreaChange - Updates the textarea field values based on user input.
   *
   * @param {React.ChangeEvent<HTMLTextAreaElement>} e - The textarea change event.
   */
  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    // Prevent leading whitespace
    const trimmedValue = value.trimStart();

    checkInputChange();
    setFields((prev) => ({
      ...prev,
      [name]: trimmedValue,
    }));
  };

  /**
   * mapItemsDataToPayload - Maps the item details from the form data into a format
   * suitable for the API payload.
   *
   * @param {DataTypes[]} data - The array of item details entered by the user.
   * @returns The mapped array of item details for the API payload.
   */
  const mapItemsDataToPayload = (data: any[]) => {
    return data.map((item) => ({
      id: item.descriptionId || "",
      description: item.description || "",
      handlingUnit: item.handlingUnit || "",
      quantity: item.quantity || "",
      weight: item.weight || "",
      weightUnit: "lbs",
      // weightUnit: item?.weightUnit?.id || "lbs",
      length: item.itemDimension?.length || "",
      width: item.itemDimension?.width || "",
      height: item.itemDimension?.height || "",
      unit: item.unit.id,
      itemClass: item.itemClass.toString() || "",
      comments: item.comments || "",
      nmfc: item.nmfc || "",
      skuNumber: item.skuNumber || "",
      packageType:
        typeof item.packageType === "object"
          ? item.packageType?.name
          : item.packageType || "",
      package: item.package
        .filter((x: any) => x.quantity !== "" && x.weight !== "")
        .map((x: any) => ({ ...x, id: x.descriptionId || "" })),
      stackable:
        item?.stackable == "Yes" ||
          item?.stackable?.name == "Yes" ||
          item?.stackable?.name == "yes"
          ? true
          : false || false,
      linearFeetStackable: 0,
      linearFeet: item?.linearFeet,
      density: item?.density,
      cbm: item?.cbm,
      hazardMaterial: item.hazmat
        ? {
          itemName: item.hazardMaterial?.itemName || "",
          packageType:
            typeof item.hazardMaterial?.packageType === "object"
              ? item.hazardMaterial?.packageType?.name
              : item.hazardMaterial?.packageType || "",
          hazmatClass: item.hazardMaterial?.hazmatClass || "",
          unno: item.hazardMaterial?.unno || "",
          properShippingName: item.hazardMaterial?.properShippingName || "",
          packagingGroup: item.hazardMaterial?.packagingGroup || "",
          contact: item.hazardMaterial?.contact || "",
          phoneNumber: item.hazardMaterial?.phoneNumber || "",
          countryCallingCode: item.hazardMaterial?.countryCallingCode || "",
          poison: item.hazardMaterial?.poison ? true : false,
          isReportableQuantity: item.hazardMaterial?.isReportableQuantity
            ? true
            : false,
          isMarinePollutant: item.hazardMaterial?.isMarinePollutant
            ? true
            : false,
        }
        : {},
    }));
  };

  /**
   * handleSave - Validates the form fields and saves the customer order by sending
   * the data to the API. If there are errors, it prevents submission.
   */
  const handleSave = async () => {
    // Mandatory messages are owned by the Get Quote validation and must not gate Save.
    if (
      Object.values(errors).some(
        (error) => !!error && !isMandatoryMessage(error)
      )
    ) {
      return;
    }
    if (orderTypeRowData?.name !== "Expedited" && !isQuote) {
      return;
    }
    if (!notLTL) {
      if (orderTypeRowData?.name === "Expedited") {
        handleSaveExpediet();
      } else if (orderTypeRowData?.name === "LTL") {
        handleSaveLTL();
      } else {
        handleSaveLTLFinalMile();
      }
      return;
    }
    const apiPayload = {
      groupId: groupQuoteId,
      customerId: customerRowData?.id,
      associatedCustomerId: assCustomerRowData?.id,
      orderTypeId: orderTypeRowData?.id,
      reference: fields.orderReference,
      reference2: moreReferences?.[0]?.["referenceValue"] ?? "",
      reference3: moreReferences?.[1]?.["referenceValue"] ?? "",
      reference4: moreReferences?.[2]?.["referenceValue"] ?? "",
      reference5: moreReferences?.[3]?.["referenceValue"] ?? "",
      serviceId: fields?.selectedServiceId,
      items: mapItemsDataToPayload(data),
      agentHubId: fields?.agentHubId,
      // selected quote id
      quoteId: fields.selectedQuoteId,
      // all quote id's
      allQuoteIds: fields.allQuoteId,
      destinationAddress: {
        address: fields.destinationAddress,
        aptSuit: fields.destinationAPTSuite,
        zipCode: fields.destinationZipcode,
        city: fields.destinationCity,
        state: fields.destinationState,
        country: fields.destinationCountry,
        businessLocation: fields.destinationBusinessLocation,
        contactName: fields.destinationConsigneeName,
        phoneNumber: fields.destinationConsigneePhoneNumber.replace(/\D/g, ""),
        countryCallingCode: fields.destinationConsigneePhoneExt,
        contactEmail: fields.destinationConsigneeContactEmail,
        extraMen: fields.destinationExtramen,
        waitTime: fields.destinationWaitTimeMinutes,
        stairsNumber: fields.destinationNoOfFlights,
        storage: fields.destinationStorage,
        handlingUnit: fields.destinationHandingUnits,
        weight: fields.destinationWeight,
        pieces: fields.destinationPieces,
        specialInstructions: fields.destinationSpecialInstructions,
      },
      midStop: {
        address: masterHubAddress?.address ? masterHubAddress.address : fields.MidStopAddress,
        aptSuit: masterHubAddress?.aptSuit ? masterHubAddress.aptSuit : fields.MidStopAPTSuit,
        zipCode: masterHubAddress?.zipCode ? masterHubAddress.zipCode : fields.MidStopZipcode,
        city: masterHubAddress?.city ? masterHubAddress.city : fields.MidStopCity,
        state: masterHubAddress?.state ? masterHubAddress.state : fields.MidStopState,
        country: masterHubAddress?.country ? masterHubAddress.country : fields.MidStopCountry,
        contactName: masterHubAddress?.contactPerson ? masterHubAddress.contactPerson : fields.MidStopContactName,
        contactEmail: masterHubAddress?.contactEmail ? masterHubAddress.contactEmail : fields.MidStopContactEmail,
        phoneNumber: masterHubAddress?.phoneNumber ? masterHubAddress.phoneNumber : fields.MidStopPhoneNumber,
        countryCallingCode: masterHubAddress?.countryCallingCode ? masterHubAddress.countryCallingCode : fields.MidStopPhoneNumberExt,
      },
      document: {
        fileUrl: documentInfo.files[0]?.fileUrl,
        urls: documentInfo.urls,
      },
      orderSwapAndExchange: fields.additionalServices.includes("Order Swap & Exchange"),
      additionalServices: fields.additionalServices,
      removeAndDisposal: fields.additionalServices.includes("Remove & Disposal"),
    };
    try {
      setLoading(true);
      if (documentInfo.files?.length && documentInfo.files[0]?.fileUrl) {
        const formData = new FormData();
        formData.append("file", documentInfo.files[0]?.fileUrl);
        const uploadApiResponse = await postApi<any>(
          `/order/upload-doc`,
          formData
        );
        if (uploadApiResponse.success) {
          const data = uploadApiResponse?.data;
          apiPayload.document.fileUrl = data.url;
          const apiResponse = await postApi(`/order`, apiPayload);
          if (apiResponse.success) {
            handleUpdateNavigation();
            setMessage(ORDER_MANAGEMENT_MESSAGES.CUSTOMER_ORDER_CREATE_SUCCESS, "success");
            navigate("/all-orders");
          } else {
            setMessage(
              apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
              "error"
            );
          }
        } else {
          setMessage(
            uploadApiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
            "error"
          );
        }
      } else {
        const apiResponse = await postApi(`/order`, apiPayload);
        if (apiResponse.success) {
          handleUpdateNavigation();
          setMessage(ORDER_MANAGEMENT_MESSAGES.CUSTOMER_ORDER_CREATE_SUCCESS, "success");
          navigate("/all-orders");
        } else {
          setMessage(
            apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
            "error"
          );
        }
      }
    } catch (error) {
      console.error(GENERAL_ERROR_MESSAGE, error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveExpediet = async () => {
    const apiPayload = {
      customerId: assCustomerRowData?.id || customerRowData?.id,
      orderTypeId: orderTypeRowData?.id,
      agentId: agentRowData?.id,
      agentHubId: agentHubRowData?.id,
      serviceId: serviceSelected?.id,
      reference: fields.orderReference,
      reference2: moreReferences?.[0]?.["referenceValue"] ?? "",
      reference3: moreReferences?.[1]?.["referenceValue"] ?? "",
      reference4: moreReferences?.[2]?.["referenceValue"] ?? "",
      reference5: moreReferences?.[3]?.["referenceValue"] ?? "",
      courierProOne: fields.courierPro1,
      courierProTwo: fields.courierPro2,
      originCity: fields.originCity,
      distanceToDestination: "",
      totalPrice: fields.serviceCost,
      items: mapItemsDataToPayload(data),
      originAddress: {
        address: fields.OriginAddress || "",
        aptSuit: fields.OriginAPTSuite || "",
        zipCode: fields.OriginZipcode || "",
        city: fields.OriginCity || "",
        state: fields.OriginState || "",
        country: fields.OriginCountry || "",
        businessLocation: fields.OriginBusinessLocation || "",
        contactName: fields.OriginConsigneeName || "",
        phoneNumber: fields.OriginConsigneePhoneNumber.replace(/\D/g, ""),
        countryCallingCode: fields.OriginConsigneePhoneExt || "",
        contactEmail: fields.OriginConsigneeContactEmail || "",
        pickUpTargetDateTimeFrom: getISODate(
          fields?.PickupTargetDateFrom,
          fields?.PickupTargetTimeFrom
        ),
        pickUpTargetDateTimeTo: getISODate(
          fields?.PickupTargetDateTo,
          fields?.PickupTargetTimeTo
        ),
        pickUpDeliveryTargetDateTimeFrom: getISODate(
          fields?.PickupDeliveryDateFrom,
          fields?.PickupDeliveryTimeFrom
        ),
        pickUpDeliveryTargetDateTimeTo: getISODate(
          fields?.PickupDeliveryDateTo,
          fields?.PickupDeliveryTimeTo
        ),
      },
      destinationAddress: {
        address: fields.destinationAddress,
        aptSuit: fields.destinationAPTSuite,
        zipCode: fields.destinationZipcode,
        city: fields.destinationCity,
        state: fields.destinationState,
        country: fields.destinationCountry,
        businessLocation: fields.destinationBusinessLocation,
        contactName: fields.destinationConsigneeName,
        phoneNumber: fields.destinationConsigneePhoneNumber.replace(/\D/g, ""),
        countryCallingCode: fields.destinationConsigneePhoneExt,
        contactEmail: fields.destinationConsigneeContactEmail,
        extraMen: fields.destinationExtramen,
        waitTime: fields.destinationWaitTimeMinutes,
        stairsNumber: fields.destinationNoOfFlights,
        storage: fields.destinationStorage,
        handlingUnit: fields.destinationHandingUnits,
        weight: fields.destinationWeight,
        pieces: fields.destinationPieces,
        specialInstructions: fields.destinationSpecialInstructions,
      },
      document: {
        fileUrl: documentInfo.files[0]?.fileUrl,
        urls: documentInfo.urls,
      },
    };
    try {
      setLoading(true);

      if (documentInfo.files?.length && documentInfo.files[0]?.fileUrl) {
        const formData = new FormData();
        formData.append("file", documentInfo.files[0]?.fileUrl);
        const uploadApiResponse = await postApi<any>(
          `/order/upload-doc`,
          formData
        );
        if (uploadApiResponse.success) {
          const data = uploadApiResponse?.data;
          apiPayload.document.fileUrl = data.url;
          const apiResponse = await postApi(
            `/order/createexpeditedorder`,
            apiPayload
          );
          if (apiResponse.success) {
            handleUpdateNavigation();
            setMessage(ORDER_MANAGEMENT_MESSAGES.CUSTOMER_ORDER_CREATE_SUCCESS, "success");
            navigate("/all-orders");
          } else {
            setMessage(
              apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
              "error"
            );
          }
        } else {
          setMessage(
            uploadApiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
            "error"
          );
        }
      } else {
        const apiResponse = await postApi(
          `/order/createexpeditedorder`,
          apiPayload
        );
        if (apiResponse.success) {
          handleUpdateNavigation();
          setMessage(ORDER_MANAGEMENT_MESSAGES.CUSTOMER_ORDER_CREATE_SUCCESS, "success");
          navigate("/all-orders");
        } else {
          setMessage(
            apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
            "error"
          );
        }
      }
    } catch (error) {
      console.error(GENERAL_ERROR_MESSAGE, error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLTL = async () => {

    let apiPayload = {
      groupId: groupQuoteId,
      request: {
        quoteDetail: {
          serviceId: serviceSelected?.id,
          customerId: customerRowData?.id,
          associatedCustomerId: assCustomerRowData?.id,
          orderTypeId: orderTypeRowData?.id,
          scac: fields?.scac || "",
          reference: fields.orderReference,
          reference2: moreReferences?.[0]?.["referenceValue"] ?? "",
          reference3: moreReferences?.[1]?.["referenceValue"] ?? "",
          reference4: moreReferences?.[2]?.["referenceValue"] ?? "",
          reference5: moreReferences?.[3]?.["referenceValue"] ?? "",
          distanceToDestination: fields.distance || "",
          totalWeight: fields.destinationWeight || "",
          threePLProvider: fields.plProvider || "",
          courierProOne: fields.courierPro1 || "",
          courierProTwo: fields.offerId || "",
          items: mapItemsDataToPayload(data),
          pickUpDock: {
            dockName: fields.PickUpDockName || "",
            pickupNumber: fields.PickUpDOckNumber || "",
            pickUpDate: getISODate(
              fields.PickUpDockDate || "",
              fields.PickUpDockTime || ""
            ),
            pickUpTime: fields.PickUpDockTime || "",
            note: fields.PickUpDOckNote || "",
            dockOpenTime:
              convertTo24HourFormat(fields.PickUpDockOpenTime) || "",
            dockCloseTime:
              convertTo24HourFormat(fields.PickUpDOckCloseTime) || "",
            firstComeFirstServe: fields.PickUpFCFS,
          },
          dropDock: {
            dockName: fields.DropDockName || "",
            pickupNumber: fields.DropDOckNumber || "",
            dropDate: "2024-12-12",
            dropTime: "19:44",
            note: fields.DropDOckNote || "",
            dropOpenTime: convertTo24HourFormat(fields.DropDockOpenTime) || "",
            dropCloseTime:
              convertTo24HourFormat(fields.DropDOckCloseTime) || "",
            firstComeFirstServe: fields.DropFCFS,
          },
          originAddress: {
            address: fields.OriginAddress || "",
            aptSuit: fields.OriginAPTSuite || "",
            zipCode: fields.OriginZipcode || "",
            city: fields.OriginCity || "",
            state: fields.OriginState || "",
            country: fields.OriginCountry || "",
            businessLocation: fields.OriginBusinessLocation || "",
            contactName: fields.OriginConsigneeName || "",
            phoneNumber: fields.OriginConsigneePhoneNumber.replace(/\D/g, ""),
            countryCallingCode: fields.OriginConsigneePhoneExt || "",
            contactEmail: fields.OriginConsigneeContactEmail || "",
            accessorials: pickupAccessorialsSelected,
            locationType: pickupLimitedAccessSelected?.name || "",
          },
          destinationAddress: {
            address: fields.destinationAddress || "",
            aptSuit: fields.destinationAPTSuite || "",
            zipCode: fields.destinationZipcode || "",
            city: fields.destinationCity || "",
            state: fields.destinationState || "",
            country: fields.destinationCountry || "",
            businessLocation: fields.destinationBusinessLocation || "",
            contactName: fields.destinationConsigneeName || "",
            phoneNumber: fields.destinationConsigneePhoneNumber.replace(
              /\D/g,
              ""
            ),
            countryCallingCode: fields.destinationConsigneePhoneExt || "",
            contactEmail: fields.destinationConsigneeContactEmail || "",
            handlingUnit: fields.destinationHandingUnits || "",
            weight: fields.destinationWeight || "",
            pieces: fields.destinationPieces || "",
            accessorials: dropAccessorialsSelected,
            locationType: dropLimitedAccessSelected?.name || "",
          },
        },
        shipmentProductTransactionId: fields.productTransactionId || "",
        shipmentOfferId: fields.offerId || "",
        shipment: {},
        isSelfScheduled: false,
        pickupDate: getISODate(fields.PickUpDockDate || "", fields.PickUpDockTime || ""),
        closeTime: fields.PickUpDOckCloseTime
          ? `${convertTo24HourFormat(fields.PickUpDOckCloseTime)}:00`
          : "",
        readyTime: fields?.PickUpDockOpenTime
          ? `${convertTo24HourFormat(fields.PickUpDockOpenTime)}:00`
          : "",
        shipmentReferenceList: mapReferences(true),
        pickupSpecialInstructions: fields.PickUpDOckNote || "",
        deliverySpecialInstructions: fields.DropDOckNote || "",
        specialInstructions: "updated main instr",
      },
    };
    try {
      setLoading(true);

      const apiResponse = await postApi(`/order/createltlorder`, apiPayload);
      if (apiResponse.success) {
        handleUpdateNavigation();
        setMessage(ORDER_MANAGEMENT_MESSAGES.CUSTOMER_ORDER_CREATE_SUCCESS, "success");
        navigate("/all-orders");
      } else {
        setMessage(
          apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
      console.error(GENERAL_ERROR_MESSAGE, error);
    } finally {
      setLoading(false);
    }
  };
  const handleSaveLTLFinalMile = async () => {

    const apiPayload = {
      groupId: groupQuoteId,
      quoteDetail: {
        customerId: customerRowData?.id,
        associatedCustomerId: assCustomerRowData?.id,
        orderTypeId: orderTypeRowData.id,
        agentHubId: fields?.AgentHub?.id || "",
        reference: fields.orderReference,
        reference2: moreReferences?.[0]?.["referenceValue"] ?? "",
        reference3: moreReferences?.[1]?.["referenceValue"] ?? "",
        reference4: moreReferences?.[2]?.["referenceValue"] ?? "",
        reference5: moreReferences?.[3]?.["referenceValue"] ?? "",
        serviceId: serviceSelected?.id,
        finalmile_quote_id: fields.selectedQuoteId,
        distanceToDestination: fields.finalMileDistance,
        totalWeight: fields.destinationWeight,
        totalPrice: fields.finalMileCost,
        customerMarkupType: fields.customerMarkupType,
        allQuoteIds: [fields.selectedQuoteId],
        destinationAddress: {
          address: fields.destinationAddress || "",
          aptSuit: fields.destinationAPTSuite || "",
          zipCode: fields.destinationZipcode || "",
          city: fields.destinationCity || "",
          state: fields.destinationState || "",
          country: fields.destinationCountry || "",
          businessLocation: fields.destinationBusinessLocation || "",
          contactName: fields.destinationConsigneeName || "",
          phoneNumber: fields.destinationConsigneePhoneNumber.replace(
            /\D/g,
            ""
          ),
          countryCallingCode: fields.destinationConsigneePhoneExt || "",
          contactEmail: fields.destinationConsigneeContactEmail || "",
          extraMen: fields.destinationExtramen,
          waitTime: fields.destinationWaitTimeMinutes,
          stairsNumber: fields.destinationNoOfFlights,
          storage: fields.destinationStorage,
          handlingUnit: fields.destinationHandingUnits || "",
          weight: fields.destinationWeight || "",
          pieces: fields.destinationPieces || "",
          accessorials: mapAccessorials(dropAccessorialsSelected) || [],
          locationType: dropLimitedAccessSelected?.name || "",
        },
        midStop: {
          address: masterHubAddress?.address ? masterHubAddress.address : fields.MidStopAddress,
          aptSuit: masterHubAddress?.aptSuit ? masterHubAddress.aptSuit : fields.MidStopAPTSuit,
          zipCode: masterHubAddress?.zipCode ? masterHubAddress.zipCode : fields.MidStopZipcode,
          city: masterHubAddress?.city ? masterHubAddress.city : fields.MidStopCity,
          state: masterHubAddress?.state ? masterHubAddress.state : fields.MidStopState,
          country: masterHubAddress?.country ? masterHubAddress.country : fields.MidStopCountry,
          contactName: masterHubAddress?.contactPerson ? masterHubAddress.contactPerson : fields.MidStopContactName,
          contactEmail: masterHubAddress?.contactEmail ? masterHubAddress.contactEmail : fields.MidStopContactEmail,
          phoneNumber: masterHubAddress?.phoneNumber ? masterHubAddress.phoneNumber : fields.MidStopPhoneNumber,
          countryCallingCode: masterHubAddress?.countryCallingCode ? masterHubAddress.countryCallingCode : fields.MidStopPhoneNumberExt,
        },

        document: {
          fileUrl: "",
          urls: [""],
        },
        additionalServices: [],
        items: mapItemsDataToPayload(data),
      },
      ltlQuoteDetail: {
        threePLProvider: fields.plProvider,
        courierProOne: fields.courierPro1,
        scac: fields.scac,
        agentName: !fields.scac && fields?.internalLTL ? (fields?.CarrierName || "") : "",
        courierProtwo: fields.courierPro2,
        customerMarkupType: fields.customerMarkupType,
        shipmentProductTransactionId: fields.productTransactionId,
        shipmentOfferId: fields.offerId,
        pickUpDock: {
          dockName: fields.PickUpDockName || "",
          pickupNumber: fields.PickUpDOckNumber || "",
          pickUpDate: getISODate(
            fields.PickUpDockDate || "",
            fields.PickUpDockTime || ""
          ),
          pickUpTime: fields.PickUpDockTime || "",
          note: fields.PickUpDOckNote || "",
          dockOpenTime: fields.PickUpDockOpenTime ? convertTo24HourFormat(fields.PickUpDockOpenTime) : "",
          dockCloseTime: fields.PickUpDOckCloseTime ? convertTo24HourFormat(fields.PickUpDOckCloseTime) : "",
          firstComeFirstServe: fields.PickUpFCFS,
        },
        dropDock: {
          dockName: selectedHubData.hubName || "",
          pickupNumber: fields.DropDOckNumber || "",
          dropDate: "2024-12-12",
          dropTime: "19:44",
          note: fields.DropDOckNote || "",
          dropOpenTime: selectedHubData.operationFrom || "",
          dropCloseTime: selectedHubData.operationTo || "",
          firstComeFirstServe: fields.DropFCFS,
        },
        originAddress: {
          address: fields.OriginAddress || "",
          aptSuit: fields.OriginAPTSuite || "",
          zipCode: fields.OriginZipcode || "",
          city: fields.OriginCity || "",
          state: fields.OriginState || "",
          country: fields.OriginCountry || "",
          businessLocation: fields.OriginBusinessLocation || "",
          contactName: fields.OriginConsigneeName || "",
          phoneNumber: fields.OriginConsigneePhoneNumber.replace(/\D/g, ""),
          countryCallingCode: fields.OriginConsigneePhoneExt || "",
          contactEmail: fields.OriginConsigneeContactEmail || "",
          accessorials: mapAccessorials(pickupAccessorialsSelected),
          locationType: pickupLimitedAccessSelected?.name || "",
        },
        shipment: {},
        isSelfScheduled: false,
        pickupDate: getISODate(fields.PickUpDockDate || "", fields.PickUpDockTime || ""),
        closeTime: fields.PickUpDOckCloseTime
          ? `${convertTo24HourFormat(fields.PickUpDOckCloseTime)}:00`
          : "",
        readyTime: fields?.PickUpDockOpenTime
          ? `${convertTo24HourFormat(fields.PickUpDockOpenTime)}:00`
          : "",
        shipmentReferenceList: fields.orderReference ? mapReferences(true) : [],
        pickupSpecialInstructions: fields.PickUpDOckNote || "",
        deliverySpecialInstructions: fields.DropDOckNote || "",
        specialInstructions: "updated main instr",
      },
    };
    try {
      setLoading(true);
      if (documentInfo.files?.length && documentInfo.files[0]?.fileUrl) {
        const formData = new FormData();
        formData.append("file", documentInfo.files[0]?.fileUrl);
        const uploadApiResponse = await postApi<any>(
          `/order/upload-doc`,
          formData
        );
        if (uploadApiResponse.success) {
          const data = uploadApiResponse?.data;
          apiPayload.quoteDetail.document.fileUrl = data.url;

          const apiResponse = await postApi(
            `/order/ltl-plus-finalmile`,
            apiPayload
          );
          if (apiResponse.success) {
            handleUpdateNavigation();
            setMessage(ORDER_MANAGEMENT_MESSAGES.CUSTOMER_ORDER_CREATE_SUCCESS, "success");
            navigate("/all-orders");
          } else {
            setMessage(
              apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
              "error"
            );
          }
        } else {
          setMessage(
            uploadApiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
            "error"
          );
        }
      } else {
        const apiResponse = await postApi(
          `/order/ltl-plus-finalmile`,
          apiPayload
        );
        if (apiResponse.success) {
          handleUpdateNavigation();
          setMessage(ORDER_MANAGEMENT_MESSAGES.CUSTOMER_ORDER_CREATE_SUCCESS, "success");
          navigate("/all-orders");
        } else {
          setMessage(
            apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
            "error"
          );
        }
      }
    } catch (error) {
      console.error(GENERAL_ERROR_MESSAGE, error);
    } finally {
      setLoading(false);
    }
  };

  const resetLocationState = () => {
    // setIsSelectedParent(false);
    // Clear the location state by navigating to the same route with empty state
    navigate(location.pathname, {
      state: null,
      replace: true, // This replaces the current history entry instead of adding a new one
    });
  };

  const mapAccessorials = (accessorialsData: any[]): any[] => {
    return accessorialsData.map((item) => ({
      id: item?.id || "",
      name: item?.name || "",
    }));
  };

  /**
   * getZipcodeDetails - Fetches and updates city, state, and country information
   * based on the provided zipcode.
   */
  const getZipcodeDetails = async () => {
    if ((fields.destinationZipcode?.length ?? 0) < 5) {
      setDestinationCities([]);
      setFields((prev) => ({ ...prev, destinationCity: "", destinationState: "", destinationCountry: "" }));
    }
    if (fields.destinationZipcode?.length >= 5) {
      const url = `${GET_DATA_FROM_ZIPCODE}${fields.destinationZipcode}`;
      const apiResponse = await getApi<any>(url);
      if (apiResponse?.success) {
        const data = apiResponse?.data;
        const cities: string[] = (data?.cities || []).map((c: any) => c.city || "").filter(Boolean);
        const primaryCity: string = data?.city || "";
        const orderedCities = primaryCity
          ? [primaryCity, ...cities.filter((c) => c.toLowerCase() !== primaryCity.toLowerCase())]
          : cities;
        setDestinationCities(orderedCities);
        setFields((prev) => ({
          ...prev,
          destinationCity: primaryCity,
          destinationState: data?.state || "",
          destinationCountry: data?.country || "",
        }));
      }
    }
    if ((fields.OriginZipcode?.length ?? 0) < 5) {
      setOriginCities([]);
      setFields((prev) => ({ ...prev, OriginCity: "", OriginState: "", OriginCountry: "" }));
    }
    if (fields.OriginZipcode?.length >= 5) {
      const url = `${GET_DATA_FROM_ZIPCODE}${fields.OriginZipcode}`;
      const apiResponse = await getApi<any>(url);
      if (apiResponse?.success) {
        const data = apiResponse?.data;
        const cities: string[] = (data?.cities || []).map((c: any) => c.city || "").filter(Boolean);
        const primaryCity: string = data?.city || "";
        const orderedCities = primaryCity
          ? [primaryCity, ...cities.filter((c) => c.toLowerCase() !== primaryCity.toLowerCase())]
          : cities;
        setOriginCities(orderedCities);
        setFields((prev) => ({
          ...prev,
          OriginCity: primaryCity,
          OriginState: data?.state || "",
          OriginCountry: data?.country || "",
        }));
      }
    }
  };

  /**
   * getQuotesData - Fetches quotes for the entered data, and updates the state
   * with the returned quotes. If the quotes are successfully fetched, it maps the
   * data into a usable format for the application.
   */
  const getQuotesData = async () => {
    if (!runQuoteValidation("getQuote")) return;
    resetLocationState();
    setGetQuote(true);
    setStateQuoteId("");
    // Clear the whole previous quote before the request goes out, so nothing from the
    // last click can survive into this one; the response below repopulates it and the
    // loader covers the gap. This is the only place that reliably runs on every click:
    // `getQuote` is already true on a repeat click, so setGetQuote(true) is a no-op and
    // the reset effect keyed on it never fires again.
    SetQuoteData([]);
    setHasMasterHub(false);
    setMasterHubName("");
    setMasterHubAddress({});
    setMasterHubNameToTransfer("");
    setMasterHubAddressToTransfer({});
    // empty mid stop fields value
    setFields((prev: any) => ({
      ...prev,
      MidStopAddress: "",
      MidStopAPTSuit: "",
      MidStopZipcode: "",
      MidStopCity: "",
      MidStopState: "",
      MidStopCountry: "",
      MidStopContactName: "",
      MidStopPhoneNumber: "",
      MidStopPhoneNumberExt: "",
      MidStopContactEmail: [],
    }));
    // set api payload data
    const apiPayload = {
      customerId: assCustomerRowData?.id || customerRowData?.id,
      serviceIds: isMultiServiceQuote
        ? serviceSelectedList.map((service: any) => service?.id)
        : [serviceSelected?.id],
      // Opt-in flag: tells the backend to return every agent with all of its services,
      // ordered closest agent first and cheapest service first within each agent.
      ...(isMultiServiceQuote ? { multiServiceQuote: true } : {}),
      items: mapItemsDataToPayload(data),
      destinationAddress: {
        address: fields.destinationAddress,
        aptSuit: fields.destinationAPTSuite,
        zipCode: fields.destinationZipcode,
        city: fields.destinationCity,
        state: fields.destinationState,
        country: fields.destinationCountry,
        businessLocation: fields.destinationBusinessLocation,
        contactName: fields.destinationConsigneeName,
        phoneNumber: fields.destinationConsigneePhoneNumber.replace(/\D/g, ""),
        countryCallingCode: fields.destinationConsigneePhoneExt,
        contactEmail: fields.destinationConsigneeContactEmail,
        extraMen: fields.destinationExtramen,
        waitTime: fields.destinationWaitTimeMinutes,
        stairsNumber: fields.destinationNoOfFlights,
        storage: fields.destinationStorage,
        handlingUnit: fields.destinationHandingUnits,
        weight: fields.destinationWeight,
        pieces: fields.destinationPieces,
        specialInstructions: fields.destinationSpecialInstructions,
      },
      additionalServices: fields.additionalServices,
    };
    try {
      setLoading(true);
      const apiResponse = await postApi<GetQuoteApiResponse[]>(
        `/order/order-quote/generate`,
        apiPayload
      );
      if (apiResponse.success) {
        const data = apiResponse?.data || [];
        const mappedData = data.map((item) => {
          // `masterHub` can come back empty or absent even with a `masterHubId` set;
          // reading `.name` off it unguarded throws, and a throw here escapes the whole
          // map to the catch below, which used to leave the previous quote on screen.
          if (item.hub?.masterHubId) {
            setHasMasterHub(true);
            setMasterHubName(item.masterHub?.name || "");
            // Never store undefined — consumers read these alongside hasMasterHub and
            // the initial state is `{}`, so keep that invariant.
            setMasterHubAddress(item.masterHub?.address || {});
            setMasterHubAddressToTransfer(item.masterHub?.address || {});
            setMasterHubNameToTransfer(item.masterHub?.name || "");
          }
          // Collect all quoteIds for the current item
          const allQuoteIds = item.services
            .map((service) => service?.quoteId)
            .filter((quoteId) => quoteId != null);
          const services = item.services.map((service, serviceIndex) => ({
            id: service?.id,
            serial: serviceIndex + 1,
            serviceName: service?.name || "",
            description: service?.description || "",
            serviceLevel: service?.serviceType?.name,
            quotePrice: service?.quotePrice || 0.0,
            fuelSurcharge: orderTypeRowData?.name === "Final Mile"
              ? service?.rateDetails?.customerFuelSurcharge
              : service?.rateDetails?.fuelSurcharge || 0.0,
            additionalOrderCharges:
              service?.rateDetails?.additionalOtherCharges,
            basePrice: service?.rateDetails?.basePrice || 0.0,
            totalWeight: service?.totalWeight || 0,
            deliverWithin: service?.deliverWithin || 0,
            distanceToDestination: service?.distanceToDestination || 0,
            shortName: service?.shortName || "",
            longName: service?.longName || "",
            useOver150Lbs: service?.useOver150Lbs || false,
            address: item.hub.address,
            operationHrsFrom: item.hub?.address?.operationHrsFrom || "",
            operationHrsTo: item.hub?.address?.operationHrsTo || "",
            hubName: item.hub.masterHubId
              ? item.masterHub.name
              : item.hub?.name || "",
            agentHubId: item.hub?.id || "",
            addressName: item.hub?.address?.address || "",
            agentName: item.hub?.agent?.name || "",
            quoteId: service?.quoteId,
            allQuoteId: allQuoteIds,
          }));
          const info: QuoteInfo = {
            agentName: item.hub?.agent?.name || "",
            operationHrsFrom: item.hub?.address?.operationHrsFrom || "",
            operationHrsTo: item.hub?.address?.operationHrsTo || "",
            hubName: item.hub?.name || "",
            addressName: item.hub?.address?.address || "",
            address: item.hub.address,
            agentHubId: item.hub?.id || "",
            // Carry each agent's own master hub. With several agents on screen the
            // shared masterHubName/masterHubAddress state holds only the last one,
            // which would otherwise be rendered on every agent's header.
            hasMasterHub: !!item.hub?.masterHubId,
            masterHubName: item.hub?.masterHubId
              ? item.masterHub?.name
              : undefined,
            masterHubAddress: item.hub?.masterHubId
              ? item.masterHub?.address
              : undefined,
            distanceToDestination:
              item.services?.[0]?.distanceToDestination || 0,
          };

          return { info, services };
        });
        SetQuoteData(mappedData);
      } else {
        SetQuoteData([]);
        setMessage(
          apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
      // Anything thrown while mapping escapes here with quoteData untouched, which
      // leaves the previous quote's agents on screen as though they were this
      // request's result. Clear the list and say so rather than showing stale hubs.
      SetQuoteData([]);
      setMessage(GENERAL_ERROR_MESSAGE, "error");
      console.error(GENERAL_ERROR_MESSAGE, error);
    } finally {
      setLoading(false);
    }
  };

  /** Red outline for any field currently carrying a validation message. */
  const fieldErrorClass = (key: string): string =>
    errors?.[key] ? " form__input--error" : "";

  /**
   * Clear handling-unit highlights as soon as the values arrive — typed in, or filled in
   * bulk by picking a saved item from the Description dropdown.
   */
  useEffect(() => {
    setInvalidCells((prev) => pruneItemIssues(prev, data));
    setInvalidPackageCells((prev) => prunePackageIssues(prev, data));
    if (data?.length > 0) setItemsEmpty(false);
  }, [data]);

  /**
   * runQuoteValidation - Checks everything the Get Quote buttons used to check silently
   * through their `disabled` expression, and turns any gap into visible feedback:
   * a message under each blank field plus scroll-and-focus on the first one.
   *
   * @returns true when the caller may proceed with the request.
   */
  const runQuoteValidation = (intent: "getQuote" | "getLtlQuote"): boolean => {
    const result = validateOrderForm({
      intent,
      fields,
      data,
      errors,
      orderTypeName: orderTypeRowData?.name,
      notLTL,
      customerId: customerRowData?.id,
      orderTypeId: orderTypeRowData?.id,
      serviceId: serviceSelected?.id,
      // A pure LTL quote forces the order type and never shows a Service Level picker.
      requireOrderTypeId: quoteType !== "traditional",
      requireServiceId: quoteType !== "traditional",
      quoteGateChecksErrors: true,
      ltlQuoteRequiresItemClass: intent === "getLtlQuote",
    });

    const cells: Record<number, string[]> = {};
    const packageCells: Record<number, Record<number, string[]>> = {};
    result.itemIssues.forEach((issue) => {
      if (issue.columnKeys.length > 0) cells[issue.rowIndex] = issue.columnKeys;
      issue.packageIssues.forEach((pkg) => {
        packageCells[issue.rowIndex] = {
          ...packageCells[issue.rowIndex],
          [pkg.packageIndex]: pkg.columnKeys,
        };
      });
    });
    setInvalidCells(cells);
    setInvalidPackageCells(packageCells);
    setItemsEmpty(result.itemsEmpty);

    const { ok, focusItemCell } = applyValidation(result);
    setFocusCell(focusItemCell);
    return ok;
  };

  /**
   * isButtonDisabled - A memoized function that determines if the "Book" button
   * should be disabled based on the form's completeness and validity.
   *
   * @returns {boolean} - Returns true if the "Book" button should be disabled,
   *                      false otherwise.
   */
  const isButtonDisabled = useMemo(() => {
    let areFieldsComplete;
    if (notLTL) {
      areFieldsComplete = !orderMandatoryFields.some((field) => !fields[field]);
    } else {
      if (orderTypeRowData?.name === "Expedited") {
        areFieldsComplete = !orderExpeditedMandatoryFields.some(
          (field) => !fields[field]
        );
        if (!agentRowData?.id) {
          areFieldsComplete = false;
        } else {
          if (
            agentRowData?.agentType &&
            agentRowData.agentType != "LTL" &&
            !agentHubRowData?.id
          ) {
            areFieldsComplete = false;
          }
        }
      } else if (orderTypeRowData?.name === "LTL") {
        areFieldsComplete = !orderLTLMandatoryFields.some(
          (field) => !fields[field]
        );
      } else {
        areFieldsComplete = !orderLTLFinalMileMandatoryFields.some(
          (field) => !fields[field]
        );
      }
    }

    // NMFC# is not mandatory on any order type.
    const areDataFieldsValid =
      data?.length > 0 &&
      data?.every(
        (item) =>
          item.description &&
          item.handlingUnit &&
          item.quantity &&
          item.weight &&
          item.packageType
      );
    if (
      customerRowData?.id &&
      orderTypeRowData?.id &&
      areFieldsComplete &&
      serviceSelected?.id &&
      areDataFieldsValid &&
      Object.values(errors).every(
        (value) => value === "" || isMandatoryMessage(value)
      )
    ) {
      return false;
    }
    return true;
  }, [
    customerRowData?.id,
    orderTypeRowData?.id,
    orderTypeRowData?.name,
    serviceSelected,
    fields,
    data,
    agentRowData?.id,
    agentHubRowData?.id,
    // The body reads both, and `errors` now also carries the Get Quote validation's
    // messages — without these the memo could hand back a stale result.
    errors,
    notLTL,
  ]);

  function fixFormat(dateStr: string): string {
    // If it's already in 'YYYY-MM-DD' format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }

    // If it's an ISO string (like '2025-07-01T19:00:00.000Z'), extract date
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split("T")[0]; // Extracts 'YYYY-MM-DD'
    }

    // If invalid date, return empty string to avoid sending bad payload
    return "";
  }

  const getTraditionalQuoteData = async () => {
    if (!runQuoteValidation("getLtlQuote")) return;
    // Reset common state like in LTL flow
    setStateQuoteId("");
    // setShowLTLData(false);
    // setShowFMList(false);
    setFields((prevData) => ({
      ...prevData,
      CarrierName: "",
      serviceCost: "",
      scac: "",
      distance: "",
      offerId: "",
      productTransactionId: "",
    }));

    // ------- helpers -------
    const asLbs = (wt: number | string | undefined) => {

      const num = Number(wt || 0);
      // return data?.[0]?.unit?.name?.toLowerCase() === "in" ? num : kgToLbs(num);
      const weightUnit = data?.[0]?.weightUnit?.name?.toLowerCase();

      // If weight is in kgs, convert to lbs; otherwise assume it's already in lbs
      return weightUnit === "kgs" ? kgToLbs(num) : num;
    };

    const addrLineList = (addr?: string, apt?: string) => {
      if (addr && apt) return [addr, apt];
      if (addr) return [addr];
      return [];
    };

    const minDim = (n: number) => (n && n > 0 ? n : 1); // avoid 0-sized dims
    const normalizePkgType = (val: any) => {
      if (!val) return "BALE";
      if (typeof val === "string") return val;
      if (typeof val === "object") return val?.name ?? val?.id ?? "BALE";
      return String(val);
    };

    // === NEW: packaging helpers (minimal) ===
    // normalize to singular, uppercase enum string (e.g., "BAGS" -> "BAG")
    const toPkgString = (val: any) => {
      const raw =
        typeof val === "string"
          ? val
          : typeof val === "object" && (val?.name || val?.id)
            ? val.name || val.id
            : "BAG";
      const up = String(raw).trim().toUpperCase();
      const map: Record<string, string> = {
        BAGS: "BAG",
        BAG: "BAG",
        BOXES: "BOX",
        BOX: "BOX",
        BALES: "BALE",
        BALE: "BALE",
        PALLETS: "PALLET",
        PALLET: "PALLET",
        DRUMS: "DRUM",
        DRUM: "DRUM",
        CARTONS: "CARTON",
        CARTON: "CARTON",
        CRATES: "CRATE",
        CRATE: "CRATE",
      };
      return map[up] || up;
    };

    // object form for shippedItemList.packagingType
    const toPkgObject = (val: any) => {
      const s = toPkgString(val);
      return { id: s, name: s };
    };
    // === end NEW helpers ===

    // Map UI line-items (data) -> traditional items payload
    const mapTraditionalItems = (rows: any[] = []) =>
      rows.map((row: any) => {
        const unit = String(row?.unit ?? row?.Unit ?? "in").toLowerCase(); // "in" | "cm"
        const qty = Number(row?.quantity ?? row?.Quantity ?? 0);
        const handlingUnit = Number(
          row?.handlingUnit ?? row?.HandlingUnit ?? qty ?? 0
        );

        const toInches = (val: number) =>
          unit === "cm" ? Number((val / 2.54).toFixed(3)) : val;

        const rawL = Number(row?.itemDimension?.length ?? 0);
        const rawW = Number(row?.itemDimension?.width ?? 0);
        const rawH = Number(row?.itemDimension?.height ?? 0);

        const lengthIn = toInches(minDim(rawL));
        const widthIn = toInches(minDim(rawW));
        const heightIn = toInches(minDim(rawH));

        // weight per piece -> string
        const wt = asLbs(row?.weight ?? row?.Weight ?? 0);

        return {
          id: row?.descriptionId || "",
          description: row?.description ?? "",
          handlingUnit: String(handlingUnit), // string per sample
          quantity: String(qty), // string per sample
          weight: String(wt), // string per sample
          weightUnit: "lbs",
          length: String(lengthIn), // string per sample
          width: String(widthIn), // string per sample
          height: String(heightIn), // string per sample
          itemClass: String(row?.itemClass ?? row?.Class ?? "300"),
          comments: row?.comments ?? "",
          nmfc: row?.nmfc ?? "",
          skuNumber: row?.skuNumber ?? "",
          // === CHANGED: ensure singular, uppercase enum string
          packageType: row?.packageType,

          // packageType: toPkgString(
          //   normalizePkgType(row?.packageType ?? row?.PackageType)
          // ),
          // === end change
          package: Array.isArray(row?.package) ? row?.package : [],
          stackable: Boolean(row?.stackable ?? row?.Stackable ?? false),
          linearFeetStackable: Number(row?.linearFeetStackable ?? 0),
          linearFeet: Number(row?.linearFeet ?? row?.LinearFeet ?? 0),
          density: Number(row?.density ?? row?.Density ?? 0),
          cbm: Number(row?.cbm ?? row?.CBM ?? 0),
          hazardMaterial: row?.hazardMaterial ?? {},
        };
      });

    // Compute a consistent total weight (LB) from items (qty * per-piece weight)
    const computeTotalWeightLb = (rows: any[] = []) => {
      let total = 0;
      rows.forEach((row: any) => {
        const qty = Number(row?.quantity ?? row?.Quantity ?? 1);
        const perPiece = asLbs(row?.weight ?? row?.Weight ?? 0);
        total += qty * perPiece;
      });
      // Fallback to destinationWeight if items don’t have weights
      if (total <= 0) total = asLbs(fields?.destinationWeight || 0);
      return Number(total.toFixed(2));
    };
    // ------- payload -------
    const items = mapTraditionalItems(data);
    const shipmentTotalWeight = computeTotalWeightLb(data);

    // === NEW: post-process mapLTLItems to enforce packaging formats
    // const mapTraditionalItems = (rows: any[] = []) =>
    const handlingUnitList = mapLTLItems(data)?.map((hu: any) => ({
      ...hu,
      // top-level HU packagingType must be a STRING (e.g., "BAG")
      packagingType: hu?.packagingType,
      // packagingType: toPkgString(hu?.packagingType),
      // shipped item packagingType must be OBJECT {id,name}
      shippedItemList: (hu?.shippedItemList || []).map((si: any) => ({
        ...si,
        packagingType: si?.packagingType,
        // packagingType: toPkgObject(si?.packagingType,
      })),
    }));

    const body = {
      quoteDetail: {
        customerId: assCustomerRowData?.id
          ? assCustomerRowData?.id
          : customerRowData?.id,
        items,

        destinationAddress: {
          address: fields?.destinationAddress || "",
          aptSuit: fields?.destinationAPTSuite || "",
          zipCode: fields?.destinationZipcode || "",
          city: fields?.destinationCity || "",
          state: fields?.destinationState || "",
          country: fields?.destinationCountry || "",
          businessLocation: fields?.destinationBusinessLocation || "",
          contactName: fields?.destinationConsigneeName || "",
          phoneNumber: fields?.destinationConsigneePhoneNumber
            ? fields.destinationConsigneePhoneNumber.replace(/\D/g, "")
            : "",
          countryCallingCode: fields?.destinationConsigneePhoneExt || "+1",
          contactEmail: fields?.destinationConsigneeContactEmail || "",
          extraMen: fields?.destinationExtramen || "",
          waitTime: fields?.destinationWaitTimeMinutes || "",
          stairsNumber: fields?.destinationNoOfFlights || "",
          storage: fields?.destinationStorage || "",
          handlingUnit: Number(fields?.destinationHandingUnits || 0),
          weight: Number(fields?.destinationWeight || 0),
          pieces: Number(fields?.destinationPieces || 0),
          specialInstructions: fields?.DropDOckNote || "",
        },

        additionalServices: Array.isArray(fields?.additionalServices)
          ? fields.additionalServices
          : [],
      },

      ltlQuoteDetail: {
        request: {
          productType: "LTL",
          quoteDetail: {
            customerId: assCustomerRowData?.id
              ? assCustomerRowData?.id
              : customerRowData?.id,
          },
          shipment: {
            shipmentDate:
              fields.PickUpDockDate !== "" && fields.PickUpDockTime !== ""
                ? getISODate(fields.PickUpDockDate, fields.PickUpDockTime)
                : fields.PickUpDockDate !== ""
                  ? getISODate(fields.PickUpDockDate, moment().format("hh:mm A"))
                  : "",

            originAddress: {
              address: {
                addressLineList: addrLineList(
                  fields.OriginAddress,
                  fields.OriginAPTSuite
                ),
                locality: fields.OriginCity || "",
                region: fields.OriginState || "",
                postalCode: fields.OriginZipcode || "",
                countryCode: fields.OriginCountry || "",
                companyName: fields.OriginBusinessLocation || "",
                phone: fields.OriginConsigneePhoneNumber
                  ? `${fields.OriginConsigneePhoneNumber.replace(/\D/g, "")}`
                  : "",
                contactList: [
                  {
                    phone: fields.OriginConsigneePhoneNumber
                      ? `${fields.OriginConsigneePhoneNumber.replace(
                        /\D/g,
                        ""
                      )}`
                      : "",
                    firstName: fields.OriginConsigneeName || "",
                  },
                ],
                locationType:
                  (pickupLimitedAccessSelected &&
                    pickupLimitedAccessSelected?.name) ||
                  "",
              },
            },

            destinationAddress: {
              address: {
                addressLineList: addrLineList(
                  fields.destinationAddress,
                  fields.destinationAPTSuite
                ),
                locality: fields.destinationCity || "",
                region: fields.destinationState || "",
                postalCode: fields.destinationZipcode || "",
                countryCode: fields.destinationCountry || "",
                companyName: fields.destinationBusinessLocation || "",
                phone: fields.destinationConsigneePhoneNumber
                  ? fields.destinationConsigneePhoneNumber.replace(/\D/g, "")
                  : "",
                contactList: [
                  {
                    phone: fields.destinationConsigneePhoneNumber
                      ? fields.destinationConsigneePhoneNumber.replace(
                        /\D/g,
                        ""
                      )
                      : "",
                    firstName: fields.destinationConsigneeName || "",
                  },
                ],
                locationType:
                  (dropLimitedAccessSelected &&
                    dropLimitedAccessSelected?.name) ||
                  "",
              },
            },

            // NOTE: mapLTLItems must itself ensure:
            // - top-level packagingType is STRING
            // - shippedItemList.packagingType is OBJECT {id,name}
            // - linearFeet is STRING
            handlingUnitList, // ← enforced via post-process above

            totalWeight: {
              value: shipmentTotalWeight, // consistent sum
              unit: "LB",
            },

            insuranceRequestFlag: false,
            totalHandlingUnitCount: Number(fields.destinationHandingUnits || 0),
            shipmentReferenceList: fields.orderReference
              ? mapReferences(true)
              : [],

            appointmentDeliveryFlag: dropAccessorialsSelected.some(
              (x: any) => x.name === "Appointment"
            ),
            directDeliveryOnlyFlag: null,
            holdAtTerminalFlag: dropAccessorialsSelected.some(
              (x: any) => x.name === "Hold"
            ),
            insideDeliveryFlag: dropAccessorialsSelected.some(
              (x: any) => x.name === "Inside Delivery"
            ),
            insidePickupFlag: pickupAccessorialsSelected.some(
              (x: any) => x.name === "Inside Pickup"
            ),
            carrierTerminalPickupFlag: pickupAccessorialsSelected.some(
              (x: any) => x.name === "Airport Pickup"
            ),
            liftgateDeliveryFlag: dropAccessorialsSelected.some(
              (x: any) => x.name === "LiftgateDelivery"
            ),
            liftgatePickupFlag: pickupAccessorialsSelected.some(
              (x: any) => x.name === "Liftgate"
            ),
            notifyBeforeDeliveryFlag: dropAccessorialsSelected.some(
              (x: any) => x.name === "Delivery Notification"
            ),
            protectionFromColdFlag: false,
            residentialDeliveryFlag: dropAccessorialsSelected.some(
              (x: any) => x.name === "Residential"
            ),
            residentialPickupFlag: pickupAccessorialsSelected.some(
              (x: any) => x.name === "Residential"
            ),
            sortAndSegregateFlag: pickupAccessorialsSelected.some(
              (x: any) => x.name === "Sort and Segregate"
            ),

            pickupSpecialInstructions: fields.PickUpDOckNote || "",
            deliverySpecialInstructions: fields.DropDOckNote || "",

            tradeshowDeliveryFlag: dropAccessorialsSelected.some(
              (x: any) => x.name === "Tradeshow Delivery"
            ),
            palletJackDeliveryFlag: dropAccessorialsSelected.some(
              (x: any) => x.name === "Pallet Jack"
            ),
            whiteGloveNoAssemblyDeliveryFlag: dropAccessorialsSelected.some(
              (x: any) => x.name === "White Glove No Assembly"
            ),
            whiteGloveWithAssemblyDeliveryFlag: dropAccessorialsSelected.some(
              (x: any) => x.name === "White Glove with Assembly"
            ),
            tradeshowDeliveryName: "",
            tradeshowPickupFlag: pickupAccessorialsSelected.some(
              (x: any) => x.name === "Tradeshow Pickup"
            ),
            tradeshowPickupName: "",
          },
        },
        correlationId: "WWEX-M2M-shopFlow-ltl",
      },
    };

    try {
      setLoading(true);
      const apiResponse = await postApi<any>(
        `/order/order-quote/traditionalquote`,
        body
      );
      if (apiResponse.success) {
        const resp = apiResponse?.data || {};
        SetQuoteData(resp?.traditonalQuote);
        setShowLTLList(true);
        setIsLTLFinalMile(false);
      } else {
        SetQuoteData([]);
        setMessage(
          apiResponse?.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
      console.error(GENERAL_ERROR_MESSAGE, error);
      setMessage(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Clear Redux traditionalQuoteData when QuickQuote component mounts
   */
  useEffect(() => {
    dispatch(clearTraditionalQuoteData());
  }, []);

  /**
   * Updates `parentCustomerId` when `customerRowData.id` changes to keep them in sync.
   */
  useEffect(() => {
    if (customerRowData && customerRowData?.id) {
      clearLTLQuoteDetails();
      setParentCustomerId(customerRowData.id);
      setFields((prevData) => ({
        ...prevData,
        serviceCost: "",
      }));
    }
  }, [customerRowData]);

  useEffect(() => {
    setFields((prevData) => ({
      ...prevData,
      serviceCost: "",
    }));
    setShowLTLList(false);
  }, [serviceSelected, assCustomerRowData]);

  const clearLTLQuoteDetails = () => {
    setShowLTLList(false);
    setFields((prevData) => ({
      ...prevData,
      CarrierName: "",
      serviceCost: "",
      scac: "",
      distance: "",
      offerId: "",
      productTransactionId: "",
    }));
  };
  const clearFinalMileQuoteDetails = () => {
    setShowLTLList(false);
    setGetQuote(false);
    setIsQuote(false);
    setFields((prevData) => ({
      ...prevData,
      CarrierName: "",
      serviceCost: "",
      scac: "",
      distance: "",
      offerId: "",
      productTransactionId: "",
    }));
  };
  useEffect(() => {
    if (!saveQuotationPage) {
      clearLTLQuoteDetails();
      clearFinalMileQuoteDetails();
    }
  }, [
    data,
    dropAccessorialsSelected,
    dropLimitedAccessSelected,
    pickupAccessorialsSelected,
    pickupLimitedAccessSelected,
    assCustomerRowData,
    customerRowData,
    serviceSelected,
    // Changing the Final Mile service selection must invalidate a stale quote too.
    serviceSelectedList,
    fields?.additionalServices,
    fields?.destinationNoOfFlights,
    fields?.destinationExtramen,
    fields?.destinationStorage,
    fields?.destinationWaitTimeMinutes,
  ]);
  /**
   * Keeps the Final Mile multi-select and the legacy single `serviceSelected` in step so
   * the existing single-service consumers (validation, the disabled-button memo, the LTL
   * payloads) keep working unchanged.
   *
   * Deliberately driven from the change handler rather than an effect: an effect keyed on
   * the order type would also fire when a saved quote restores `serviceSelected`
   * directly, and would overwrite that restore with an empty selection.
   */
  const handleServiceSelectionChange = (rows: any) => {
    const nextRows = Array.isArray(rows) ? rows : [];
    setServiceSelectedList(nextRows);
    setServiceSelected(nextRows[0] ?? {});
    clearMandatory(DROPDOWN_FIELD_KEYS.service);
  };

  useEffect(() => {
    if (!saveQuotationPage) {
      setIsQuote(false);
      setServiceSelected([]);
      setServiceSelectedList([]);
      // Service Type always starts at Delivery when the order type changes.
      serviceTypeRef.current = LOGISTICS_TYPE.DELIVERY;
      setServiceType(LOGISTICS_TYPE.DELIVERY);
    }
    clearLTLQuoteDetails();
    if (
      (orderTypeRowData &&
        orderTypeRowData.id &&
        orderTypeRowData?.name &&
        orderTypeRowData?.name?.toLowerCase() === "ltl") ||
      orderTypeRowData?.name?.toLowerCase() === "expedited" ||
      orderTypeRowData?.name?.toLowerCase() === "ltl + final mile"
    ) {
      getAccessorialsList();
      getServiceList(orderTypeRowData.id || "");
      setNotLTL(false);
    } else {
      if (orderTypeRowData && orderTypeRowData.id) {
        getServiceList(
          orderTypeRowData?.id,
          isFinalMileOrderType && !saveQuotationPage
            ? LOGISTICS_TYPE.DELIVERY
            : undefined,
        );
      }
      setNotLTL(true);
    }
    if (data.length && !saveQuotationPage) {
      const updatedData: any = data.map((item) => {
        item.packageType = "";
        return item;
      });
      setData([...updatedData]);
    }
  }, [orderTypeRowData]);

  const getAccessorialsList = async () => {
    try {
      setLoading(true);
      let url = `/order/ltl-accessorial/wwex`;
      const agentResponse = await getApi<any>(url);
      if (agentResponse.success) {
        const data = agentResponse.data || [];
        const pickup = data.filter((item: any) => item?.isPickup);
        setPickupAccessorialDropdownData(pickup);
        const drop = data.filter((item: any) => item?.isDelivery);
        setDropAccessorialDropdownData(drop);
      }
    } catch (error) {
      setMessage(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };

  const getServiceList = async (orderType: string, logisticsType?: string) => {
    try {
      setLoading(true);
      orderType = orderType.split(" + ").join(",");
      let url = `/agent/service/?limit=15&page=1&orderTypeId=${orderType}`;
      if (logisticsType) {
        url += `&logisticsType=${logisticsType}`;
      }
      const agentResponse = await getApi<any>(url);
      // Final Mile only: drop a response for a Service Type the user has since switched away from.
      if (logisticsType && logisticsType !== serviceTypeRef.current) {
        return [];
      }
      if (agentResponse.success) {
        setServiceList(agentResponse?.data);
        return agentResponse?.data || [];
      }
    } catch (error) {
      console.log(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
    return [];
  };

  /**
   * Final Mile Service Type change. The Service Level list is filtered solely by the
   * service's `logisticsType` (delivery | pickup) — never by service name. When Pickup has
   * exactly one service level, it is selected automatically.
   */
  const handleServiceTypeChange = async (_: string, item: any) => {
    // The choice is required: ignore a null selection so the field can never be cleared.
    if (!item?.id || item.id === serviceType) return;
    serviceTypeRef.current = item.id;
    setServiceType(item.id);
    setIsQuote(false);
    handleServiceSelectionChange([]);
    const services = await getServiceList(orderTypeRowData?.id || "", item.id);
    if (item.id === LOGISTICS_TYPE.PICKUP && services.length === 1) {
      handleServiceSelectionChange([{ id: services[0].id, name: services[0].name }]);
    }
  };
  /**
   * useEffect Hook - Fetches and updates the destination address details
   * (city, state, country) whenever the user changes the zipcode.
   */
  useEffect(() => {
    getZipcodeDetails();
    if (!saveQuotationPage) {
      clearLTLQuoteDetails();
      clearFinalMileQuoteDetails();
    }
  }, [fields?.destinationZipcode]);

  useEffect(() => {
    getZipcodeDetails();
    if (!saveQuotationPage) {
      clearLTLQuoteDetails();
      clearFinalMileQuoteDetails();
    }
  }, [fields?.OriginZipcode]);
  /**
   * useEffect Hook - Runs on component mount to fetch country codes, agent service
   * list, and updates the customer data based on user role.
   */
  useEffect(() => {
    if (!isUserOfOmniMove && userRole === "customer") {
      setCustomerRowData({
        id: localStorage.getItem(CUSTOMER_ID) ?? "",
        name: "",
        customerNumber: "",
        customerLevel: "",
      });
    }
    if (state?.page === "Save Quotation") {
      setSaveQuotationPage(true);
      getQuotationDetails(state.id);
    }
  }, []);

  useEffect(() => {
    if (orderTypeRowData?.name === "LTL") {
      setIsLTLFinalMile(false);
      setFields((prev) => ({
        ...prev,
        AgentHub: { id: "", name: "" },
        MidStopAddress: "",
        MidStopAPTSuit: "",
        MidStopZipcode: "",
        MidStopCity: "",
        MidStopState: "",
        MidStopCountry: "",
        MidStopContactName: "",
        MidStopPhoneNumber: "",
        MidStopPhoneNumberExt: "+1",
        MidStopContactEmail: [],
        finalMileDistance: "",
        finalMileCost: "",
        selectedQuoteId: "",
      }));
      setSelectedHubData({
        operationFrom: "",
        operationTo: "",
        hubName: "",
      });
    }
  }, [orderTypeRowData]);

  const getQuotationDetails = async (quoteGroupId: string) => {
    try {
      setLoading(true);
      let url = `/order/order-quote/detailsavedquotation?quoteGroupById=${quoteGroupId}`;
      const quoteResponse = await getApi<any>(url);
      if (quoteResponse.success) {
        const data = quoteResponse?.data?.[quoteGroupId]?.[0] || [];
        if (data.masterHub.name) setMasterHubName(data?.masterHub?.name);
        if (data.masterHub.address)
          setMasterHubAddress(data?.masterHub?.address);
        if (
          customerLevel === "first" &&
          data?.customer?.customerLevel === "third"
        ) {
          setCustomerRowData({
            id: data?.customer?.parentCustomer?.id ?? "",
            name: data?.customer?.parentCustomer?.name ?? "",
            customerNumber: "",
            customerLevel: data?.customer?.parentCustomer?.customerLevel ?? "",
          });
          setTimeout(() => {
            setAssCustomerRowData({
              id: data?.customer?.id ?? "",
              name: data?.customer?.name ?? "",
              customerNumber: "",
              customerLevel: data?.customer?.customerLevel ?? "",
            });
          }, 300);
        } else {
          setCustomerRowData({
            id: data?.customer?.id ?? "",
            name: data?.customer?.name ?? "",
            customerNumber: "",
            customerLevel: data?.customer?.customerLevel ?? "",
          });
        }
        setOrderTypeRowData({
          id: data?.orderType?.id ?? "",
          name: data?.orderType?.name ?? "",
          description: data?.orderType?.description ?? "",
        });
        setServiceSelected({
          id: data?.service?.id ?? "",
          name: data?.service?.name ?? "",
        });
        // Keep the Final Mile multi-select in step with the restored service so the
        // chip is rendered; other order types never read this list.
        setServiceSelectedList(
          data?.service?.id
            ? [{ id: data.service.id, name: data.service.name ?? "" }]
            : []
        );
        setServiceType(
          data?.service?.logisticsType === LOGISTICS_TYPE.PICKUP
            ? LOGISTICS_TYPE.PICKUP
            : LOGISTICS_TYPE.DELIVERY
        );
        let refArray = [
          {
            referenceCount: 2,
            referenceValue: data?.reference2 ?? "",
          },
        ];
        if (data?.reference3) {
          refArray.push({
            referenceCount: 3,
            referenceValue: data?.reference3 ?? "",
          });
        }
        if (data?.reference4) {
          refArray.push({
            referenceCount: 4,
            referenceValue: data?.reference4 ?? "",
          });
        }
        if (data?.reference5) {
          refArray.push({
            referenceCount: 5,
            referenceValue: data?.reference5 ?? "",
          });
        }

        setMoreReferences(refArray);
        setFields((prevData) => ({
          ...prevData,
          destinationAddress: data?.destinationAddress?.address ?? "",
          destinationAPTSuite: data?.destinationAddress?.aptSuit ?? "",
          destinationZipcode: data?.destinationAddress?.zipCode ?? "",
          destinationCity: data?.destinationAddress?.city ?? "",
          destinationState: data?.destinationAddress?.state ?? "",
          destinationCountry: data?.destinationAddress?.country ?? "",
          destinationBusinessLocation:
            data?.destinationAddress?.businessLocation ?? "",
          destinationConsigneeName: data?.destinationAddress?.contactName ?? "",
          destinationConsigneePhoneExt:
            data?.destinationAddress?.countryCallingCode ?? "+1",
          destinationConsigneePhoneNumber:
            data?.destinationAddress?.phoneNumber ?? "",
          destinationConsigneeContactEmail:
            data?.destinationAddress?.contactEmail ?? "",
          destinationExtramen: data?.destinationAddress?.extraMen ?? "",
          destinationNoOfFlights: data?.destinationAddress?.stairsNumber ?? "",
          destinationWaitTimeMinutes: data?.destinationAddress?.waitTime ?? "",
          destinationHandingUnits: data?.destinationAddress?.handlingUnit ?? "",
          destinationWeight: data?.destinationAddress?.weight ?? "",
          destinationSpecialInstructions:
            data?.destinationAddress?.specialInstructions ?? "",
          orderReference: data?.reference ?? "",
          orderReference2: data?.reference2 ?? "",
          orderReference3: data?.reference3 ?? "",
          orderReference4: data?.reference4 ?? "",
          orderReference5: data?.reference5 ?? "",
          additionalServices: data?.additionalServices ?? [],
          selectedServiceId: data?.service?.id ?? "",
          OriginAddress: data?.originAddress?.address ?? "",
          OriginAPTSuite: data?.originAddress?.aptSuit ?? "",
          OriginZipcode: data?.originAddress?.zipCode ?? "",
          OriginCity: data?.originAddress?.city ?? "",
          OriginState: data?.originAddress?.state ?? "",
          OriginCountry: data?.originAddress?.country ?? "",
          OriginBusinessLocation: data?.originAddress?.businessLocation ?? "",
          OriginConsigneeName: data?.originAddress?.contactName ?? "",
          OriginConsigneePhoneExt:
            data?.originAddress?.countryCallingCode ?? "+1",
          OriginConsigneePhoneNumber: data?.originAddress?.phoneNumber ?? "",
          OriginConsigneeContactEmail: data?.originAddress?.contactEmail ?? "",
          PickupTargetDateFrom: data?.originAddress?.pickUpTargetDateTimeFrom
            ? normalizeDate(data?.originAddress?.pickUpTargetDateTimeFrom)
            : "",
          PickupTargetTimeFrom: data?.originAddress?.pickUpTargetDateTimeFrom
            ? extractTime(data?.originAddress?.pickUpTargetDateTimeFrom)
            : "",
          PickupTargetDateTo: data?.originAddress?.pickUpTargetDateTimeTo
            ? normalizeDate(data?.originAddress?.pickUpTargetDateTimeTo)
            : "",
          PickupTargetTimeTo: data?.originAddress?.pickUpTargetDateTimeTo
            ? extractTime(data?.originAddress?.pickUpTargetDateTimeTo)
            : "",
          PickupDeliveryDateFrom: data?.originAddress?.pickUpDeliveryTargetDateTimeFrom
            ? normalizeDate(data?.originAddress?.pickUpDeliveryTargetDateTimeFrom)
            : "",
          PickupDeliveryTimeFrom: data?.originAddress?.pickUpDeliveryTargetDateTimeFrom
            ? extractTime(data?.originAddress?.pickUpDeliveryTargetDateTimeFrom)
            : "",
          PickupDeliveryDateTo: data?.originAddress?.pickUpDeliveryTargetDateTimeTo
            ? normalizeDate(data?.originAddress?.pickUpDeliveryTargetDateTimeTo)
            : "",
          PickupDeliveryTimeTo: data?.originAddress?.pickUpDeliveryTargetDateTimeTo
            ? extractTime(data?.originAddress?.pickUpDeliveryTargetDateTimeTo)
            : "",
          PickUpDockDate: data?.lTLPickUpDock?.pickUpDate
            ? normalizeDate(data?.lTLPickUpDock?.pickUpDate)
            : moment().format("YYYY-MM-DD"),
          PickUpDockTime: data?.lTLPickUpDock?.pickUpDate
            ? extractTime(data?.lTLPickUpDock?.pickUpDate)
            : "08:00 AM",
          PickUpDockName: data?.lTLPickUpDock?.dockName ?? "",
          PickUpDOckNumber: data?.lTLPickUpDock?.pickupNumber ?? "",
          PickUpDockOpenTime:
            data?.lTLPickUpDock?.dockOpenTime === "Invalid date"
              ? ""
              : formatTimeForDisplay(data?.lTLPickUpDock?.dockOpenTime),
          PickUpDOckCloseTime:
            data?.lTLPickUpDock?.dockCloseTime === "Invalid date"
              ? ""
              : formatTimeForDisplay(data?.lTLPickUpDock?.dockCloseTime),
          PickUpDOckNote: data?.lTLPickUpDock?.note ?? "",
          PickUpFCFS: data?.lTLPickUpDock?.firstComeFirstServe ?? "",
          LTLDropAccessorials: [],
          DropDockName: data?.ltlDropDock?.dockName ?? "",
          DropDOckNumber: data?.ltlDropDock?.pickupNumber ?? "",
          DropDockOpenTime:
            data?.orderType?.name === "LTL + Final Mile"
              ? data?.ltlDropDock?.dropOpenTime ?? ""
              : formatTimeForDisplay(data?.ltlDropDock?.dropOpenTime),
          DropDOckCloseTime:
            data?.orderType?.name === "LTL + Final Mile"
              ? data?.ltlDropDock?.dropCloseTime ?? ""
              : formatTimeForDisplay(data?.ltlDropDock?.dropCloseTime),
          DropDOckNote: data?.ltlDropDock?.note ?? "",
          DropFCFS: data?.ltlDropDock?.firstComeFirstServe ?? false,
        }));
        const mapOrderData = (userData: any) =>
          userData.map((user: any, index: number) => ({
            id: user?.id || "",
            serial: userData.length - index,
            density: user?.density || "",
            cbm: user?.cbm || "",
            description: user?.description || "",
            handlingUnit: user?.handlingUnit || "",
            quantity: user?.quantity || "",
            weight: user?.weight || "",
            itemDimension: {
              length: user?.length || "",
              width: user?.width || "",
              height: user?.height || "",
            },
            unit: user?.unit
              ? { name: user?.unit, id: user?.unit }
              : user?.weightUnit === "kgs"
                ? { name: "CM", id: "CM" }
                : { name: "IN", id: "IN" },
            itemClass: user?.itemClass || "",
            comments: user?.comments || "",
            nmfc: user?.nmfc || "",
            skuNumber: user?.skuNumber || "",
            packageType: user?.packageType || "",
            package: user?.package || [],
            stackable:
              user?.stackable === true
                ? { name: "Yes", id: "Yes" }
                : { name: "No", id: "No" },
            weightUnit: { name: user?.weightUnit, id: user?.weightUnit },
            hazmat: user?.hazmats?.unno ? true : false,
            linearFeet: user?.linearFeet,
            hazardMaterial: user?.hazmats?.unno
              ? {
                itemName: user.hazmats?.itemName || "",
                packageType:
                  typeof user.hazmats?.packageType === "object"
                    ? user.hazmats?.packageType?.name
                    : user.hazmats?.packageType || "",
                hazmatClass: user.hazmats?.hazmatClass || "",
                unno: user.hazmats?.unno || "",
                properShippingName: user.hazmats?.properShippingName || "",
                packagingGroup: user.hazmats?.packagingGroup || "",
                contact: user.hazmats?.contact || "",
                phoneNumber: user.hazmats?.phoneNumber || "",
                countryCallingCode: user.hazmats?.countryCallingCode || "",
                poison: user.hazmats?.poison ? true : false,
                isReportableQuantity: user.hazmats?.isReportableQuantity
                  ? true
                  : false,
                isMarinePollutant: user.hazmats?.isMarinePollutant
                  ? true
                  : false,
              }
              : {},
          }));
        setData(mapOrderData([...(data?.orderItem || [])].reverse()));
        if (
          data?.orderType?.name === "Final Mile" &&
          expiredData(data?.createdAt) &&
          !data?.isExpiry
        ) {
          let QuoteDataArr = [];
          const mappedData = (qData: any) => {
            const services = quoteResponse?.data?.[quoteGroupId].map(
              (item: any, serviceIndex: number) => ({
                id: item?.id || item?.descriptionId || "",
                serial: serviceIndex + 1,
                serviceId: item?.service?.id,
                serviceName: item?.service?.name || "",
                description: item?.service?.description || "",
                serviceLevel: item?.serviceType?.name,
                quotePrice: item?.pricing?.total || 0.0,
                fuelSurcharge: item?.rateDetails?.fuelSurcharge || 0.0,
                additionalOrderCharges:
                  item?.rateDetails?.additionalOtherCharges,
                basePrice: item?.rateDetails?.basePrice || 0.0,
                totalWeight: item?.totalWeight || 0,
                deliverWithin: item?.deliverWithin || 0,
                distanceToDestination: item?.distanceToDestination || 0,
                shortName: item?.service?.shortName || "",
                longName: item?.service?.longName || "",
                useOver150Lbs: item?.service?.useOver150Lbs || false,
                address: item?.agentHub?.address || "",
                operationHrsFrom: hasMasterHub
                  ? masterHubAddress?.operationHrsFrom
                  : item.agentHub?.address?.operationHrsFrom || 0,
                operationHrsTo: hasMasterHub
                  ? masterHubAddress?.operationHrsTo
                  : item.agentHub?.address?.operationHrsTo || 0,
                hubName: hasMasterHub
                  ? masterHubName
                  : item.agentHub?.name || "",
                agentHubId: item.agentHub?.id || "",
                addressName: hasMasterHub
                  ? masterHubAddress?.address
                  : item.agentHub?.address || "",
                agentName: item.agentHub?.agent?.name || "",
                quoteId: item?.id,
                allQuoteId: [item?.id],
              })
            );
            const info: QuoteInfo = {
              agentName: qData.agentHub?.agent?.name || "",
              operationHrsFrom: hasMasterHub
                ? masterHubAddress?.operationHrsFrom
                : qData.agentHub?.operationHrsFrom || "",
              operationHrsTo: hasMasterHub
                ? masterHubAddress?.operationHrsTo
                : qData.agentHub?.operationHrsTo || "",
              hubName: hasMasterHub
                ? masterHubName
                : qData.agentHub?.name || "",
              addressName: hasMasterHub
                ? masterHubAddress?.address
                : qData.agentHub?.address || "",
              address: hasMasterHub
                ? masterHubAddress?.address
                : qData?.agentHub?.address || "",
              distanceToDestination: qData?.distanceToDestination || 0,
            };

            return { info, services };
          };
          QuoteDataArr.push(mappedData(data));
          SetQuoteData(QuoteDataArr);
          setGetQuote(true);
        }
        setErrors((prevErrors) => ({
          ...prevErrors,
          destinationZipcode: "",
          OriginZipcode: "",
          DropDockDate: "",
          PickUpDockTime: "",
          PickUpDockDate: "",
        }));
        if (
          data?.orderType?.name === "LTL" ||
          data?.orderType?.name === "LTL + Final Mile"
        ) {
          setPickupAccessorialsSelected(data?.originAddress?.accessorials);
          setDropAccessorialsSelected(data?.destinationAddress?.accessorials);
          setPickupLimitedAccesssSelected({
            id: data?.originAddress?.locationType,
            name: data?.originAddress?.locationType,
          });
          setDropLimitedAccesssSelected({
            id: data?.destinationAddress?.locationType,
            name: data?.destinationAddress?.locationType,
          });
        }
      }
    } catch (error) {
      console.log(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };

  const getApiPackageData = async () => {
    const url = `/service/packagetype?from=order&finalMileLTL=both`;
    const apiResponse = await getApi<any>(url);
    if (!apiResponse?.success) return;

    // const packageData = apiResponse?.data

    let packageData
    if (orderTypeRowData?.name === "LTL + Final Mile" || orderTypeRowData?.name === "LTL") {
      packageData = apiResponse?.data?.ltl
    } else {
      packageData = apiResponse?.data?.finalMile
    }
    const filteredData = packageData
      .filter((item: any) => item.isActive && !item.isDeleted)
      .map((item: any) => ({
        id: item.id,
        name: item.packageCode || "",
      }));
    setApiPackageType(filteredData);
  };

  useEffect(() => {
    if (orderTypeRowData?.name === "LTL + Final Mile") {
      setDropAccessorialsSelected([]);
      setDropLimitedAccesssSelected({ id: "", name: "" });
      fields.DropFCFS = false;
    }
    if (
      orderTypeRowData?.name === "Expedited" ||
      orderTypeRowData?.name === "Final Mile" ||
      orderTypeRowData?.name === "LTL" ||
      orderTypeRowData?.name === "LTL + Final Mile" ||
      orderTypeRowData?.name === ""
    ) {
      getApiPackageData();
    }
  }, [orderTypeRowData?.name]);

  useEffect(() => {
    setMasterHubName("");
    setMasterHubAddress({});
  }, [getQuote]);

  /**
   * Adds a new reference object to the `moreReferences` array if the current
   * number of references is less than 4. Each new reference object will
   * have a `referenceCount` that increments based on the current length
   * of the `moreReferences` array and a default `referenceValue` set to an
   * empty string.
   *
   * The function uses optional chaining to safely access the length of
   * `moreReferences`. The `referenceCount` for the new reference is set
   * to one more than the current length of the array (plus one to adjust
   * for zero-based indexing).
   *
   * @function addMoreReferences
   * @returns {void} This function does not return a value. It updates the
   * state of `moreReferences` directly by calling `setMoreReferences`.
   */
  const addMoreReferences = () => {
    if (moreReferences?.length < 4) {
      let references = [...moreReferences];
      references.push({
        referenceCount: 1 + moreReferences?.length + 1,
        referenceValue: "",
      });
      setMoreReferences(references);
    }
  };

  /**
   * Removes a reference from the moreReferences array at the specified index.
   *
   * This function updates the state of moreReferences by removing the
   * reference at the given index, and it adjusts the referenceCount for
   * the remaining references to ensure they are sequentially numbered
   * starting from 2.
   *
   * @param index - The index of the reference to be removed from the moreReferences array.
   *
   * @returns {void} This function does not return a value. It updates the state of moreReferences.
   *
   * @throws {Error} Throws an error if moreReferences is undefined or not an array.
   */
  const removeReferences = (index: number) => {
    if (moreReferences?.length > 1) {
      let newRefArray: MoreReferencesType[] = [];
      let refIndex = 0;
      moreReferences.map((item, mIndex) => {
        if (index !== mIndex) {
          newRefArray.push({
            referenceCount: refIndex + 2,
            referenceValue: item.referenceValue,
          });
          refIndex++;
        }
      });
      setMoreReferences(newRefArray);
    }
  };

  /**
   * Handles changes to a reference field in a list of more references.
   *
   * This function updates the value of a specific reference based on the provided
   * index and data. It creates a new array of references with the updated value
   * while keeping the other references unchanged.
   *
   * @param {string} data - The new value to set for the reference at the specified index.
   * @param {number} index - The index of the reference to be updated.
   *
   * @returns {void} - This function does not return a value. It updates the state
   *                   of moreReferences using the setMoreReferences function.
   */
  const handleRefFieldChange = (data: string, index: number) => {
    let newRefArray: MoreReferencesType[] = [];
    moreReferences.map((item, mIndex) => {
      newRefArray.push({
        referenceCount: item.referenceCount,
        referenceValue: index === mIndex ? data : item.referenceValue,
      });
    });
    setMoreReferences(newRefArray);
  };

  /**
   * Handles changes in checkbox fields and updates the form values state.
   *
   * @param e - The checkbox change event.
   */
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = e.target as HTMLInputElement;
    if (!notLTL) {
      setFields((prevData: any) => ({ ...prevData, [id]: !prevData[id] }));
    } else {
      if (checked) {
        // Mutually exclusive: selecting one removes the other
        setFields((prevValues) => ({
          ...prevValues,
          additionalServices: [id],
        }));
      } else {
        // Uncheck: remove this option
        setFields((prevValues) => ({
          ...prevValues,
          additionalServices: prevValues.additionalServices.filter(
            (x) => x !== id
          ),
        }));
      }
    }
  };


  const handleSaveQuotation = async (quoteId: string | string[]) => {
    try {

      setLoading(true);
      const apiPayload = {
        // Final Mile saves every agent and service as one group and sends the full id
        // list; LTL + Final Mile still sends a single id and keeps the original payload.
        ...(Array.isArray(quoteId)
          ? { quoteIds: quoteId }
          : { quoteId: quoteId }),
        items: mapItemsDataToPayload(data),
        destinationAddress: {
          address: fields.destinationAddress,
          aptSuit: fields.destinationAPTSuite,
          zipCode: fields.destinationZipcode,
          city: fields.destinationCity,
          state: fields.destinationState,
          country: fields.destinationCountry,
          businessLocation: fields.destinationBusinessLocation,
          contactName: fields.destinationConsigneeName,
          phoneNumber: fields.destinationConsigneePhoneNumber.replace(
            /\D/g,
            ""
          ),
          countryCallingCode: fields.destinationConsigneePhoneExt,
          contactEmail: fields.destinationConsigneeContactEmail,
          extraMen: fields.destinationExtramen,
          waitTime: fields.destinationWaitTimeMinutes,
          stairsNumber: fields.destinationNoOfFlights,
          storage: fields.destinationStorage,
          handlingUnit: fields.destinationHandingUnits,
          weight: fields.destinationWeight,
          pieces: fields.destinationPieces,
          specialInstructions: fields.destinationSpecialInstructions,
        },
        originAddress: null,
        orderTypeId: orderTypeRowData?.id,
        reference: fields.orderReference,
        reference2: moreReferences?.[0]?.["referenceValue"] ?? "",
        reference3: moreReferences?.[1]?.["referenceValue"] ?? "",
        reference4: moreReferences?.[2]?.["referenceValue"] ?? "",
        reference5: moreReferences?.[3]?.["referenceValue"] ?? "",
        additionalServices: fields.additionalServices,
      };
      const apiResponse = await postApi(SAVE_QUOTATION, apiPayload);
      if (apiResponse.success) {
        setMessage(ORDER_MANAGEMENT_MESSAGES.QUOTE_SAVE_SUCCESS, "success");
        navigate("/saved-quotes");
      } else {
        setMessage(
          apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Add (or reuse) this constant near your other endpoint constants
  const SAVE_QUOTATION_FOR_LTL = "/order/order-quote/savedquotationforltl";

  // ---- Optional small helpers (co-locate with your other utils/helpers) ----
  const asNum = (v: any, fallback = 0) => {
    const n = typeof v === "string" ? Number(v) : v;
    return Number.isFinite(n) ? n : fallback;
  };
  const asStr = (v: any, fallback = "") =>
    (typeof v === "string" ? v : v?.toString?.() || "") || fallback;
  const onlyDigits = (s?: string) => asStr(s).replace(/\D/g, "");

  // If you already have mapItemsDataToPayload, keep it; else use this LTL-specific mapper:
  const mapItemsDataToPayloadForLTL = (items: any[]) =>
    (items || []).map((it) => ({
      description: asStr(it.description),
      handlingUnit: asStr(it.handlingUnit ?? it.handlingUnitAmt ?? it.hu),
      quantity: asStr(it.quantity ?? it.qty),
      weight: asStr(it.weight),
      weightUnit: asStr(it.weightUnit ?? "lbs"),
      length: asStr(it.length),
      width: asStr(it.width),
      height: asStr(it.height),
      itemClass: asStr(it.itemClass),
      comments: asStr(it.comments),
      nmfc: asStr(it.nmfc),
      skuNumber: asStr(it.skuNumber),
      packageType: asStr(it.packageType ?? it.package_type ?? it.package),
      package: Array.isArray(it.package)
        ? it.package.map((x: any) => ({ ...x, id: x.descriptionId || "" }))
        : [],
      stackable: Boolean(it.stackable),
      linearFeetStackable: asNum(it.linearFeetStackable, 0),
      linearFeet: asNum(it.linearFeet),
      density: asNum(it.density),
      cbm: asNum(it.cbm),
      hazardMaterial: it.hazardMaterial ?? {}, // keep structure if present
    }));

  const buildQuoteArray = () => {
    // Persist exactly what the quote table shows: with "LTL Only" checked the
    // saved quote keeps only the LTL offers, otherwise every offer is saved.
    const quotesToSave = filterLtlOnlyQuotes(quoteData, isLtlOnly);
    if (quotesToSave.length === 0) return [];

    // De-duplicate by the offer's whole identity — see dedupeKey below.
    const seen = new Set<string>();


    // return quoteData
    //   .map((q: any) => {
    //     const offered =
    //       q?.quoteFrom === "worldwide"
    //         ? Array.isArray(q.offeredProductList)
    //           ? q?.offeredProductList?.[0]
    //           : null
    //         : null;

    //     // Prefer the most customer-facing total; fall back sensibly
    //     const total = asNum(
    //       q.orderTypeName === 'LTL + Final Mile'
    //         ?
    //         q.totalOfferPriceWithMarkupAndFinalMile ??
    //         offered?.offerPrice?.value ??
    //         q.totalOfferPrice?.value ??
    //         0
    //         :
    //         q.totalPrice ??
    //         offered?.offerPrice?.value ??
    //         q.totalOfferPrice?.value ??
    //         0
    //     );

    //     const transitDays = asNum(
    //       q?.quoteFrom === "banyan"
    //         ? q?.transitTime
    //         : q?.quoteFrom === "worldwide"
    //           ? offered?.shopRQShipment?.timeInTransit?.transitDays
    //           : q?.transitDays,
    //       0
    //     );

    //     const carrierName = asStr(
    //       q?.quoteFrom === "banyan"
    //         ? q?.carrierName
    //         : q?.quoteFrom === "worldwide"
    //           ? q.primaryVendor?.preferredName
    //           : q?.hub?.agent?.name
    //     );

    //     const carrierCode = asStr(
    //       q?.quoteFrom === "banyan"
    //         ? q?.scac
    //         : q?.quoteFrom === "worldwide"
    //           ? q.primaryVendor?.scac
    //           : q?.hub?.agent?.carrierCode,
    //       ""
    //     );

    //     const shipmentOfferId = asStr(
    //       q?.quoteFrom === "banyan"
    //         ? q?.quoteId
    //         : q?.quoteFrom === "worldwide"
    //           ? q.offerId
    //           : q?.services?.[0]?.quoteId
    //     ); // ← becomes shipmentOfferId in payload
    //     const productTransactionId = asStr(
    //       q?.quoteFrom === "banyan"
    //         ? q?.quoteId
    //         : q?.quoteFrom === "worldwide"
    //           ? q?.productTransactionId
    //           : q?.services?.[0]?.quoteId
    //     );
    //     // Top-level order/service from your quoteData (preferred) with safe fallbacks
    //     const orderTypeId = asStr(q.orderId || orderTypeRowData?.id || "");
    //     const serviceId = asStr(q.serviceId || fields?.selectedServiceId || "");
    //     return {
    //       orderTypeId,
    //       serviceId,
    //       shipmentOfferId,
    //       productTransactionId,
    //       pricing: {
    //         total,
    //         carrierName,
    //         carrierCode,
    //         estimatedLTLTransitDay: transitDays,
    //         finalmileCarrierName: q?.finalMileData?.hub?.agent?.name || "", // LTL only; intentionally blank
    //       },
    //     };
    //   })
    return quotesToSave
      .map((q: any, idx: number) => {
        try {
          const offered =
            q?.quoteFrom === "worldwide"
              ? (Array.isArray(q?.offeredProductList)
                ? q?.offeredProductList?.[0]
                : null)
              : null;

          const total = asNum(
            q?.orderTypeName === "LTL + Final Mile"
              ? q?.totalOfferPriceWithMarkupAndFinalMile ??
              offered?.offerPrice?.value ??
              q?.totalOfferPrice?.value ??
              0
              : q?.totalPrice ??
              offered?.offerPrice?.value ??
              q?.totalOfferPrice?.value ??
              0
          );

          // Alliance carries its own id pair and spells transit days `TransitDays`,
          // and matches none of the branches below, so without these it falls to the
          // internal-LTL defaults: a blank shipmentOfferId drops the whole quote at
          // the guard further down, and carrier name/code save empty.
          const isAlliance = q?.quoteFrom === "alliance";

          const transitDays = asNum(
            q?.quoteFrom === "banyan"
              ? q?.transitTime
              : q?.quoteFrom === "worldwide"
                ? offered?.shopRQShipment?.timeInTransit?.transitDays
                : isAlliance
                  ? q?.TransitDays
                  : q?.transitDays,
            0
          );

          const carrierName = asStr(
            (q?.quoteFrom === "banyan" || q?.quoteFrom === "providence" || q?.quoteFrom === "armstrong" || isAlliance)
              ? q?.carrierName
              : q?.quoteFrom === "worldwide"
                ? q?.primaryVendor?.preferredName
                : q?.hub?.agent?.name
          );

          const carrierCode = asStr(
            q?.quoteFrom === "banyan"
              ? q?.scac
              : q?.quoteFrom === "worldwide"
                ? q?.primaryVendor?.scac
                : q?.quoteFrom === "providence"
                  ? q?.scac
                  : q?.quoteFrom === "armstrong" || isAlliance
                    ? q?.scac
                    : q?.hub?.agent?.carrierCode,
            ""
          );

          const shipmentOfferId = asStr(
            q?.quoteFrom === "banyan"
              ? q?.quoteId
              : q?.quoteFrom === "worldwide"
                ? q?.offerId
                : q?.quoteFrom === "providence"
                  ? q?.quoteDetailId
                  : q?.quoteFrom === "armstrong"
                    ? q?.rateRequestLocator
                    : isAlliance
                      ? q?.shipmentOfferId
                      : q?.services?.[0]?.quoteId
          );

          const productTransactionId = asStr(
            q?.quoteFrom === "banyan"
              ? q?.quoteId
              : q?.quoteFrom === "worldwide"
                ? q?.productTransactionId
                : q?.quoteFrom === "providence"
                  ? q?.quoteDetailId
                  : q?.quoteFrom === "armstrong"
                    ? q?.carrierQuoteId
                    : isAlliance
                      ? q?.shipmentProductTransactionId
                      : q?.services?.[0]?.quoteId
          );

          const orderTypeId = asStr(q?.orderId || orderTypeRowData?.id || "");
          const serviceId = asStr(q?.serviceId || fields?.selectedServiceId || "");

          if (!shipmentOfferId || !orderTypeId || !serviceId) {
            return null;
          }

          // Dedupe on the offer's whole identity instead of a single id. The
          // quote table renders one row per offer and never drops any, so
          // keying on one id silently lost rows the user could see. Armstrong
          // is the reason serviceLevelType is in the key: it returns several
          // service levels under ONE carrierQuoteId (e.g. TFORCE STND and
          // GTD_PM), so that id alone collapsed them into a single saved quote.
          // rateRequestLocator is shared even more widely — it is one per rate
          // request — and an offer whose own id comes back blank used to be
          // dropped outright.
          const dedupeKey = [
            q?.quoteFrom,
            shipmentOfferId,
            productTransactionId,
            q?.serviceLevelType,
            carrierName,
            total,
          ].join("|");
          if (seen.has(dedupeKey)) return null;
          seen.add(dedupeKey);

          return {
            orderTypeId,
            serviceId,
            shipmentOfferId,
            productTransactionId,
            pricing: {
              total,
              carrierName,
              carrierCode,
              estimatedLTLTransitDay: transitDays,
              finalmileCarrierName:
                q?.finalMileData?.hub?.agent?.name || "",
            },
          };
        } catch (err) {
          console.error("Error building quote entry at index", idx, q, err);
          return null;
        }
      })
      .filter(Boolean);
  };
  const handleSaveQuotationForLTL = async () => {
    try {

      setLoading(true);
      ;
      const customerId = assCustomerRowData?.id
        ? assCustomerRowData?.id
        : customerRowData?.id; //asStr(customerRowData?.id) || "";
      const orderTypeId = asStr(orderTypeRowData?.id, "");
      const items = mapItemsDataToPayload(data);
      const destinationAddress = {
        address: asStr(fields.destinationAddress),
        aptSuit: asStr(fields.destinationAPTSuite),
        zipCode: asStr(fields.destinationZipcode),
        city: asStr(fields.destinationCity),
        state: asStr(fields.destinationState),
        country: asStr(fields.destinationCountry),
        businessLocation: asStr(fields.destinationBusinessLocation),
        contactName: asStr(fields.destinationConsigneeName),
        phoneNumber: onlyDigits(fields.destinationConsigneePhoneNumber),
        countryCallingCode: asStr(fields.destinationConsigneePhoneExt, "+1"),
        contactEmail: asStr(fields.destinationConsigneeContactEmail),
        extraMen: asStr(fields.destinationExtramen),
        waitTime: asStr(fields.destinationWaitTimeMinutes),
        stairsNumber: asStr(fields.destinationNoOfFlights),
        storage: asStr(fields.destinationStorage),
        handlingUnit: asNum(fields.destinationHandingUnits),
        weight: asNum(fields.destinationWeight),
        pieces: asNum(fields.destinationPieces),
        accessorials: dropAccessorialsSelected,
        locationType: dropLimitedAccessSelected,
      };

      const originAddress = {
        address: asStr(fields.OriginAddress),
        aptSuit: asStr(fields.OriginAPTSuite),
        zipCode: asStr(fields.OriginZipcode),
        city: asStr(fields.originCity),
        state: asStr(fields.OriginState),
        country: asStr(fields.OriginCountry),
        businessLocation: asStr(fields.OriginBusinessLocation),
        contactName: asStr(fields.OriginConsigneeName),
        phoneNumber: onlyDigits(fields.OriginConsigneePhoneNumber),
        countryCallingCode: asStr(fields.OriginConsigneePhoneExt, "+1"),
        contactEmail: asStr(fields.OriginConsigneeContactEmail),
        accessorials: pickupAccessorialsSelected,
        locationType: pickupLimitedAccessSelected,
      };
      const quoteArray = buildQuoteArray();
      const pickUpDock = {
        dockName: asStr(fields.PickUpDockName),
        pickupNumber: asStr(fields.PickUpDOckNumber),
        pickUpDate: getISODate(fields.PickUpDockDate || "", fields.PickUpDockTime || ""),
        pickUpTime: asStr(fields.PickUpDockTime),
        note: asStr(fields.PickUpDOckNote),
        dockOpenTime: fields.PickUpDockOpenTime ? convertTo24HourFormat(fields.PickUpDockOpenTime) : "",
        dockCloseTime: fields.PickUpDOckCloseTime ? convertTo24HourFormat(fields.PickUpDOckCloseTime) : "",
        firstComeFirstServe: false,
      };

      const dropDock = {
        dockName: asStr(fields.DropDockName),
        pickupNumber: asStr(fields.DropDOckNumber),
        note: asStr(fields.DropDOckNote),
        dropOpenTime: dropTimeForPayload(fields.DropDockOpenTime),
        dropCloseTime: dropTimeForPayload(fields.DropDOckCloseTime),
        firstComeFirstServe: false,
      };

      // Build final payload (mirror the sample 1:1 on keys)
      const payload = {
        groupId: asStr(fields.selectedQuoteId),
        // serviceId intentionally omitted (commented in sample)
        customerId,
        totalWeight:
          asNum(fields.destinationWeight) ||
          asNum(fields.destinationWeight) ||
          items.reduce((sum: number, it: any) => sum + asNum(it.weight), 0),
        distanceToDestination: asStr(fields.distanceToDestination),
        orderTypeId,
        isLtlOnly: isLtlOnly,
        reference: asStr(fields.orderReference),
        reference2: asStr(moreReferences?.[0]?.referenceValue),
        reference3: asStr(moreReferences?.[1]?.referenceValue),
        reference4: asStr(moreReferences?.[2]?.referenceValue),
        reference5: asStr(moreReferences?.[3]?.referenceValue),
        items,
        destinationAddress,
        originAddress,
        quoteArray,
        pickUpDock,
        dropDock,
        additionalServices: Array.isArray(fields.additionalServices)
          ? fields.additionalServices
          : [],
      };
      const apiResponse = await postApi(SAVE_QUOTATION_FOR_LTL, payload);
      if (apiResponse?.success) {
        setMessage(ORDER_MANAGEMENT_MESSAGES.QUOTE_SAVE_SUCCESS, "success");
        navigate("/saved-quotes");
      } else {
        setMessage(
          apiResponse?.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (err) {
      setMessage(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };

  const getLTLQuoteDetails = () => {
    let newArr: any = [];
    TLList.map((item: any) => {
      newArr.push({
        shipmentOfferId: item?.offerId,
        productTransactionId: item?.productTransactionId,
        pricing: {
          total: orderTypeRowData?.name === "LTL"
            ? item?.priceWithMarkUp || item?.price || "0.0"
            : item?.priceWithFinalMile !== "0" ? String(item?.priceWithFinalMile) : item?.price ?? "0.0",
          carrierName: item?.carrierName,
          carrierCode: item?.scac,
          estimatedLTLTransitDay: item?.transitTime,
          finalmileCarrierName: item?.finalmileCarrierName,
        },
      });
    });
    return newArr;
  };

  const handleSaveLTLQuotation = async (quoteId: string) => {
    try {

      setLoading(true);
      ;
      const apiPayload = {
        groupId: groupQuoteId,
        serviceId: serviceSelected?.id,
        customerId: assCustomerRowData?.id
          ? assCustomerRowData?.id
          : customerRowData?.id,
        totalWeight: fields?.destinationWeight,
        distanceToDestination: fields.distance,
        orderTypeId: orderTypeRowData?.id,
        reference: fields.orderReference,
        reference2: moreReferences?.[0]?.["referenceValue"] ?? "",
        reference3: moreReferences?.[1]?.["referenceValue"] ?? "",
        reference4: moreReferences?.[2]?.["referenceValue"] ?? "",
        reference5: moreReferences?.[3]?.["referenceValue"] ?? "",
        items: mapItemsDataToPayload(data),
        destinationAddress: {
          address: fields.destinationAddress,
          aptSuit: fields.destinationAPTSuite,
          zipCode: fields.destinationZipcode,
          city: fields.destinationCity,
          state: fields.destinationState,
          country: fields.destinationCountry,
          businessLocation: fields.destinationBusinessLocation,
          contactName: fields.destinationConsigneeName,
          phoneNumber: fields.destinationConsigneePhoneNumber.replace(
            /\D/g,
            ""
          ),
          countryCallingCode: fields.destinationConsigneePhoneExt,
          contactEmail: fields.destinationConsigneeContactEmail,
          extraMen: fields.destinationExtramen,
          waitTime: fields.destinationWaitTimeMinutes,
          stairsNumber: fields.destinationNoOfFlights,
          storage: fields.destinationStorage,
          handlingUnit: fields.destinationHandingUnits,
          weight: fields.destinationWeight,
          pieces: fields.destinationPieces,
          accessorials: mapAccessorials(dropAccessorialsSelected) || [],
          locationType: dropLimitedAccessSelected?.name || "",
        },
        originAddress: {
          address: fields.OriginAddress,
          aptSuit: fields.OriginAPTSuite,
          zipCode: fields.OriginZipcode,
          city: fields.OriginCity,
          state: fields.OriginState,
          country: fields.OriginCountry,
          businessLocation: fields.OriginBusinessLocation,
          contactName: fields.OriginConsigneeName,
          phoneNumber: fields.OriginConsigneePhoneNumber,
          countryCallingCode: fields.OriginConsigneePhoneExt,
          contactEmail: fields.OriginConsigneeContactEmail,
          accessorials: mapAccessorials(pickupAccessorialsSelected),
          locationType: pickupLimitedAccessSelected?.name || "",
        },
        quoteArray: getLTLQuoteDetails(),
        pickUpDock: {
          dockName: fields.PickUpDockName || "",
          pickupNumber: fields.PickUpDOckNumber || "",
          pickUpDate: getISODate(fields.PickUpDockDate || "", fields.PickUpDockTime || ""),
          pickUpTime: fields.PickUpDockTime || "",
          note: fields.PickUpDOckNote || "",
          dockOpenTime: fields.PickUpDockOpenTime ? convertTo24HourFormat(fields.PickUpDockOpenTime) : "",
          dockCloseTime: fields.PickUpDOckCloseTime ? convertTo24HourFormat(fields.PickUpDOckCloseTime) : "",
          firstComeFirstServe: fields.PickUpFCFS,
        },
        dropDock: {
          dockName: fields.DropDockName || "",
          pickupNumber: fields.DropDOckNumber || "",
          note: fields.DropDOckNote || "",
          dropOpenTime: dropTimeForPayload(fields.DropDockOpenTime),
          dropCloseTime: dropTimeForPayload(fields.DropDOckCloseTime),
          firstComeFirstServe: fields.DropFCFS,
        },
        additionalServices: fields.additionalServices,
      };
      const apiResponse = await postApi(SAVE_LTL_QUOTATION, apiPayload);
      if (apiResponse.success) {
        setMessage(ORDER_MANAGEMENT_MESSAGES.QUOTE_SAVE_SUCCESS, "success");
        navigate("/saved-quotes");
      } else {
        setMessage(
          apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = (data: any) => {
    let totalHandlingUnits = 0;
    let totalWeight = 0;
    let totalPieces = 0;
    let totalFeet = 0;
    let totalDensity = 0;
    let totalCBM = 0;

    data?.forEach((item: any) => {
      const handlingUnits = parseFloat(item?.handlingUnit) || 0;
      const quantity = parseFloat(item?.quantity) || 0;
      const weight = parseFloat(item?.weight) || 0;
      const linearFeet = parseFloat(item?.linearFeet) || 0;
      const itemWeight = quantity * weight;
      const itemDensity = parseFloat(item?.density) || 0;
      const itemcbm = parseFloat(item?.cbm) || 0;

      totalHandlingUnits += handlingUnits;
      totalWeight += itemWeight;
      totalPieces += quantity;
      totalFeet += linearFeet;
      totalDensity += itemDensity;
      totalCBM += itemcbm;
    });

    return {
      handlingUnits: totalHandlingUnits,
      weight: totalWeight,
      pieces: totalPieces,
      totalFeet,
      totalDensity,
      totalCBM,
    };
  };

  useEffect(() => {
    if (saveQuotationPage) {
      setTimeout(() => {
        setSaveQuotationPage(false);
      }, 3000);
    }
  }, [saveQuotationPage]);
  const dataToTransfer = {
    quoteId: "",
    items: mapItemsDataToPayload(data),
    destinationAddress: {
      address: fields.destinationAddress,
      aptSuit: fields.destinationAPTSuite,
      zipCode: fields.destinationZipcode,
      city: fields.destinationCity,
      state: fields.destinationState,
      country: fields.destinationCountry,
      businessLocation: fields.destinationBusinessLocation,
      contactName: fields.destinationConsigneeName,
      phoneNumber: fields.destinationConsigneePhoneNumber.replace(/\D/g, ""),
      countryCallingCode: fields.destinationConsigneePhoneExt,
      contactEmail: fields.destinationConsigneeContactEmail,
      extraMen: fields.destinationExtramen,
      waitTime: fields.destinationWaitTimeMinutes,
      stairsNumber: fields.destinationNoOfFlights,
      storage: fields.destinationStorage,
      handlingUnit: fields.destinationHandingUnits,
      weight: fields.destinationWeight,
      pieces: fields.destinationPieces,
      specialInstructions: fields.destinationSpecialInstructions,
    },
    originAddress: null,
    customer: customerRowData,
    associatedCustomer: assCustomerRowData,
    service: serviceSelected,
    // Final Mile Service Type travels to Add Order (the selected row's service carries no logisticsType).
    serviceType: isFinalMileOrderType ? serviceType : LOGISTICS_TYPE.DELIVERY,
    orderType: orderTypeRowData,
    reference: fields.orderReference,
    reference2: moreReferences?.[0]?.["referenceValue"] ?? "",
    reference3: moreReferences?.[1]?.["referenceValue"] ?? "",
    reference4: moreReferences?.[2]?.["referenceValue"] ?? "",
    reference5: moreReferences?.[3]?.["referenceValue"] ?? "",
    additionalServices: fields.additionalServices,
  };

  const ltlDataToTransfer = {
    quoteId: "",
    items: mapItemsDataToPayload(data),
    destinationAddress: {
      address: fields.destinationAddress,
      aptSuit: fields.destinationAPTSuite,
      zipCode: fields.destinationZipcode,
      city: fields.destinationCity,
      state: fields.destinationState,
      country: fields.destinationCountry,
      businessLocation: fields.destinationBusinessLocation,
      contactName: fields.destinationConsigneeName,
      phoneNumber: fields.destinationConsigneePhoneNumber.replace(/\D/g, ""),
      countryCallingCode: fields.destinationConsigneePhoneExt,
      contactEmail: fields.destinationConsigneeContactEmail,
      extraMen: fields.destinationExtramen,
      waitTime: fields.destinationWaitTimeMinutes,
      stairsNumber: fields.destinationNoOfFlights,
      storage: fields.destinationStorage,
      handlingUnit: fields.destinationHandingUnits,
      weight: fields.destinationWeight,
      pieces: fields.destinationPieces,
      specialInstructions: fields.destinationSpecialInstructions,
    },
    originAddress: {
      address: fields.OriginAddress,
      aptSuit: fields.OriginAPTSuite,
      zipCode: fields.OriginZipcode,
      city: fields.OriginCity,
      state: fields.OriginState,
      country: fields.OriginCountry,
      businessLocation: fields.OriginBusinessLocation,
      contactName: fields.OriginConsigneeName,
      phoneNumber: fields.OriginConsigneePhoneNumber,
      countryCallingCode: fields.OriginConsigneePhoneExt,
      contactEmail: fields.OriginConsigneeContactEmail,
      accessorials: mapAccessorials(pickupAccessorialsSelected),
      locationType: pickupLimitedAccessSelected?.name || "",
    },

    customer: customerRowData,
    associatedCustomer: assCustomerRowData,
    service: serviceSelected,
    orderType: orderTypeRowData,
    reference: fields.orderReference,
    reference2: moreReferences?.[0]?.["referenceValue"] ?? "",
    reference3: moreReferences?.[1]?.["referenceValue"] ?? "",
    reference4: moreReferences?.[2]?.["referenceValue"] ?? "",
    reference5: moreReferences?.[3]?.["referenceValue"] ?? "",
    additionalServices: fields.additionalServices,
    distanceToDestination: fields.distance,
    totalWeight: fields.destinationWeight,

    pickUpDock: {
      dockName: fields.PickUpDockName || "",
      pickupNumber: fields.PickUpDOckNumber || "",
      pickUpDate: getISODate(fields.PickUpDockDate || "", fields.PickUpDockTime || ""),
      pickUpTime: fields.PickUpDockTime || "",
      note: fields.PickUpDOckNote || "",
      dockOpenTime: fields.PickUpDockOpenTime ? convertTo24HourFormat(fields.PickUpDockOpenTime) : "",
      dockCloseTime: fields.PickUpDOckCloseTime ? convertTo24HourFormat(fields.PickUpDOckCloseTime) : "",
      firstComeFirstServe: fields.PickUpFCFS,
    },

    dropDock: {
      dockName: fields.DropDockName || "",
      pickupNumber: fields.DropDOckNumber || "",
      note: fields.DropDOckNote || "",
      dropOpenTime: dropTimeForPayload(fields.DropDockOpenTime),
      dropCloseTime: dropTimeForPayload(fields.DropDOckCloseTime),
      firstComeFirstServe: fields.DropFCFS,
    },
  };

  useEffect(() => {
    if (quoteType === "traditional") {
      setNotLTL(false);
      setOrderTypeRowData({
        id: ltlOrderType?.id,
        name: "LTL",
        description: "Less than Truck Load",
      });
    }
    if (quoteType === "nonTraditional") {
      setOrderTypeRowData({
        id: "",
        name: "",
        description: "",
      });
    }
  }, [quoteType]);

  useEffect(() => {
    setLtlAccessorialData({
      dropAccessorialsSelected: dropAccessorialsSelected,
      dropLimitedAccessSelected: dropLimitedAccessSelected,
      pickupLimitedAccessSelected: pickupLimitedAccessSelected,
    });
  }, [
    dropAccessorialsSelected,
    dropLimitedAccessSelected,
    pickupLimitedAccessSelected,
  ]);

  return (
    <>
      {loading && <Loader />}
      <div className="details" ref={containerRef}>
        <div className="form form--innersec">
          <section className="form__section pt_16">
            <h1 className="form__section__title form__section__title--flex">
              New Quote
              <div className="btn__group">
                <button
                  className="btn btn__primary"
                  onClick={() => {
                    navigate("/saved-quotes");
                  }}
                >
                  View Saved Quotes
                </button>
              </div>
            </h1>
            {!isUserOfOmniMove && customerLevel === "first" ? (
              <>
                <div className="formRow">
                  <div className="formCol">
                    <div className="form__Field">
                      <div className="form__radio form__radio--gray">
                        <div className="form__radio__option">
                          <input
                            type="radio"
                            id="traditionalOrder"
                            name="orderType"
                            value="traditional"
                            className="form__radio__input"
                            checked={quoteType === "traditional"}
                            onChange={() => setQuoteType("traditional")}
                          />
                          <label
                            htmlFor="traditionalOrder"
                            className="form__radio__label"
                          >
                            LTL Quote
                          </label>
                        </div>
                        <div className="form__radio__option">
                          <input
                            type="radio"
                            name="orderType"
                            id="nonTraditional"
                            value="nonTraditional"
                            className="form__radio__input"
                            checked={quoteType === "nonTraditional"}
                            onChange={() => setQuoteType("nonTraditional")}
                          />
                          <label
                            htmlFor="ChargeOnDelivery"
                            className="form__radio__label"
                          >
                            Final Mile, LTL + Final Mile Quote
                          </label>
                        </div>
                      </div>
                    </div>
                    {/* <label className="radio__group">
                         
                          <span>Traditional Order</span>
                        </label>
                        <label className="radio__group">
                          <input
                            type="radio"
                            name="orderType"
                            value="nonTraditional"
                            checked={quoteType === "nonTraditional"}
                            onChange={() => setQuoteType("nonTraditional")}
                          />
                          <span>Non Traditional Order</span>
                        </label> */}
                  </div>
                </div>
                <div className="formRow formRow__2">
                  <div className="formCol">
                    <MutliDropdown
                      columns={customerOrderColumns}
                      data={customerNameDropdown}
                      setData={setCustomerNameDropdown}
                      captionTitles="Select Customer"
                      rowData={customerRowData}
                      setRowData={(row: any) => {
                        setCustomerRowData(row);
                        clearMandatory(DROPDOWN_FIELD_KEYS.customer);
                      }}
                      fieldKey={DROPDOWN_FIELD_KEYS.customer}
                      error={errors[DROPDOWN_FIELD_KEYS.customer]}
                      displayColumn="name"
                      apiUrl={"/customer"}
                      labelTitle="Customer Name"
                      mandatory={true}
                      enableRemove={true}
                      type={"order"}
                      status={3}
                      extendedUrl={{
                        requestFrom: "new",
                      }}
                    />
                  </div>
                  <div className="formCol">
                    <MutliDropdown
                      columns={customerOrderColumns}
                      data={assCustomerNameDropdown}
                      setData={setAssCustomerNameDropdown}
                      captionTitles="Select Sub Customer"
                      rowData={
                        customerRowData?.customerLevel !== "second"
                          ? ""
                          : assCustomerRowData
                      }
                      setRowData={setAssCustomerRowData}
                      displayColumn="name"
                      enableRemove={true}
                      apiUrl={parentCustomerId ? "/customer" : ""}
                      labelTitle="Sub Customer Name"
                      mandatory={associatedCusReqired}
                      parentCustomerId={parentCustomerId}
                      disable={customerRowData?.customerLevel !== "second"}
                      extendedUrl={{
                        requestFrom: "new",
                      }}
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="formRow">
                  <div className="formCol">
                    <div className="form__Field">
                      <div className="form__radio form__radio--gray">
                        <div className="form__radio__option">
                          <input
                            type="radio"
                            id="traditionalOrder"
                            name="orderType"
                            value="traditional"
                            className="form__radio__input"
                            checked={quoteType === "traditional"}
                            onChange={() => setQuoteType("traditional")}
                          />
                          <label
                            htmlFor="traditionalOrder"
                            className="form__radio__label"
                          >
                            LTL Quote
                          </label>
                        </div>
                        <div className="form__radio__option">
                          <input
                            type="radio"
                            name="orderType"
                            id="nonTraditional"
                            value="nonTraditional"
                            className="form__radio__input"
                            checked={quoteType === "nonTraditional"}
                            onChange={() => setQuoteType("nonTraditional")}
                          />
                          <label
                            htmlFor="ChargeOnDelivery"
                            className="form__radio__label"
                          >
                            Final Mile, LTL + Final Mile Quote
                          </label>
                        </div>
                      </div>
                    </div>
                    {/* <label className="radio__group">
                         
                          <span>Traditional Order</span>
                        </label>
                        <label className="radio__group">
                          <input
                            type="radio"
                            name="orderType"
                            value="nonTraditional"
                            checked={quoteType === "nonTraditional"}
                            onChange={() => setQuoteType("nonTraditional")}
                          />
                          <span>Non Traditional Order</span>
                        </label> */}
                  </div>
                </div>
                <div className="formRow formRow__2">
                  <div className="formCol">
                    <MutliDropdown
                      columns={customerOrderColumns}
                      data={customerNameDropdown}
                      setData={setCustomerNameDropdown}
                      captionTitles="Select Customer"
                      rowData={customerRowData}
                      setRowData={(row: any) => {
                        setCustomerRowData(row);
                        clearMandatory(DROPDOWN_FIELD_KEYS.customer);
                      }}
                      fieldKey={DROPDOWN_FIELD_KEYS.customer}
                      error={errors[DROPDOWN_FIELD_KEYS.customer]}
                      displayColumn="name"
                      enableRemove={true}
                      apiUrl={"/customer"}
                      labelTitle="Customer Name"
                      mandatory={true}
                      type={"order"}
                      extendedUrl={{
                        requestFrom: "new",
                      }}
                    />
                  </div>
                </div>
              </>
            )}
            {quoteType === "nonTraditional" && (
              <div className="formRow formRow__2">
                <div className="formCol">
                  <MutliDropdown
                    mandatory={true}
                    columns={orderTypeColumns}
                    data={orderTypeDropdown?.filter(
                      (item) => item.name !== "Expedited"
                    )}
                    enableRemove={true}
                    setData={setOrderTypeDropdown}
                    captionTitles="Select Order Type"
                    rowData={orderTypeRowData}
                    setRowData={(row: any) => {
                      setOrderTypeRowData(row);
                      clearMandatory(DROPDOWN_FIELD_KEYS.orderType);
                    }}
                    fieldKey={DROPDOWN_FIELD_KEYS.orderType}
                    error={errors[DROPDOWN_FIELD_KEYS.orderType]}
                    displayColumn="name"
                    apiUrl={"/order/order-type"}
                    extendedUrl={{ from: "order" }}
                    labelTitle="Order Type"
                  />
                </div>
                {isFinalMileOrderType && (
                  <div className="formCol">
                    <SingleDropdown
                      fieldName="serviceType"
                      selectedItem={
                        FINAL_MILE_SERVICE_TYPE_OPTIONS.find(
                          (option) => option.id === serviceType,
                        ) ?? null
                      }
                      setSelectedData={handleServiceTypeChange}
                      staticData={FINAL_MILE_SERVICE_TYPE_OPTIONS}
                      labelTitle="Service Type"
                      placeholder="-Select-"
                      mandatory={true}
                    />
                  </div>
                )}
                <div className="formCol">
                  <div className="form__Field">
                    <label htmlFor="serviceLevel" className="form__label">
                      Service Level <span className="mandatory-mark">*</span>
                    </label>
                    {isFinalMileOrderType ? (
                      // Final Mile quotes several services at once; every other order
                      // type stays on the original single-select dropdown.
                      <MultiSelectChips
                        dropdownData={serviceList}
                        setDropdownData={setServiceList}
                        selectedData={serviceSelectedList}
                        setSelectedData={handleServiceSelectionChange}
                        keyBindName="name"
                        searchPlaceholder="Service Level"
                        extendedUrl={{
                          orderTypeId: orderTypeRowData?.id || "",
                          logisticsType: serviceType,
                        }}
                        apiUrl={`/agent/service/`}
                        fieldKey={DROPDOWN_FIELD_KEYS.service}
                        error={errors[DROPDOWN_FIELD_KEYS.service]}
                        // The field already has its own "Service Level *" label above;
                        // blanking this suppresses the component's built-in one.
                        labelTitle=""
                        disable={!orderTypeRowData?.id}
                      />
                    ) : (
                      <MutliDropdown
                        columns={serviceColumns}
                        data={serviceList}
                        setData={setServiceList}
                        captionTitles="Service Level"
                        rowData={serviceSelected}
                        setRowData={(row: any) => {
                          setServiceSelected(row);
                          clearMandatory(DROPDOWN_FIELD_KEYS.service);
                        }}
                        fieldKey={DROPDOWN_FIELD_KEYS.service}
                        error={errors[DROPDOWN_FIELD_KEYS.service]}
                        extendedUrl={{
                          orderTypeId: orderTypeRowData?.id || "",
                        }}
                        displayColumn="name"
                        apiUrl={`/agent/service/`}
                        type={"dropdown"}
                        mandatory={true}
                        disable={!orderTypeRowData?.id}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </section>
          {!notLTL ? (
            <div className="form__sectionGrid">
              <section className="form__section">
                <h1 className="form__section__title">Origin Address</h1>
                <div className="formRow formRow__2 ">
                  <div className="formCol">
                    <div className="formRow formRow__2 ">
                      <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="OriginZipcode"
                            className="form__label"
                          >
                            Zip Code <span className="mandatory-mark">*</span>
                          </label>
                          <input
                            type="text"
                            id="OriginZipcode"
                            data-field="OriginZipcode"
                            name="OriginZipcode"
                            className={`form__input form__input--sm${fieldErrorClass("OriginZipcode")}`}
                            placeholder="Zip Code"
                            autoComplete="off"
                            value={fields.OriginZipcode}
                            onChange={handleFieldChange}
                          />
                          {errors.OriginZipcode && (
                            <span className="form__error">
                              {errors.OriginZipcode}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form__Field">
                          <label htmlFor="OriginCity" className="form__label">
                            City
                          </label>
                          {originCities.length > 0 ? (
                            <SingleDropdown
                              fieldName="OriginCity"
                              selectedItem={fields.OriginCity ? { id: fields.OriginCity, name: fields.OriginCity } : null}
                              setSelectedData={(_, item) => setFields((prev) => ({ ...prev, OriginCity: item?.name || "" }))}
                              staticData={originCities.map((c) => ({ id: c, name: c }))}
                              placeholder="City"
                            />
                          ) : (
                            <input
                              type="text"
                              id="OriginCity"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="City"
                              autoComplete="off"
                              value={fields.OriginCity}
                              onChange={handleFieldChange}
                              disabled={true}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="formRow formRow__2">
                      <div className="formCol">
                        <div className="form__Field">
                          <label htmlFor="OriginState" className="form__label">
                            State
                          </label>
                          <input
                            type="text"
                            id="OriginState"
                            name="OriginState"
                            className="form__input form__input--sm form__input--disabled"
                            placeholder="State"
                            autoComplete="off"
                            value={fields.OriginState}
                            onChange={handleFieldChange}
                            disabled={true}
                          />
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="OriginCountry"
                            className="form__label"
                          >
                            Country
                          </label>
                          <input
                            type="text"
                            id="OriginCountry"
                            name="OriginCountry"
                            className="form__input form__input--sm form__input--disabled"
                            placeholder="Country"
                            autoComplete="off"
                            value={fields.OriginCountry}
                            onChange={handleFieldChange}
                            disabled={true}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {orderTypeRowData?.name === "Expedited" ? (
                  <>
                    <div className="formCol">
                      <div className="formRow formRow__4 mb_0">
                        <div className="formCol">
                          <div className="form__Field">
                            <label className="form__label">
                              Pickup Target Date From{" "}
                              <span className="mandatory-mark">*</span>
                            </label>
                            <input
                              type="date"
                              id="PickupTargetDateFrom"
                              name="PickupTargetDateFrom"
                              className="form__date form__date--sm"
                              placeholder="DD/MM/YYYY"
                              autoComplete="off"
                              onChange={handleFieldChange}
                              value={normalizeDate(fields.PickupTargetDateFrom)}
                              min={normalizeDate(new Date())}
                            />
                            {errors.PickupTargetDateFrom && (
                              <span className="form__error">
                                {errors.PickupTargetDateFrom}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label className="form__label">
                              Pickup Target Time From{" "}
                              <span className="mandatory-mark">*</span>
                            </label>
                            <CustomTimePicker
                              value={fields.PickupTargetTimeFrom}
                              onChange={handleTimeChange}
                              property="PickupTargetTimeFrom"
                            />
                            {errors.PickupTargetTimeFrom && (
                              <span className="form__error">
                                {errors.PickupTargetTimeFrom}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label className="form__label">
                              Pickup Target Date To{" "}
                              <span className="mandatory-mark">*</span>
                            </label>
                            <input
                              type="date"
                              id="PickupTargetDateTo"
                              name="PickupTargetDateTo"
                              className="form__date form__date--sm"
                              placeholder="DD/MM/YYYY"
                              autoComplete="off"
                              onChange={handleFieldChange}
                              value={normalizeDate(fields.PickupTargetDateTo)}
                              min={
                                fields.PickupTargetDateFrom
                                  ? normalizeDate(fields.PickupTargetDateFrom)
                                  : normalizeDate(new Date())
                              }
                            />
                            {errors.PickupTargetDateTo && (
                              <span className="form__error">
                                {errors.PickupTargetDateTo}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label className="form__label">
                              Pickup Target Time To{" "}
                              <span className="mandatory-mark">*</span>
                            </label>
                            <CustomTimePicker
                              value={fields.PickupTargetTimeTo}
                              onChange={handleTimeChange}
                              property="PickupTargetTimeTo"
                            />
                            {errors.PickupTargetTimeTo && (
                              <span className="form__error">
                                {errors.PickupTargetTimeTo}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="formRow formRow__4">
                        <div className="formCol">
                          <div className="form__Field">
                            <label className="form__label">
                              Pickup Delivery Date From{" "}
                              <span className="mandatory-mark">*</span>
                            </label>
                            <input
                              type="date"
                              id="PickupDeliveryDateFrom"
                              name="PickupDeliveryDateFrom"
                              className="form__date form__date--sm"
                              placeholder="DD/MM/YYYY"
                              autoComplete="off"
                              onChange={handleFieldChange}
                              value={normalizeDate(
                                fields.PickupDeliveryDateFrom
                              )}
                              min={
                                fields.PickupTargetDateTo
                                  ? normalizeDate(fields.PickupTargetDateTo)
                                  : fields.PickupTargetDateFrom
                                    ? normalizeDate(fields.PickupTargetDateFrom)
                                    : normalizeDate(new Date())
                              }
                            />
                            {errors.PickupDeliveryDateFrom && (
                              <span className="form__error">
                                {errors.PickupDeliveryDateFrom}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label className="form__label">
                              Pickup Delivery Time From{" "}
                              <span className="mandatory-mark">*</span>
                            </label>
                            <CustomTimePicker
                              value={fields.PickupDeliveryTimeFrom}
                              onChange={handleTimeChange}
                              property="PickupDeliveryTimeFrom"
                            />
                            {errors.PickupDeliveryTimeFrom && (
                              <span className="form__error">
                                {errors.PickupDeliveryTimeFrom}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label className="form__label">
                              Pickup Delivery Date To{" "}
                              <span className="mandatory-mark">*</span>
                            </label>
                            <input
                              type="date"
                              id="PickupDeliveryDateTo"
                              name="PickupDeliveryDateTo"
                              className="form__date form__date--sm"
                              placeholder="DD/MM/YYYY"
                              autoComplete="off"
                              onChange={handleFieldChange}
                              value={normalizeDate(fields.PickupDeliveryDateTo)}
                              min={
                                fields.PickupDeliveryDateFrom
                                  ? normalizeDate(fields.PickupDeliveryDateFrom)
                                  : fields.PickupTargetDateTo
                                    ? normalizeDate(fields.PickupTargetDateTo)
                                    : fields.PickupTargetDateFrom
                                      ? normalizeDate(fields.PickupTargetDateFrom)
                                      : normalizeDate(new Date())
                              }
                            />
                            {errors.PickupDeliveryDateTo && (
                              <span className="form__error">
                                {errors.PickupDeliveryDateTo}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label className="form__label">
                              Pickup Delivery Time To{" "}
                              <span className="mandatory-mark">*</span>
                            </label>
                            <CustomTimePicker
                              value={fields.PickupDeliveryTimeTo}
                              onChange={handleTimeChange}
                              property="PickupDeliveryTimeTo"
                            />
                            {errors.PickupDeliveryTimeTo && (
                              <span className="form__error">
                                {errors.PickupDeliveryTimeTo}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  ""
                )}
                {!notLTL && orderTypeRowData?.name !== "Expedited" && (
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <MultiSelectChips
                          apiUrl={"/order/ltl-accessorial/wwex"}
                          dropdownData={pickupAccessorials}
                          selectedData={pickupAccessorialsSelected}
                          setDropdownData={setPickupAccessorialDropdownData}
                          setSelectedData={setPickupAccessorialsSelected}
                          searchPlaceholder="Search"
                          extendedUrl={{ isPickup: "true" }}
                          labelTitle="LTL Pickup Accessorials"
                          keyBindName="name"
                          mandatory={false}
                        />
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <MutliDropdown
                          columns={accessorialsColumn}
                          data={pickupLimitedAccess}
                          setData={setPickupLimitedAccessDropdownData}
                          captionTitles="Select Limited Access Type"
                          rowData={pickupLimitedAccessSelected}
                          setRowData={setPickupLimitedAccesssSelected}
                          displayColumn="name"
                          apiUrl={"/order/ltl-accessorial/wwex"}
                          labelTitle="Limited Access Type"
                          extendedUrl={{
                            isPickup: "true",
                            isLimitAccess: "true",
                          }}
                          enableRemove={true}
                          mandatory={false}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </section>
              <section className="form__section">
                <h1 className="form__section__title">Destination Address</h1>

                <div className="formRow formRow__2 ">
                  <div className="formCol">
                    <div className="formRow formRow__2">
                      <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationZipcode"
                            className="form__label"
                          >
                            Zip Code <span className="mandatory-mark">*</span>
                          </label>
                          <input
                            type="text"
                            id="destinationZipcode"
                            data-field="destinationZipcode"
                            name="destinationZipcode"
                            className={`form__input form__input--sm${fieldErrorClass("destinationZipcode")}`}
                            placeholder="Zip Code"
                            autoComplete="off"
                            value={fields.destinationZipcode}
                            onChange={handleFieldChange}
                          />
                          {errors.destinationZipcode && (
                            <span className="form__error">
                              {errors.destinationZipcode}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationCity"
                            className="form__label"
                          >
                            City
                          </label>
                          {destinationCities.length > 0 ? (
                            <SingleDropdown
                              fieldName="destinationCity"
                              selectedItem={fields.destinationCity ? { id: fields.destinationCity, name: fields.destinationCity } : null}
                              setSelectedData={(_, item) => setFields((prev) => ({ ...prev, destinationCity: item?.name || "" }))}
                              staticData={destinationCities.map((c) => ({ id: c, name: c }))}
                              placeholder="City"
                            />
                          ) : (
                            <input
                              type="text"
                              id="destinationCity"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="City"
                              autoComplete="off"
                              value={fields.destinationCity}
                              onChange={handleFieldChange}
                              disabled={true}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="formRow formRow__2">
                      <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationState"
                            className="form__label"
                          >
                            State
                          </label>
                          <input
                            type="text"
                            id="destinationState"
                            name="destinationState"
                            className="form__input form__input--sm form__input--disabled"
                            placeholder="State"
                            autoComplete="off"
                            value={fields.destinationState}
                            onChange={handleFieldChange}
                            disabled={true}
                          />
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationCountry"
                            className="form__label"
                          >
                            Country
                          </label>
                          <input
                            type="text"
                            id="destinationCountry"
                            name="destinationCountry"
                            className="form__input form__input--sm form__input--disabled"
                            placeholder="Country"
                            autoComplete="off"
                            value={fields.destinationCountry}
                            onChange={handleFieldChange}
                            disabled={true}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="formCol">
                  <div className="formRow formRow__3">
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="destinationHandingUnits"
                          className="form__label"
                        >
                          Handling Units
                        </label>
                        <input
                          type="number"
                          id="destinationHandingUnits"
                          name="destinationHandingUnits"
                          className="form__input form__input--sm form__input--disabled"
                          placeholder="Handling Units"
                          autoComplete="off"
                          value={fields.destinationHandingUnits}
                          onChange={handleFieldChange}
                          disabled={true}
                          onKeyDown={handleKeyDown}
                        />
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="destinationWeight"
                          className="form__label"
                        >
                          Total Weight(LBS)
                        </label>
                        <input
                          type="number"
                          id="destinationWeight"
                          name="destinationWeight"
                          className="form__input form__input--sm form__input--disabled"
                          placeholder="Total Weight"
                          autoComplete="off"
                          value={fields.destinationWeight}
                          onChange={handleFieldChange}
                          disabled={true}
                        />
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="destinationPieces"
                          className="form__label"
                        >
                          Total Items/Pieces
                        </label>
                        <input
                          type="number"
                          id="destinationPieces"
                          name="destinationPieces"
                          className="form__input form__input--sm form__input--disabled"
                          placeholder="Pieces"
                          autoComplete="off"
                          value={fields.destinationPieces}
                          onChange={handleFieldChange}
                          disabled={true}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* <div className="formRow formRow__2"> */}
                {(notLTL ||
                  orderTypeRowData?.name === "Expedited" ||
                  orderTypeRowData?.name === "LTL + Final Mile") && (
                    <div className="formCol">
                      <div className="formRow formRow__3">
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="destinationExtramen"
                              className="form__label"
                            >
                              Extra Men
                            </label>
                            <input
                              type="number"
                              id="destinationExtramen"
                              name="destinationExtramen"
                              className="form__input form__input--sm"
                              placeholder="Extra Men"
                              autoComplete="off"
                              value={
                                fields.destinationExtramen !== ""
                                  ? Math.abs(Number(fields.destinationExtramen))
                                  : fields.destinationExtramen
                              }
                              onChange={handleFieldChange}
                              onKeyDown={handleKeyDown}
                            />
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="destinationNoOfFlights"
                              className="form__label"
                            >
                              No. of Flights
                            </label>
                            <input
                              type="number"
                              id="destinationNoOfFlights"
                              name="destinationNoOfFlights"
                              className="form__input form__input--sm"
                              placeholder="No. of Flights"
                              autoComplete="off"
                              value={
                                fields.destinationNoOfFlights !== ""
                                  ? Math.abs(
                                    Number(fields.destinationNoOfFlights)
                                  )
                                  : fields.destinationNoOfFlights
                              }
                              onChange={handleFieldChange}
                              onKeyDown={handleKeyDown}
                            />
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="destinationWaitTimeMinutes"
                              className="form__label"
                            >
                              Wait Time
                            </label>
                            <input
                              type="number"
                              id="destinationWaitTimeMinutes"
                              name="destinationWaitTimeMinutes"
                              className="form__input form__input--sm"
                              placeholder="Wait Time"
                              autoComplete="off"
                              value={
                                fields.destinationWaitTimeMinutes !== ""
                                  ? Math.abs(
                                    Number(fields.destinationWaitTimeMinutes)
                                  )
                                  : fields.destinationWaitTimeMinutes
                              }
                              onChange={handleFieldChange}
                              onKeyDown={handleKeyDown}
                            />
                          </div>
                        </div>
                        {/* <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationStorage"
                            className="form__label"
                          >
                            Storage
                          </label>
                          <input
                            type="number"
                            id="destinationStorage"
                            name="destinationStorage"
                            className="form__input form__input--sm"
                            placeholder="Storage"
                            value={fields.destinationStorage}
                            onChange={handleFieldChange}
                            onKeyDown={handleKeyDown}
                          />
                        </div>
                      </div> */}
                      </div>
                    </div>
                  )}

                {!notLTL && orderTypeRowData?.name !== "Expedited" && (
                  <div className="formRow formRow__2 mb_0">
                    <div className="formCol">
                      <div className="form__Field">
                        <MultiSelectChips
                          apiUrl={"/order/ltl-accessorial/wwex"}
                          dropdownData={dropAccessorials}
                          selectedData={dropAccessorialsSelected}
                          setDropdownData={setDropAccessorialDropdownData}
                          setSelectedData={setDropAccessorialsSelected}
                          searchPlaceholder="Search"
                          extendedUrl={{ isPickup: "false" }}
                          labelTitle="LTL Delivery Accessorials"
                          keyBindName="name"
                          mandatory={false}
                          disable={
                            orderTypeRowData?.name === "LTL + Final Mile"
                          }
                          callingFrom="TraditionalQuote" // ← enables the 3 extra items
                        />
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <MutliDropdown
                          columns={accessorialsColumn}
                          data={dropLimitedAccess}
                          setData={setDropLimitedAccessDropdownData}
                          captionTitles="Select Limited Access Type"
                          rowData={dropLimitedAccessSelected}
                          setRowData={setDropLimitedAccesssSelected}
                          displayColumn="name"
                          apiUrl={"/order/ltl-accessorial/wwex"}
                          extendedUrl={{
                            isPickup: "false",
                            isLimitAccess: "true",
                          }}
                          labelTitle="Limited Access Type"
                          enableRemove={true}
                          mandatory={false}
                          disable={
                            orderTypeRowData?.name === "LTL + Final Mile"
                          }
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* </div> */}

                {(notLTL || orderTypeRowData?.name === "Expedited") && (
                  <>
                    <div className="formRow mb_0">
                      <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationSpecialInstructions"
                            className="form__label"
                          >
                            Special Instructions
                          </label>
                          <textarea
                            id="destinationSpecialInstructions"
                            name="destinationSpecialInstructions"
                            className="form__textarea form__textarea--xs"
                            placeholder="Special Instructions"
                            autoComplete="off"
                            value={fields.destinationSpecialInstructions}
                            onChange={handleTextAreaChange}
                            maxLength={255}
                          ></textarea>
                        </div>
                      </div>
                    </div>
                    {/* <div className="formRow formRow--gapNone">
                      <h1 className="form__section__title form__section__title--flex">
                        Add Item Details
                        <div className="btn__group">
                          <button
                            className="btn btn__white"
                            onClick={handleAddItemDetails}
                          >
                            <AddIcon />
                            Add New Item
                          </button>
                        </div>
                      </h1>
                      <div className="tableWrap tableWrap--order p_0">
                        <ItemDetailsTable
                          columns={
                            orderTypeRowData?.name === "Expedited"
                              ? tableConfig.columnsExpediet
                              : tableConfig.columns
                          }
                          data={data}
                          setData={setData}
                          setLoading={setLoading}
                          onDeleteConfig={tableConfig.delete}
                          showDelete={true}
                          isEditable={true}
                          setFields={setFields}
                          isLTL={true}
                        />
                      </div>
                    </div> */}
                  </>
                )}
              </section>
            </div>
          ) : (
            <section className="form__section">
              <h1 className="form__section__title">
                {isFinalMilePickup ? "Origin Address" : "Destination Address"}
              </h1>
              <div className="formRow formRow__2">
                <div className="formCol">
                  <div className="form__Field">
                    <label htmlFor="destinationZipcode" className="form__label">
                      Zip Code <span className="mandatory-mark">*</span>
                    </label>
                    <input
                      type="text"
                      id="destinationZipcode"
                      data-field="destinationZipcode"
                      name="destinationZipcode"
                      className={`form__input form__input--sm${fieldErrorClass("destinationZipcode")}`}
                      placeholder="Zip Code"
                      autoComplete="off"
                      value={fields.destinationZipcode}
                      onChange={handleFieldChange}
                    />
                    {errors.destinationZipcode && (
                      <span className="form__error">
                        {errors.destinationZipcode}
                      </span>
                    )}
                  </div>
                </div>
                <div className="formCol">
                  <div className="formRow formRow__3">
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="destinationCity"
                          className="form__label"
                        >
                          City
                        </label>
                        {destinationCities.length > 0 ? (
                          <SingleDropdown
                            fieldName="destinationCity"
                            selectedItem={fields.destinationCity ? { id: fields.destinationCity, name: fields.destinationCity } : null}
                            setSelectedData={(_, item) => setFields((prev) => ({ ...prev, destinationCity: item?.name || "" }))}
                            staticData={destinationCities.map((c) => ({ id: c, name: c }))}
                            placeholder="City"
                          />
                        ) : (
                          <input
                            type="text"
                            id="destinationCity"
                            className="form__input form__input--sm form__input--disabled"
                            placeholder="City"
                            autoComplete="off"
                            value={fields.destinationCity}
                            onChange={handleFieldChange}
                            disabled={true}
                          />
                        )}
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="destinationState"
                          className="form__label"
                        >
                          State
                        </label>
                        <input
                          type="text"
                          id="destinationState"
                          name="destinationState"
                          className="form__input form__input--sm form__input--disabled"
                          placeholder="State"
                          autoComplete="off"
                          value={fields.destinationState}
                          onChange={handleFieldChange}
                          disabled={true}
                        />
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="destinationCountry"
                          className="form__label"
                        >
                          Country
                        </label>
                        <input
                          type="text"
                          id="destinationCountry"
                          name="destinationCountry"
                          className="form__input form__input--sm form__input--disabled"
                          placeholder="Country"
                          autoComplete="off"
                          value={fields.destinationCountry}
                          onChange={handleFieldChange}
                          disabled={true}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="formRow">
                {(notLTL ||
                  orderTypeRowData?.name === "Expedited" ||
                  orderTypeRowData?.name === "LTL + Final Mile") && (
                    <div className="formCol">
                      <div className="formRow formRow__3">
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="destinationExtramen"
                              className="form__label"
                            >
                              Extra Men
                            </label>
                            <input
                              type="number"
                              id="destinationExtramen"
                              name="destinationExtramen"
                              className="form__input form__input--sm"
                              placeholder="Extra Men"
                              autoComplete="off"
                              value={
                                fields.destinationExtramen !== ""
                                  ? Math.abs(Number(fields.destinationExtramen))
                                  : fields.destinationExtramen
                              }
                              onChange={handleFieldChange}
                              onKeyDown={handleKeyDown}
                            />
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="destinationNoOfFlights"
                              className="form__label"
                            >
                              No. of Flights (Stairs)
                            </label>
                            <input
                              type="number"
                              id="destinationNoOfFlights"
                              name="destinationNoOfFlights"
                              className="form__input form__input--sm"
                              placeholder="No. of Flights"
                              autoComplete="off"
                              value={
                                fields.destinationNoOfFlights !== ""
                                  ? Math.abs(
                                    Number(fields.destinationNoOfFlights)
                                  )
                                  : fields.destinationNoOfFlights
                              }
                              onChange={handleFieldChange}
                              onKeyDown={handleKeyDown}
                            />
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="destinationWaitTimeMinutes"
                              className="form__label"
                            >
                              Wait Time (Minutes)
                            </label>
                            <input
                              type="number"
                              id="destinationWaitTimeMinutes"
                              name="destinationWaitTimeMinutes"
                              className="form__input form__input--sm"
                              placeholder="Wait Time"
                              autoComplete="off"
                              value={
                                fields.destinationWaitTimeMinutes !== ""
                                  ? Math.abs(
                                    Number(fields.destinationWaitTimeMinutes)
                                  )
                                  : fields.destinationWaitTimeMinutes
                              }
                              onChange={handleFieldChange}
                              onKeyDown={handleKeyDown}
                            />
                          </div>
                        </div>
                        {/* <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationStorage"
                            className="form__label"
                          >
                            Storage
                          </label>
                          <input
                            type="number"
                            id="destinationStorage"
                            name="destinationStorage"
                            className="form__input form__input--sm"
                            placeholder="Storage"
                            value={fields.destinationStorage}
                            onChange={handleFieldChange}
                            onKeyDown={handleKeyDown}
                          />
                        </div>
                      </div> */}
                      </div>
                    </div>
                  )}
              </div>
              <div className="formRow formRow__3">
                <div className="formCol">
                  <div className="form__Field">
                    <label
                      htmlFor="destinationHandingUnits"
                      className="form__label"
                    >
                      Handling Units
                    </label>
                    <input
                      type="number"
                      id="destinationHandingUnits"
                      name="destinationHandingUnits"
                      className="form__input form__input--sm form__input--disabled"
                      placeholder="Handling Units"
                      autoComplete="off"
                      value={fields.destinationHandingUnits}
                      onChange={handleFieldChange}
                      disabled={true}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
                <div className="formCol">
                  <div className="form__Field">
                    <label htmlFor="destinationWeight" className="form__label">
                      Total Weight(LBS)
                    </label>
                    <input
                      type="number"
                      id="destinationWeight"
                      name="destinationWeight"
                      className="form__input form__input--sm form__input--disabled"
                      placeholder="Total Weight"
                      autoComplete="off"
                      value={fields.destinationWeight}
                      onChange={handleFieldChange}
                      disabled={true}
                    />
                  </div>
                </div>
                <div className="formCol">
                  <div className="form__Field">
                    <label htmlFor="destinationPieces" className="form__label">
                      Total Items/Pieces
                    </label>
                    <input
                      type="number"
                      id="destinationPieces"
                      name="destinationPieces"
                      className="form__input form__input--sm form__input--disabled"
                      placeholder="Pieces"
                      autoComplete="off"
                      value={fields.destinationPieces}
                      onChange={handleFieldChange}
                      disabled={true}
                    />
                  </div>
                </div>
              </div>
              {!notLTL && orderTypeRowData?.name !== "Expedited" && (
                <div className="formRow formRow__2 mb_0">
                  <div className="formCol">
                    <div className="form__Field">
                      <MultiSelectChips
                        apiUrl={"/order/ltl-accessorial/wwex"}
                        dropdownData={dropAccessorials}
                        selectedData={dropAccessorialsSelected}
                        setDropdownData={setDropAccessorialDropdownData}
                        setSelectedData={setDropAccessorialsSelected}
                        searchPlaceholder="Search"
                        extendedUrl={{ isPickup: "false" }} // Delivery
                        labelTitle="LTL Delivery Accessorials"
                        keyBindName="name"
                        mandatory={false}
                        disable={orderTypeRowData?.name === "LTL + Final Mile"}
                        callingFrom="TraditionalQuote"
                      />
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <MutliDropdown
                        columns={accessorialsColumn}
                        data={dropLimitedAccess}
                        setData={setDropLimitedAccessDropdownData}
                        captionTitles="Select Limited Access Type"
                        rowData={dropLimitedAccessSelected}
                        setRowData={setDropLimitedAccesssSelected}
                        displayColumn="name"
                        apiUrl={"/order/ltl-accessorial/wwex"}
                        extendedUrl={{
                          isPickup: "false",
                          isLimitAccess: "true",
                        }}
                        labelTitle="Limited Access Type"
                        enableRemove={true}
                        mandatory={false}
                        disable={orderTypeRowData?.name === "LTL + Final Mile"}
                      />
                    </div>
                  </div>
                </div>
              )}

              {(notLTL || orderTypeRowData?.name === "Expedited") && (
                <>
                  <div className="formRow">
                    <div className="formCol"></div>
                  </div>
                  <div className="formRow formRow--gapNone">
                    <h1 className="form__section__title form__section__title--flex">
                      Add Handling Unit Details
                      <div className="btn__group">
                        <button
                          className="btn btn__white"
                          onClick={handleAddItemDetails}
                        >
                          <AddIcon />
                          Add Handling Units
                        </button>
                      </div>
                    </h1>
                    <OrderItemDetailTable
                      invalidCells={invalidCells}
                      invalidPackageCells={invalidPackageCells}
                      emptyMessage={itemsEmpty ? HANDLING_UNITS_REQUIRED_MESSAGE : undefined}
                      fieldKey={HANDLING_UNITS_FIELD_KEY}
                      focusCell={focusCell}
                      columns={tableConfig.columnsLTL}
                      packageType={apiPackageType}
                      data={data}
                      setData={setData}
                      setLoading={setLoading}
                      onDeleteConfig={tableConfig.delete}
                      showDelete={true}
                      isLTL={true}
                      isEditable={true}
                      setFields={setFields}
                      orderType={orderTypeRowData?.name}
                      customerId={assCustomerRowData?.id || customerRowData?.id}
                    />
                    {/* <div className="tableWrap tableWrap--order p_0">
                      <ItemDetailsTable
                        columns={
                          orderTypeRowData?.name === "Expedited"
                            ? tableConfig.columnsExpediet
                            : tableConfig.columns
                        }
                        data={data}
                        setData={setData}
                        setLoading={setLoading}
                        onDeleteConfig={tableConfig.delete}
                        showDelete={true}
                        isEditable={true}
                        setFields={setFields}
                        isLTL={true}
                      />
                    </div> */}
                  </div>
                  <div className="formRow formRow__3">
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="destinationConsigneePhoneNumber"
                          className="form__label"
                        >
                          Total Cubic Meter
                        </label>
                      </div>
                      <input
                        type="text"
                        id="MidStopAPTSuit"
                        name="MidStopAPTSuit"
                        autoComplete="off"
                        className="form__input form__input--sm form__input--disabled"
                        placeholder="Total Cubic Meter"
                        value={parseFloat(fields.totalCBM).toFixed(3)}
                        disabled={true}
                      />
                    </div>
                  </div>
                </>
              )}
            </section>
          )}

          {notLTL === false && orderTypeRowData?.name !== "Expedited" ? (
            <div className="form__sectionGrid">
              <section className="form__section">
                <h1 className="form__section__title">Pickup Dock Data</h1>

                <div className="formRow formRow__2">
                  <div className="formCol">
                    <div className="form__Field">
                      <label className="form__label">
                        Pickup Date <span className="mandatory-mark">*</span>
                      </label>
                      <input
                        type="date"
                        id="PickUpDockDate"
                        data-field="PickUpDockDate"
                        name="PickUpDockDate"
                        className={`form__date form__date--sm${fieldErrorClass("PickUpDockDate")}`}
                        placeholder="DD/MM/YYYY"
                        autoComplete="off"
                        min={moment().format("YYYY-MM-DD")}
                        onChange={handleFieldChange}
                        value={normalizeDate(fields.PickUpDockDate)}
                      />
                    </div>
                    {errors.PickUpDockDate || errors.PickUpDockTime ? (
                      <span className="form__error">
                        {errors.PickUpDockDate
                          ? errors.PickUpDockDate
                          : errors.PickUpDockTime}
                      </span>
                    ) : (
                      ""
                    )}
                    {/* {errors.PickUpDockTime && (
                      <span className="form__error">
                        {errors.PickUpDockTime}
                      </span>
                    )} */}
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <label htmlFor="PickUpDockTime" className="form__label">
                        Pickup Time <span className="mandatory-mark">*</span>
                      </label>
                      <CustomTimePicker
                        value={fields.PickUpDockTime}
                        onChange={handleTimeChange}
                        property="PickUpDockTime"
                      />
                    </div>
                  </div>
                </div>
              </section>
            </div>
          ) : (
            ""
          )}

          {notLTL ? (
            <section className="form__section">
              <h1 className="form__section__title">
                Select Additional Services
              </h1>
              <div className="formRow">
                <div className="form__checkbox form__checkbox--gray">
                  <div className="form__checkbox__option mb_0">
                    <input
                      type="checkbox"
                      id="Remove & Disposal"
                      autoComplete="off"
                      className={`form__checkbox__input`}
                      checked={fields.additionalServices.some(
                        (x) => x === "Remove & Disposal"
                      )}
                      onChange={handleCheckboxChange}
                    />
                    <label className="form__checkbox__label">
                      Remove & Disposal
                    </label>
                  </div>
                  <div className="form__checkbox__option mb_0">
                    <input
                      type="checkbox"
                      id="Order Swap & Exchange"
                      autoComplete="off"
                      className={`form__checkbox__input`}
                      checked={fields.additionalServices.some(
                        (x) => x === "Order Swap & Exchange"
                      )}
                      onChange={handleCheckboxChange}
                    />
                    <label className="form__checkbox__label">
                      Order Swap & Exchange
                    </label>
                  </div>
                </div>
              </div>
              <div className="formRow mb_0">
                {/* Flush regardless of results: the quote list below brings its
                    own spacing, so the 15px `.form--innersec` adds once results
                    show up only opened a void under the button. */}
                <div className="btn__group mb_0">
                  <button
                    className="btn btn__success"
                    type="button"
                    onClick={() => {
                      getQuotesData();
                    }}
                    disabled={isQuote}
                  >
                    Get Quote
                  </button>
                </div>
              </div>
              {getQuote &&
                (quoteData?.length > 0 ? (
                  <GetQuote
                    setFields={setFields}
                    quoteData={quoteData}
                    setIsQuote={setIsQuote}
                    saveQuotation={handleSaveQuotation}
                    quoteSelectedId={stateQuoteId}
                    formData={dataToTransfer}
                    calledFrom={"quickQuote"}
                    masterHubName={masterHubName}
                    masterHubAddress={masterHubAddress}
                    masterHubNameToTransfer={masterHubNameToTransfer}
                    masterHubAddressToTransfer={masterHubAddressToTransfer}
                    lfmQuoteId={lfmQuoteId}
                  />
                ) : (
                  <span>
                    {orderTypeRowData?.name === "Final Mile"
                      ? "No Final Mile Agent Available!"
                      : orderTypeRowData?.name === "LTL"
                        ? "No LTL Agent Available!"
                        : orderTypeRowData?.name === "LTL + Final Mile"
                          ? "No LTL + Final Mile1 Agent Available!"
                          : "No Results Found!"}
                  </span>
                ))}
            </section>
          ) : orderTypeRowData?.name !== "Expedited" ? (
            <section className="form__section">
              <div className="formRow formRow--gapNone">
                <h1 className="form__section__title form__section__title--flex">
                  Add Handling Unit Details
                  <div className="btn__group mb_0">
                    <button
                      className="btn btn__white"
                      onClick={handleAddItemDetails}
                    >
                      <AddIcon />
                      Add Handling Units
                    </button>
                  </div>
                </h1>

                <OrderItemDetailTable
                  invalidCells={invalidCells}
                  invalidPackageCells={invalidPackageCells}
                  emptyMessage={itemsEmpty ? HANDLING_UNITS_REQUIRED_MESSAGE : undefined}
                  fieldKey={HANDLING_UNITS_FIELD_KEY}
                  focusCell={focusCell}
                  columns={tableConfig.columnsLTL}
                  packageType={
                    // orderTypeRowData?.name === "LTL" ||
                    //   orderTypeRowData?.name === "LTL + Final Mile"
                    //   ? PACKAGE_TYPE
                    //   : EXPEDIET_PACKAGE_TYPE
                    apiPackageType
                  }
                  data={data}
                  setData={setData}
                  setLoading={setLoading}
                  onDeleteConfig={tableConfig.delete}
                  showDelete={true}
                  isLTL={true}
                  isEditable={true}
                  setFields={setFields}
                  orderType={orderTypeRowData?.name}
                  customerId={assCustomerRowData?.id || customerRowData?.id}
                />

                {/* <div className="tableWrap tableWrap--order p_0">
                  <ItemDetailsTable
                    columns={tableConfig.columnsLTL}
                    data={data}
                    setData={setData}
                    setLoading={setLoading}
                    onDeleteConfig={tableConfig.delete}
                    showDelete={true}
                    isLTL={true}
                    isEditable={true}
                    setFields={setFields}
                  />
                </div> */}
              </div>
              <div className="formRow formRow__3">
                <div className="formCol">
                  <div className="form__Field">
                    <label
                      htmlFor="destinationConsigneePhoneNumber"
                      className="form__label"
                    >
                      Total Density
                    </label>
                  </div>
                  <input
                    type="text"
                    id="MidStopAPTSuit"
                    name="MidStopAPTSuit"
                    autoComplete="off"
                    className="form__input form__input--sm form__input--disabled"
                    placeholder="Total Linear Feet"
                    value={parseFloat(fields.totalDensity).toFixed(3)}
                    disabled={true}
                  />
                </div>
                <div className="formCol">
                  <div className="form__Field">
                    <label
                      htmlFor="destinationConsigneePhoneNumber"
                      className="form__label"
                    >
                      Total Linear Feet
                    </label>
                  </div>
                  <input
                    type="text"
                    id="MidStopAPTSuit"
                    name="MidStopAPTSuit"
                    autoComplete="off"
                    className="form__input form__input--sm form__input--disabled"
                    placeholder="Total Linear Feet"
                    value={parseFloat(fields.totalLinearFeet).toFixed(3)}
                    disabled={true}
                  />
                </div>
                <div className="formCol">
                  <div className="form__Field">
                    <label
                      htmlFor="destinationConsigneePhoneNumber"
                      className="form__label"
                    >
                      Total Cubic Meter
                    </label>
                  </div>
                  <input
                    type="text"
                    id="MidStopAPTSuit"
                    name="MidStopAPTSuit"
                    autoComplete="off"
                    className="form__input form__input--sm form__input--disabled"
                    placeholder="Total Cubic Meter"
                    value={parseFloat(fields.totalCBM).toFixed(3)}
                    disabled={true}
                  />
                </div>
              </div>
              <div className="formRow mb_0">
                <div className={showLTLList ? "btn__group" : "btn__group mb_0"}>
                  <button
                    className="btn btn__success"
                    type="button"
                    onClick={() => {
                      if (quoteType === "traditional") {
                        getTraditionalQuoteData();
                      } else {
                        getLTLQuotesData();
                      }
                    }}
                  >
                    {orderTypeRowData?.name === "LTL"
                      ? "Get Quote"
                      : "Get Quote"}
                  </button>
                </div>
              </div>
              {showLTLList && (
                <>
                  {quoteType === "traditional" ? (
                    <GetTraditionalQuote
                      orderType={
                        orderTypeRowData?.name === "LTL"
                          ? "LTL"
                          : "LTL + Final Mile"
                      }
                      setFields={setFields}
                      fields={fields}
                      quoteData={quoteData}
                      setIsQuote={setIsQuote}
                      saveQuotation={
                        orderTypeRowData?.name === "LTL"
                          ? handleSaveQuotationForLTL
                          : handleSaveQuotation
                      }
                      quoteSelectedId={stateQuoteId}
                      orderTypeRowData={orderTypeRowData}
                      setOrderTypeRowData={setOrderTypeRowData}
                      ltlFMData={ltlFMData}
                      formData={ltlDataToTransfer}
                      setLtlFMData={setLtlFMData}
                      calledFrom={"quickQuoteLTL"}
                      ltlAccessorialData={ltlAccessorialData}
                      isLtlOnly={isLtlOnly}
                      setIsLtlOnly={setIsLtlOnly}
                    />
                  ) : (
                    <GetLTLQuote
                      orderType={orderTypeRowData?.name || ""}
                      quoteData={TLList}
                      fields={fields}
                      setFields={setFields}
                      setIsQuote={setIsQuote}
                      isLtlFinalmile={isLtlFinalmile}
                      saveQuotation={handleSaveLTLQuotation}
                      quoteSelectedId={stateQuoteId}
                      formData={ltlDataToTransfer}
                      calledFrom={"quickQuote"}
                    />
                  )}
                </>
              )}
              {getQuote &&
                notLTL &&
                (quoteData?.length > 0 ? (
                  <GetQuote
                    setFields={setFields}
                    quoteData={quoteData}
                    setIsQuote={setIsQuote}
                  />
                ) : (
                  <span>No Results Found!</span>
                ))}
            </section>
          ) : (
            ""
          )}
          <section className="form__section">
            {isQuote && notLTL && (
              <>
                <h3 className="form__section__title">Mid Stop</h3>
                <div className="formRow formRow__2">
                  <div className="formCol">
                    <div className="form__Field">
                      <label htmlFor="MidStopAddress" className="form__label">
                        Address
                      </label>
                      <input
                        type="text"
                        id="MidStopAddress"
                        name="MidStopAddress"
                        className="form__input form__input--sm form__input--disabled"
                        placeholder="Address"
                        autoComplete="off"
                        value={
                          hasMasterHub
                            ? masterHubAddress?.address
                            : fields.MidStopAddress
                        }
                        onChange={handleFieldChange}
                        disabled={true}
                      />
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <label htmlFor="MidStopAPTSuit" className="form__label">
                        APT / Suite
                      </label>
                      <input
                        type="text"
                        id="MidStopAPTSuit"
                        name="MidStopAPTSuit"
                        className="form__input form__input--sm form__input--disabled"
                        placeholder="APT/Suite"
                        autoComplete="off"
                        value={
                          hasMasterHub
                            ? masterHubAddress?.aptSuit
                            : fields.MidStopAPTSuit
                        }
                        onChange={handleFieldChange}
                        disabled={true}
                      />
                    </div>
                  </div>
                </div>
                <div className="formRow formRow__2">
                  <div className="formCol">
                    <div className="form__Field">
                      <label htmlFor="MidStopZipcode" className="form__label">
                        Zip Code
                      </label>
                      <input
                        type="text"
                        id="MidStopZipcode"
                        name="MidStopZipcode"
                        autoComplete="off"
                        className="form__input form__input--sm form__input--disabled"
                        placeholder="Zip Code"
                        value={
                          hasMasterHub
                            ? masterHubAddress?.zipCode
                            : fields.MidStopZipcode
                        }
                        onChange={handleFieldChange}
                        disabled={true}
                      />
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="formRow formRow__3">
                      <div className="formCol">
                        <div className="form__Field">
                          <label htmlFor="MidStopCity" className="form__label">
                            City
                          </label>
                          <input
                            type="text"
                            id="MidStopCity"
                            name="MidStopCity"
                            className="form__input form__input--sm form__input--disabled"
                            placeholder="City"
                            autoComplete="off"
                            value={
                              hasMasterHub
                                ? masterHubAddress?.city
                                : fields.MidStopCity
                            }
                            onChange={handleFieldChange}
                            disabled={true}
                          />
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form__Field">
                          <label htmlFor="MidStopState" className="form__label">
                            State
                          </label>
                          <input
                            type="text"
                            id="MidStopState"
                            name="MidStopState"
                            className="form__input form__input--sm form__input--disabled"
                            placeholder="State"
                            autoComplete="off"
                            value={
                              hasMasterHub
                                ? masterHubAddress?.state
                                : fields.MidStopState
                            }
                            onChange={handleFieldChange}
                            disabled={true}
                          />
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="MidStopCountry"
                            className="form__label"
                          >
                            Country
                          </label>
                          <input
                            type="text"
                            id="MidStopCountry"
                            name="MidStopCountry"
                            className="form__input form__input--sm form__input--disabled"
                            placeholder="Country"
                            autoComplete="off"
                            value={
                              hasMasterHub
                                ? masterHubAddress?.country
                                : fields.MidStopCountry
                            }
                            onChange={handleFieldChange}
                            disabled={true}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="formRow formRow__2">
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="MidStopContactName"
                        className="form__label"
                      >
                        Contact Name
                      </label>
                      <input
                        type="text"
                        id="MidStopContactName"
                        name="MidStopContactName"
                        className="form__input form__input--sm form__input--disabled"
                        placeholder="Contact name"
                        autoComplete="off"
                        value={
                          hasMasterHub
                            ? masterHubAddress?.contactPerson
                            : fields.MidStopContactName
                        }
                        onChange={handleFieldChange}
                        disabled={true}
                      />
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="MidStopPhoneNumber"
                        className="form__label"
                      >
                        Phone Number
                      </label>
                      <div className="form__groupField form__groupField--phonenumber form__groupField--disabled">
                        <select
                          className="form__select form__select--sm"
                          disabled={true}
                          value={
                            hasMasterHub
                              ? masterHubAddress?.phoneNumber
                              : fields.MidStopPhoneNumberExt
                          }
                          onChange={(e) => {
                            setFields(() => ({
                              ...fields,
                              MidStopPhoneNumberExt: e.target.value,
                            }));
                          }}
                        >
                          {countryCode?.map((item: any) => (
                            <option
                              key={item?.countryCallingCode}
                              value={item?.countryCallingCode}
                            >
                              {item?.countryCallingCode}
                            </option>
                          ))}
                        </select>
                        <input
                          type="text"
                          id="MidStopPhoneNumber"
                          name="MidStopPhoneNumber"
                          className="form__input form__input--sm"
                          placeholder="Phone Number"
                          value={onInputPhNumFormat(
                            fields.MidStopPhoneNumber,
                            fields.MidStopPhoneNumberExt
                          )}
                          onChange={handleFieldChange}
                          onSelect={handleFieldChange}
                          autoComplete="off"
                          disabled={true}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="formRow formRow__2">
                  <div className="formCol">
                    <EmailChips
                      emails={
                        hasMasterHub
                          ? masterHubAddress?.contactEmail
                          : fields.MidStopContactEmail
                      }
                      setEmails={setFields}
                      placeholder="Contact Email"
                      label="Contact Email"
                      keyName="MidStopContactEmail"
                      error={errors}
                      setError={setErrors}
                      disable={true}
                    />
                  </div>
                </div>
              </>
            )}
            {orderTypeRowData?.name === "Expedited" ? (
              <>
                <div className="formRow formRow--gapNone">
                  <h1 className="form__section__title form__section__title--flex">
                    Add Handling Unit Details
                    <div className="btn__group">
                      <button
                        className="btn btn__white"
                        onClick={handleAddItemDetails}
                      >
                        <AddIcon />
                        Add Handling Units
                      </button>
                    </div>
                  </h1>
                  <OrderItemDetailTable
                    invalidCells={invalidCells}
                    invalidPackageCells={invalidPackageCells}
                    emptyMessage={itemsEmpty ? HANDLING_UNITS_REQUIRED_MESSAGE : undefined}
                    fieldKey={HANDLING_UNITS_FIELD_KEY}
                    focusCell={focusCell}
                    columns={tableConfig.columnsLTL}
                    packageType={apiPackageType}
                    data={data}
                    setData={setData}
                    setLoading={setLoading}
                    onDeleteConfig={tableConfig.delete}
                    showDelete={true}
                    isLTL={true}
                    isEditable={true}
                    setFields={setFields}
                    orderType={orderTypeRowData?.name}
                    customerId={assCustomerRowData?.id || customerRowData?.id}
                  />
                </div>
                <div className="formRow formRow__3">
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationConsigneePhoneNumber"
                        className="form__label"
                      >
                        Total Cubic Meter
                      </label>
                    </div>
                    <input
                      type="text"
                      id="MidStopAPTSuit"
                      name="MidStopAPTSuit"
                      autoComplete="off"
                      className="form__input form__input--sm form__input--disabled"
                      placeholder="Total Cubic Meter"
                      value={parseFloat(fields.totalCBM).toFixed(3)}
                      disabled={true}
                    />
                  </div>
                </div>
                <h3 className="form__section__title">
                  Courier / On Demand Agent Details
                </h3>
                <div className="formRow formRow__2">
                  <div className="formCol">
                    <MutliDropdown
                      columns={agentOrderColumns}
                      data={agentNameDropdown}
                      setData={setAgentNameDropdown}
                      captionTitles="Select Agent"
                      rowData={agentRowData}
                      setRowData={setAgentRowData}
                      displayColumn="name"
                      apiUrl={"/agent"}
                      labelTitle="Agent Name"
                      enableRemove={true}
                      mandatory={true}
                      type={"dropdown"}
                    />
                  </div>
                  <div className="formCol">
                    <MutliDropdown
                      labelTitle="Agent Hub"
                      columns={agentHubColumns}
                      data={agentHubDropdownData}
                      setData={setAgentHubDropdownData}
                      captionTitles="Select Agent Hub"
                      rowData={agentHubRowData}
                      setRowData={setAgentHubRowData}
                      displayColumn="name"
                      apiUrl={agentRowData?.id ? "/agent/agent-hub" : ""}
                      type={"dropdown"}
                      extraParentId={{
                        key: "agentId",
                        id: agentRowData?.id ? agentRowData?.id : "",
                      }}
                      enableRemove={true}
                      mandatory={
                        agentRowData?.agentType &&
                          agentRowData?.agentType != "LTL"
                          ? true
                          : false
                      }
                      extendedUrl={{
                        from: "dropdown",
                      }}
                    />
                  </div>
                </div>
                <div className="formRow formRow__2">
                  <div className="formCol">
                    <div className="form__Field">
                      <label className="form__label">Origin City</label>
                      <input
                        type="text"
                        id="originCity"
                        name="originCity"
                        className="form__input form__input--sm"
                        placeholder="Origin City"
                        autoComplete="off"
                        value={fields.originCity}
                        onChange={handleFieldChange}
                      />
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <label htmlFor="MidStopCity" className="form__label">
                        Service Cost <span className="mandatory-mark">*</span>
                      </label>
                      <input
                        type="number"
                        id="serviceCost"
                        name="serviceCost"
                        className="form__input form__input--sm"
                        placeholder="Service Cost"
                        autoComplete="off"
                        value={fields.serviceCost}
                        onChange={handleFieldChange}
                        onKeyDown={handleKeyDown}
                      />
                    </div>
                  </div>
                </div>
                <div className="formRow formRow__2">
                  <div className="formCol">
                    <div className="form__Field">
                      <label className="form__label">Courier Pro. No. 1</label>
                      <input
                        type="text"
                        id="courierPro1"
                        name="courierPro1"
                        className="form__input form__input--sm"
                        placeholder="Courier Pro. No. 1"
                        autoComplete="off"
                        value={fields.courierPro1}
                        onChange={handleFieldChange}
                      />
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="MidStopPhoneNumber"
                        className="form__label"
                      >
                        Courier Pro. No. 2
                      </label>
                      <input
                        type="text"
                        id="courierPro2"
                        name="courierPro2"
                        className="form__input form__input--sm"
                        placeholder="Courier Pro. No. 2"
                        autoComplete="off"
                        value={fields.courierPro2}
                        onChange={handleFieldChange}
                      />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              ""
            )}
            <div className="btn__group">
              <button
                className="btn btn__white"
                type="button"
                onClick={handleBack}
              >
                Cancel
              </button>
              <button
                className="btn btn__primary"
                type="submit"
                onClick={handleSave}
                disabled={isButtonDisabled}
              >
                Book
              </button>
              {((isQuote && orderTypeRowData?.name != "LTL") ||
                orderTypeRowData?.name === "Expedited") && (
                  <button
                    className="btn btn__primary"
                    type="submit"
                    onClick={() => {
                      setViewAddDocumentModal(true);
                    }}
                  >
                    Add Document & URL
                  </button>
                )}
            </div>
          </section>
        </div>
      </div>
      {viewAddDocumentModal && (
        <AddDocumentURLModal
          confirmModal={viewAddDocumentModal}
          setConfirmModal={setViewAddDocumentModal}
          setDocumentInfo={setDocumentInfo}
          documentInfo={documentInfo}
        />
      )}
      {showModal && (
        <ConfirmModal
          confirmInfo={{
            heading: "Unsaved Changes!",
            para: "You have not saved your changes, do you want to continue",
          }}
          onConfirm={handleConfirmNavigation}
          setConfirmModal={handleCancelNavigation}
          firstMessageShow={true}
        />
      )}
    </>
  );
};

export default QuickQuote;
